import  { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { ChevronLeft, ChevronRight, Star, MapPin, Video, Search, X, CalendarClock, ChevronDown, Loader2, FileSearch, FileText, Folder, TrendingUp, List } from 'lucide-react';
import { format, addDays, subDays, parseISO, differenceInMilliseconds, isSameDay } from 'date-fns';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
  standaloneSalesByMonth,
} from '../lib/invoices/standaloneInvoiceSales';
import { triggerBookingCreatedAfterScheduling } from '../lib/automationsV2';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { holidayCountryFromTimezone, holidayNameForDate } from '../lib/holidays';
import BookingInfoModal from '../components/BookingInfoModal';
import DaySummaryModal from '../components/DaySummaryModal';
import CreateMobileBookingModal from '../components/CreateMobileBookingModal';
import CreateBookingModal from '../components/CreateBookingModal';
import BookingTypeSelectionModal, {
  monthCreateSlotKey,
  type AnchorRect,
} from '../components/BookingTypeSelectionModal';
import MoveBookingConfirmModal from '../components/MoveBookingConfirmModal';
import ConfirmBookingMoveModal from '../components/ConfirmBookingMoveModal';
import '../components/MobileListView.css';
// Removed all persistence systems
import { CalendarBooking, getCalendarBookings, getBooking, syncMobileBookingsForBooking, triggerGcalSync, getUnscheduledBookings, updateBooking, type UnscheduledBookingSummary } from '../lib/bookings';
import { getCalendarFieldQuotes } from '../lib/fieldQuotes/calendarFieldQuotes';
import SchedulePickerPanel from '../components/booking/SchedulePickerPanel';
import { getCalendarTasks } from '../lib/tasks';
import TaskDetailsModal from '../components/TaskDetailsModal';
import CreateTaskModal from '../components/CreateTaskModal';
import { getBookingFeedback } from '../lib/feedback';
import { isGcalDigitalEvent, isGcalInPersonEvent, truncateGcalLocation } from '../lib/gcalEventFormat';

/**
 * All-day Google events are stored as midnight boundaries in UTC. Hand
 * FullCalendar the calendar date only, so the event sits on the day Google
 * has it on, not the evening before in local time. The end is exclusive.
 */
function gcalAllDayDate(iso: string): string {
  const d = new Date(iso);
  // Local midnight east of UTC lands late the previous UTC day; round to the nearest day.
  const legacyEnd = d.getUTCHours() === 23 && d.getUTCMinutes() === 59;
  if (!legacyEnd && d.getUTCHours() >= 12) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { usePermissions } from '../contexts/PermissionContext';
import PortalDropdown from '../components/ui/PortalDropdown';
import { useLongPress } from '../hooks/useLongPress';
import { useDismissOverlayProtectingCalendarCreate } from '../hooks/useDismissOverlayProtectingCalendarCreate';
import { hapticLight, hapticMedium } from '../lib/haptics';
import BookingQuickInfoModal from '../components/BookingQuickInfoModal';
import GoogleCalendarEventModal from '../components/GoogleCalendarEventModal';
import CreateGoogleCalendarEventModal from '../components/CreateGoogleCalendarEventModal';
import ProjectInfoModal from '../components/ProjectInfoModal';
import FieldQuoteDetailModal from '../components/FieldQuoteDetailModal';
import ScheduleFieldQuoteModal from '../components/ScheduleFieldQuoteModal';
import AdvancedMapView from '../components/AdvancedMapView';
import { useSimpleTimer } from '../contexts/SimpleTimerContext';
import { detectPWAEnvironment } from '../utils/pwaDetection';
import { getResources, Resource } from '../lib/resources';
import WeekView, { weekSlotKey } from '../components/WeekView';
import DayView from '../components/DayView';
import ResourceDetailsModal from '../components/ResourceDetailsModal';
import { useAppSystem } from '../hooks/useAppSystem';
import { getBusinessResourcesWorkingAvailability, ResourceWorkingAvailability } from '../lib/resourceAvailability';
import { getAssets, Asset } from '../lib/assets';
import { getBusinessAssetsAvailability } from '../lib/assetAvailability';
import AssetModal from '../components/AssetModal';
import { sendSMS } from '../lib/messaging';
import { MapPin as MapPinIcon } from 'lucide-react';
import { Bookings } from './Bookings';
import {
  clearCalendarViewOverride,
  getCalendarViewOverride,
} from '../lib/calendarViewNavigation';
import { useCalendarBookingSearch } from '../hooks/useCalendarBookingSearch';
import CalendarBookingSearch from '../components/admin-dashboard/CalendarBookingSearch';


interface BookingPayload {
  id: string;
  business_id: string;
  [key: string]: any;
}

export interface GoogleCalendarAttendee {
  email: string;
  displayName: string | null;
  responseStatus: 'accepted' | 'declined' | 'tentative' | 'needsAction';
  organizer: boolean;
  self: boolean;
}

export interface GoogleCalendarEvent {
  id: string;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  location: string | null;
  description: string | null;
  resource_id: string | null;
  google_event_id?: string | null;
  status?: string | null;
  meet_link?: string | null;
  conference_data?: any;
  attendees?: GoogleCalendarAttendee[] | null;
  organizer_email?: string | null;
  organizer_name?: string | null;
}

// Cache for basic blocks
const basicBlocksCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for full details
const detailsCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for time off requests
const timeOffCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for sales calculations
const salesCache = new Map<string, { 
  totalSales: number, 
  timestamp: number 
}>();

// Cache duration in milliseconds (15 minutes)
const CACHE_DURATION = 15 * 60 * 1000;

export default function CalendarV2() {
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const holidayCountry = useMemo(
    () => holidayCountryFromTimezone(selectedBusiness?.timezone),
    [selectedBusiness?.timezone],
  );
  const { canAccess, currentResource } = usePermissionCheck();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { activeTimer, getElapsedSeconds } = useSimpleTimer();
  const location = useLocation();
  const navigate = useNavigate();

  // Check for navigation state immediately to determine initial modal state
  const navigationState = location.state as { selectedBookingId?: string } | null;
  const shouldOpenBookingModal = Boolean(navigationState?.selectedBookingId);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarTitle, setCalendarTitle] = useState('');
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<CalendarBooking[]>([]);
  const [fieldQuoteEvents, setFieldQuoteEvents] = useState<CalendarBooking[]>([]);
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState<GoogleCalendarEvent[]>([]);
  const [selectedGcalEvent, setSelectedGcalEvent] = useState<GoogleCalendarEvent | null>(null);
  const [isGcalEventModalOpen, setIsGcalEventModalOpen] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalSyncId, setGcalSyncId] = useState<string | null>(null);
  const [isCreateGcalEventModalOpen, setIsCreateGcalEventModalOpen] = useState(false);
  const [createGcalEventDate, setCreateGcalEventDate] = useState<Date | null>(null);
  const [createGcalEventEndDate, setCreateGcalEventEndDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Buffer block modal state
  const [isBufferModalOpen, setIsBufferModalOpen] = useState(false);
  const [selectedBufferBlock, setSelectedBufferBlock] = useState<any>(null);
  const [bufferBlockBooking, setBufferBlockBooking] = useState<any>(null);
  const [bufferBlockResource, setBufferBlockResource] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewStartDate, setViewStartDate] = useState<Date>(new Date());
  const [viewEndDate, setViewEndDate] = useState<Date>(new Date());
  const [loadedBookingIds, setLoadedBookingIds] = useState<Set<string>>(new Set());
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingInfoModalOpen, setIsBookingInfoModalOpen] = useState(shouldOpenBookingModal); // Open immediately if needed

  // Month-view header search — finds bookings across any timeframe.
  const [searchQuery, setSearchQuery] = useState('');
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
  } = useCalendarBookingSearch(selectedBusiness?.id, searchQuery);

  const handleSelectSearchResult = useCallback(async (bookingId: string) => {
    try {
      const booking = await getBooking(bookingId);
      setSelectedBooking(booking);
      setIsBookingInfoModalOpen(true);
      setSearchQuery('');
    } catch (err) {
      console.error('Error opening booking from search:', err);
    }
  }, []);

  // The AI assistant's open_booking tool: an event when the calendar is
  // already mounted, plus a sessionStorage handoff for when it navigated
  // here first and the event fired before mount.
  useEffect(() => {
    const openFromAssistant = (bookingId: string) => {
      if (bookingId) void handleSelectSearchResult(bookingId);
    };
    const onEvent = (e: Event) => openFromAssistant(String((e as CustomEvent).detail?.bookingId || ''));
    window.addEventListener('assistant:openBooking', onEvent);
    try {
      const pending = sessionStorage.getItem('assistant_open_booking');
      if (pending) {
        sessionStorage.removeItem('assistant_open_booking');
        openFromAssistant(pending);
      }
    } catch { /* storage unavailable */ }
    return () => window.removeEventListener('assistant:openBooking', onEvent);
  }, [handleSelectSearchResult]);

  // Unscheduled bookings ("limbo" state) — surfaced via a pull-tab on the header.
  const [unscheduledOpen, setUnscheduledOpen] = useState(false);
  const [unscheduledBookings, setUnscheduledBookings] = useState<UnscheduledBookingSummary[]>([]);
  const [unscheduledLoading, setUnscheduledLoading] = useState(false);
  // The unscheduled booking currently being scheduled via the inline badge picker.
  const [scheduleTarget, setScheduleTarget] = useState<{ id: string; duration: number } | null>(null);
  const [isSchedulingUnscheduled, setIsSchedulingUnscheduled] = useState(false);
  const unscheduledRef = useRef<HTMLDivElement>(null);

  const loadUnscheduled = useCallback(async () => {
    if (!selectedBusiness?.id) {
      setUnscheduledBookings([]);
      return;
    }
    setUnscheduledLoading(true);
    try {
      const rows = await getUnscheduledBookings(selectedBusiness.id);
      setUnscheduledBookings(rows);
    } catch (err) {
      console.error('Failed to load unscheduled bookings:', err);
    } finally {
      setUnscheduledLoading(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    void loadUnscheduled();
  }, [loadUnscheduled]);

  // Schedule an unscheduled booking from the panel badge: assign dates, clear the
  // limbo flag, and refresh both the calendar grid and the unscheduled list.
  const handleScheduleUnscheduledFromPanel = useCallback(
    async (startDate: string, startTime: string, endDate: string, endTime: string) => {
      if (!scheduleTarget || isSchedulingUnscheduled) return;
      setIsSchedulingUnscheduled(true);
      try {
        await updateBooking(scheduleTarget.id, {
          start_date: startDate,
          start_time: startTime,
          end_date: endDate,
          end_time: endTime,
          is_unscheduled: false,
          status: 'scheduled',
        });
        // The booking now has a real date — THIS is its "created" moment for
        // automations (suppressed while it sat unscheduled).
        if (selectedBusiness?.id) {
          void triggerBookingCreatedAfterScheduling(selectedBusiness.id, scheduleTarget.id);
        }
        setScheduleTarget(null);
        await Promise.all([loadUnscheduled(), fetchBookings(true)]);
      } catch (err) {
        console.error('Error scheduling unscheduled booking:', err);
      } finally {
        setIsSchedulingUnscheduled(false);
      }
    },
    [scheduleTarget, isSchedulingUnscheduled, loadUnscheduled, selectedBusiness?.id],
  );

  // Total sales across unscheduled bookings — mirrors the calendar's sales metric
  // (discounted subtotal, excluding tax).
  const unscheduledSalesTotal = useMemo(
    () =>
      unscheduledBookings.reduce(
        (acc, b) => acc + ((b.subtotal || 0) - (b.discount_amount || 0)),
        0,
      ),
    [unscheduledBookings],
  );

  // Close the unscheduled panel on outside click — don't also create on the calendar under the cursor
  const closeUnscheduled = useCallback(() => setUnscheduledOpen(false), []);
  useDismissOverlayProtectingCalendarCreate(unscheduledOpen, closeUnscheduled, [unscheduledRef]);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isLoadingBookingFromNav, setIsLoadingBookingFromNav] = useState(shouldOpenBookingModal);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDaySummaryOpen, setIsDaySummaryOpen] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isBookingQuickInfoModalOpen, setIsBookingQuickInfoModalOpen] = useState(false);
  const [quickInfoBooking, setQuickInfoBooking] = useState<any>(null);
  const [timerElapsedTime, setTimerElapsedTime] = useState(0);
  const [daySummaryPosition, setDaySummaryPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isCalculatingSales, setIsCalculatingSales] = useState(false); // Start as false - will be set to true only when calculating
  const [isRescheduleMobileBookingModalOpen, setIsRescheduleMobileBookingModalOpen] = useState(false);
  const [bookingToReschedule, setBookingToReschedule] = useState<any | null>(null);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isBookingTypeSelectionModalOpen, setIsBookingTypeSelectionModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [taskInitialData, setTaskInitialData] = useState<{
    date: Date;
    resourceId: string;
    isAsset?: boolean;
    endDate?: Date | null;
  } | null>(null);
  // Field quote created from a clicked/highlighted calendar slot
  const [isSlotFieldQuoteModalOpen, setIsSlotFieldQuoteModalOpen] = useState(false);
  const [fieldQuoteInitialSchedule, setFieldQuoteInitialSchedule] = useState<{
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  } | null>(null);
  const [quickBookingData, setQuickBookingData] = useState<any | null>(null);
  const [selectedTimeSlotData, setSelectedTimeSlotData] = useState<{
    date: Date;
    resourceId: string;
    isAsset?: boolean;
    endDate?: Date | null;
    anchorRect?: AnchorRect | null;
    anchorSlotKey?: string | null;
    anchorEndSlotKey?: string | null;
  } | null>(null);
  const [salesInitiallyLoaded, setSalesInitiallyLoaded] = useState(false);
  
  // App system for checking if projects app is installed
  const { coreApps, installedAddons } = useAppSystem();
  const isProjectsAppInstalled = coreApps.some(app => app.app?.slug === 'projects') || 
                                 installedAddons.some(app => app.app?.slug === 'projects');
  
  // FAB settings
  const [showMobileBookingInFAB, setShowMobileBookingInFAB] = useState(false);
  const [showFieldQuotesInFAB, setShowFieldQuotesInFAB] = useState(false);
  const [showLocationBookingInFAB, setShowLocationBookingInFAB] = useState(true);
  const [mapViewEnabled, setMapViewEnabled] = useState(false);
  const [salesPinsEnabled, setSalesPinsEnabled] = useState(true);
  const [tasksEnabled, setTasksEnabled] = useState(false);
  const [viewType, setViewType] = useState<'month' | 'week' | 'day' | null>(null);
  const [showAdvancedMap, setShowAdvancedMap] = useState(false);
  const [showBookingsView, setShowBookingsView] = useState(false);
  const [defaultViewLoaded, setDefaultViewLoaded] = useState(false);
  const [roleDefaultViewApplied, setRoleDefaultViewApplied] = useState(false);
  /** Deep-link ?view=day|month from control centre — must win over default_calendar_view. */
  const pendingUrlCalendarViewRef = useRef<'day' | 'month' | null>(null);
  const urlCalendarViewAppliedRef = useRef(false);
  
  // Map pin modal state
  const [isGoogleMapsModalOpen, setIsGoogleMapsModalOpen] = useState(false);
  const [selectedBookingAddress, setSelectedBookingAddress] = useState<string>('');
  const [selectedBookingForSMS, setSelectedBookingForSMS] = useState<any>(null);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  
  // Drag and drop state
  const [isMoveBookingConfirmModalOpen, setIsMoveBookingConfirmModalOpen] = useState(false);
  const [bookingToMove, setBookingToMove] = useState<CalendarBooking | null>(null);
  const [isDraggingMode, setIsDraggingMode] = useState(false);
  const [draggedBooking, setDraggedBooking] = useState<CalendarBooking | null>(null);
  const [isConfirmMoveModalOpen, setIsConfirmMoveModalOpen] = useState(false);
  const [newDropLocation, setNewDropLocation] = useState<{ date: Date; resourceId?: string } | null>(null);
  const [isSavingMove, setIsSavingMove] = useState(false);
  
  const roleDefaultCalendarView = permissions?.calendar?.defaultView || null;
  const canViewMonth = canAccess.calendarMonthView;
  const canViewWeek = canAccess.calendarWeekView;
  const canViewDay = canAccess.calendarDayView;

  const isViewAllowed = (view: 'month' | 'week' | 'day') => {
    if (view === 'month') return canViewMonth;
    if (view === 'week') return canViewWeek;
    return canViewDay;
  };

  const getFirstAllowedView = () => {
    if (canViewMonth) return 'month';
    if (canViewWeek) return 'week';
    if (canViewDay) return 'day';
    return 'month';
  };

  const resolveRequestedCalendarView = (): 'day' | 'month' | null => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('view');
    if (fromQuery === 'day' || fromQuery === 'month') return fromQuery;
    if (pendingUrlCalendarViewRef.current === 'day' || pendingUrlCalendarViewRef.current === 'month') {
      return pendingUrlCalendarViewRef.current;
    }
    return getCalendarViewOverride();
  };

  const applyPendingUrlCalendarView = (): boolean => {
    if (!selectedBusiness?.id || permissionsLoading) return false;

    const viewParam = resolveRequestedCalendarView();
    if (viewParam !== 'day' && viewParam !== 'month') return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (viewParam === 'day' && canViewDay) {
      setShowAdvancedMap(false);
      setViewType('day');
      setCurrentDate(today);
      setDefaultViewLoaded(true);
      urlCalendarViewAppliedRef.current = true;
      pendingUrlCalendarViewRef.current = null;
      if (new URLSearchParams(location.search).get('view')) {
        navigate(location.pathname, { replace: true });
      }
      return true;
    }

    if (viewParam === 'month' && canViewMonth) {
      setShowAdvancedMap(false);
      setViewType('month');
      setCurrentDate(today);
      setDefaultViewLoaded(true);
      urlCalendarViewAppliedRef.current = true;
      pendingUrlCalendarViewRef.current = null;
      if (new URLSearchParams(location.search).get('view')) {
        navigate(location.pathname, { replace: true });
      }
      return true;
    }

    return false;
  };

  // Control centre deep links: apply before default view RPC can overwrite
  useLayoutEffect(() => {
    const requested = resolveRequestedCalendarView();
    if (requested === 'day' || requested === 'month') {
      pendingUrlCalendarViewRef.current = requested;
      applyPendingUrlCalendarView();
    } else {
      pendingUrlCalendarViewRef.current = null;
    }
  }, [
    location.search,
    location.pathname,
    selectedBusiness?.id,
    permissionsLoading,
    canViewDay,
    canViewMonth,
  ]);

  // Drop session override after view is applied (delay survives Strict Mode remount)
  useEffect(() => {
    if (!urlCalendarViewAppliedRef.current || !viewType || !defaultViewLoaded) return;
    const timeoutId = window.setTimeout(() => {
      clearCalendarViewOverride();
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [viewType, defaultViewLoaded]);

  // Load FAB settings and default view (stored in calendar app settings)
  useEffect(() => {
    let cancelled = false;

    const loadFABSettings = async () => {
      if (!selectedBusiness?.id || !supabase) return;
      if (permissionsLoading) return;
      
      try {
        // Load mobile booking setting
        const { data: mobileData, error: mobileError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true
          });
        
        console.log('📱 Mobile booking FAB setting loaded:', {
          data: mobileData,
          error: mobileError,
          willShow: mobileError || mobileData === null ? true : mobileData
        });
        
        if (!mobileError && mobileData !== null) {
          setShowMobileBookingInFAB(mobileData);
        } else {
          setShowMobileBookingInFAB(true); // Default to true
        }

        // Load location booking setting
        const { data: locationData, error: locationError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true
          });
        
        console.log('🏢 Location booking FAB setting loaded:', {
          data: locationData,
          error: locationError,
          willShow: locationError || locationData === null ? true : locationData
        });
        
        if (!locationError && locationData !== null) {
          setShowLocationBookingInFAB(locationData);
        } else {
          setShowLocationBookingInFAB(true); // Default to true
        }

        // Load map view setting
        const { data: mapViewData, error: mapViewError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'map_view_enabled',
            p_default_value: false
          });
        
        if (!mapViewError && mapViewData !== null) {
          setMapViewEnabled(mapViewData);
        } else {
          setMapViewEnabled(false);
        }

        // Load sales pins setting
        const { data: salesPinsData, error: salesPinsError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'sales_pins_enabled',
            p_default_value: true
          });

        if (!salesPinsError && salesPinsData !== null) {
          setSalesPinsEnabled(salesPinsData);
        } else {
          setSalesPinsEnabled(true);
        }

        // Load tasks setting
        const { data: tasksData, error: tasksError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_tasks_in_fab',
            p_default_value: false
          });

        if (!tasksError && tasksData !== null) {
          setTasksEnabled(tasksData);
        } else {
          setTasksEnabled(false);
        }

        // Load field quotes setting (same gate as the FAB)
        const { data: fieldQuotesData, error: fieldQuotesError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_field_quotes_in_fab',
            p_default_value: false
          });

        if (!fieldQuotesError && fieldQuotesData !== null) {
          setShowFieldQuotesInFAB(fieldQuotesData);
        } else {
          setShowFieldQuotesInFAB(false);
        }

        if (cancelled) return;

        // Load default calendar view (skip when opened via ?view=day|month from control centre)
        if (urlCalendarViewAppliedRef.current || applyPendingUrlCalendarView()) {
          if (!defaultViewLoaded) {
            setDefaultViewLoaded(true);
          }
        } else if (resolveRequestedCalendarView()) {
          // Control-centre override still pending — do not apply business default
        } else if (!defaultViewLoaded || (roleDefaultCalendarView && !roleDefaultViewApplied)) {
          const { data: viewData, error: viewError } = await supabase
            .rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'calendar',
              p_setting_key: 'default_calendar_view',
              p_default_value: 'month'
            });

          if (cancelled) return;
          
          console.log('📅 Default calendar view loaded:', viewData);
          
          const businessDefaultView = (viewData as 'month' | 'week' | 'day') || 'month';
          const preferredView = roleDefaultCalendarView || businessDefaultView;
          const defaultView = isViewAllowed(preferredView) ? preferredView : getFirstAllowedView();
          console.log('📅 Setting default view to:', defaultView);
          if (cancelled) return;
          setViewType(defaultView);
          
          // Initialize date state based on default view
          if (defaultView === 'day') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            setCurrentDate(today);
            console.log('📅 Day view: Initialized currentDate to today:', format(today, 'yyyy-MM-dd'));
          } else if (defaultView === 'week') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            // Find the start of the week (Sunday)
            const dayOfWeek = today.getDay();
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - dayOfWeek);
            setWeekStartDate(weekStart);
            console.log('📅 Week view: Initialized weekStartDate to:', format(weekStart, 'yyyy-MM-dd'));
          }
          
          if (!cancelled) {
            setDefaultViewLoaded(true);
            if (roleDefaultCalendarView) {
              setRoleDefaultViewApplied(true);
            }
          }
        }
      } catch (error) {
        console.error('Error loading FAB settings:', error);
        if (!cancelled) {
          setShowMobileBookingInFAB(true);
          setShowLocationBookingInFAB(true);
        }
      }
    };
    
    loadFABSettings();

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, permissionsLoading, roleDefaultCalendarView, defaultViewLoaded, roleDefaultViewApplied]);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const checkGcalConnection = async () => {
      const { data } = await supabase
        .from('google_calendar_sync')
        .select('id, is_active')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      setGcalConnected(!!data);
      setGcalSyncId(data?.id || null);
    };
    checkGcalConnection();
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const handleCalendarSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_tasks_in_fab') {
        setTasksEnabled(event.detail.value);
      }
      if (event.detail.key === 'show_field_quotes_in_fab') {
        setShowFieldQuotesInFAB(event.detail.value);
      }
      if (event.detail.key === 'week_view_filter') {
        const val = event.detail.value;
        if (val === 'all' || val === 'resources' || val === 'assets') {
          setWeekViewFilter(val);
        }
      }
      if (event.detail.key === 'map_view_enabled') {
        setMapViewEnabled(event.detail.value);
        if (!event.detail.value) {
          setShowAdvancedMap(false);
        }
      }
      if (event.detail.key === 'sales_pins_enabled') {
        setSalesPinsEnabled(event.detail.value);
      }
    };
    window.addEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);
    return () => {
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);
    };
  }, []);

  useEffect(() => {
    setRoleDefaultViewApplied(false);
  }, [roleDefaultCalendarView, selectedBusiness?.id]);

  useEffect(() => {
    if (!viewType) return;
    if (
      urlCalendarViewAppliedRef.current ||
      pendingUrlCalendarViewRef.current ||
      getCalendarViewOverride()
    ) {
      return;
    }
    if (!isViewAllowed(viewType)) {
      const fallbackView = getFirstAllowedView();
      setViewType(fallbackView);
      if (fallbackView === 'day') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setCurrentDate(today);
      } else if (fallbackView === 'week') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayOfWeek = today.getDay();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - dayOfWeek);
        setWeekStartDate(weekStart);
      }
    }
  }, [viewType, canViewMonth, canViewWeek, canViewDay]);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPWA, setIsPWA] = useState(false);
  const [isSalesModalOpen, setIsSalesModalOpen] = useState(false);
  const [monthlySales, setMonthlySales] = useState<{ [key: number]: number }>({});
  const [isLoadingMonthlySales, setIsLoadingMonthlySales] = useState(false);
  const [salesModalYear, setSalesModalYear] = useState(new Date().getFullYear());
  const [isProjectInfoModalOpen, setIsProjectInfoModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [isFieldQuoteDetailModalOpen, setIsFieldQuoteDetailModalOpen] = useState(false);
  const [selectedFieldQuoteId, setSelectedFieldQuoteId] = useState<string | null>(null);
  const [isResourceDetailsModalOpen, setIsResourceDetailsModalOpen] = useState(false);
  const [selectedResourceForDetails, setSelectedResourceForDetails] = useState<Resource | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [resourceAvailabilities, setResourceAvailabilities] = useState<Map<string, ResourceWorkingAvailability>>(new Map());
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [calendarAssets, setCalendarAssets] = useState<Resource[]>([]);
  const [weekViewFilter, setWeekViewFilter] = useState<'all' | 'resources' | 'assets'>('all');
  const [weekViewFilterDropdownOpen, setWeekViewFilterDropdownOpen] = useState(false);
  const weekViewFilterRef = useRef<HTMLDivElement>(null);

  // Close week view filter dropdown on outside click — don't also create on the calendar under the cursor
  const closeWeekViewFilter = useCallback(() => setWeekViewFilterDropdownOpen(false), []);
  useDismissOverlayProtectingCalendarCreate(weekViewFilterDropdownOpen, closeWeekViewFilter, [weekViewFilterRef]);

  const saveWeekViewFilter = useCallback(async (value: 'all' | 'resources' | 'assets') => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: 'week_view_filter',
        p_setting_value: value
      });
    } catch (err) {
      console.error('Error saving week view filter:', err);
    }
  }, [selectedBusiness?.id]);

  const [isAssetModalOpenFromCalendar, setIsAssetModalOpenFromCalendar] = useState(false);
  const [selectedAssetForCalendar, setSelectedAssetForCalendar] = useState<Asset | null>(null);
  // Initialize weekStartDate to today
  const [weekStartDate, setWeekStartDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  
  // Calculate week days (7 days starting from today)
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const start = new Date(weekStartDate);
    start.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStartDate]);
  
  // Calculate week title
  const weekTitle = useMemo(() => {
    if (weekDays.length === 0) return '';
    const start = weekDays[0];
    const end = weekDays[6];
    
    // If same month
    if (start.getMonth() === end.getMonth()) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    }
    // Different months
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  }, [weekDays]);

  // Calculate compact week title for mobile
  const weekTitleCompact = useMemo(() => {
    if (weekDays.length === 0) return '';
    const start = weekDays[0];
    const end = weekDays[6];
    
    // If same month
    if (start.getMonth() === end.getMonth()) {
      return `${format(start, 'M/d')} - ${format(end, 'M/d')}`;
    }
    // Different months
    return `${format(start, 'M/d')} - ${format(end, 'M/d')}`;
  }, [weekDays]);
  
  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      // Method 1: Check for display-mode: standalone
      const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // Method 2: Check for iOS PWA
      const isIOSPWA = (window.navigator as any).standalone === true;
      
      // Method 3: Check for PWA launch parameters
      const urlParams = new URLSearchParams(window.location.search);
      const launchedAsPWA = urlParams.get('pwa') === 'true';
      
      // Method 4: Check if body has pwa-mode class (set by App.tsx)
      const hasPWAClass = document.body.classList.contains('pwa-mode');
      
      const isPWA = isDisplayModeStandalone || isIOSPWA || launchedAsPWA || hasPWAClass;
      
      setIsPWA(isPWA);
      console.log('CalendarV2 PWA detection:', { isPWA, isDisplayModeStandalone, isIOSPWA, launchedAsPWA, hasPWAClass });
    };
    
    checkPWA();
    
    // Also check periodically in case the class is added later
    const interval = setInterval(checkPWA, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Remove local sidebar state - we'll use CSS variables instead
  
  // Reference to the calendar instance
  const calendarRef = useRef<FullCalendar>(null);
  const isMounted = useRef(true);
  
  // Add reference to track fetch calls to prevent multiple simultaneous fetches
  const isFetching = useRef(false);
  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasInitialScrolled = useRef(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [isCalendarReady, setIsCalendarReady] = useState(false);

  // Add feedback state
  const [bookingFeedback, setBookingFeedback] = useState<Map<string, { rating: number; hasText: boolean }>>(new Map());

  // Modal persistence hook
  // Removed all persistence systems

  // Removed all persistence systems

  // Get cache key based on business ID and date range
  const getCacheKey = useCallback((businessId: string, startDate: Date, endDate: Date) => {
    return `${businessId}_${startDate.toISOString()}_${endDate.toISOString()}`;
  }, []);

  // Check if assets feature is enabled for this business
  useEffect(() => {
    const checkAssetsEnabled = async () => {
      if (!selectedBusiness?.id || !supabase) { setAssetsEnabled(false); return; }
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'teams',
          p_setting_key: 'assets_enabled',
          p_default_value: false
        });
        if (!error && data !== null) setAssetsEnabled(data);

        // Load the saved week view filter preference
        const { data: filterData, error: filterError } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'week_view_filter',
          p_default_value: 'all'
        });
        if (!filterError && filterData !== null) {
          const val = typeof filterData === 'string' ? filterData : 'all';
          if (val === 'all' || val === 'resources' || val === 'assets') {
            setWeekViewFilter(val);
          }
        }
      } catch { setAssetsEnabled(false); }
    };
    checkAssetsEnabled();
  }, [selectedBusiness?.id]);

  // Fetch resources for the business
  const fetchResources = useCallback(async () => {
    if (!selectedBusiness?.id) {
      setResources([]);
      setCalendarAssets([]);
      return;
    }

    try {
      setIsLoadingResources(true);
      const data = await getResources(selectedBusiness.id);
      // Filter to active resources only, excluding "not bookable" resources
      const activeResources = data.filter(r => r.status === 'active' && !r.not_bookable);
      // Filter by permission if needed
      const filteredResources = canAccess.viewAllBookings 
        ? activeResources 
        : activeResources.filter(r => r.id === currentResource?.id);
      // Sort resources consistently by first_name, then last_name to ensure stable order
      const sortedResources = [...filteredResources].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setResources(sortedResources);
      
      // Fetch resource availabilities
      const availabilityMap = new Map<string, ResourceWorkingAvailability>();
      if (selectedBusiness?.id) {
        try {
          const availabilities = await getBusinessResourcesWorkingAvailability(selectedBusiness.id);
          availabilities.forEach(avail => {
            availabilityMap.set(avail.resource_id, avail);
          });
        } catch (err) {
          console.error('Error fetching resource availabilities:', err);
        }
      }

      // Fetch assets if enabled
      if (assetsEnabled && selectedBusiness?.id) {
        try {
          const assetData = await getAssets(selectedBusiness.id);
          const activeAssets = assetData.filter(a => a.status === 'active' && !a.not_bookable);

          // Map assets to Resource-compatible objects
          const mappedAssets: Resource[] = activeAssets.map(asset => ({
            id: asset.id,
            business_id: asset.business_id,
            first_name: asset.name,
            last_name: '',
            email: '',
            phone: '',
            role: '',
            hourly_rate: 0,
            commission_rate: 0,
            status: asset.status,
            created_at: asset.created_at,
            updated_at: asset.updated_at,
            schedule_availability: asset.schedule_availability || {},
            compensation_type: 'hourly' as const,
            flat_rate_amount: 0,
            commission_percentage: 0,
            earnings_ytd: 0,
            earnings_mtd: 0,
            earnings_last_month: 0,
            is_mobile_available: false,
            not_bookable: asset.not_bookable,
            is_admin: false,
            avatar_url: asset.image_url || undefined,
            _isAsset: true,
            _assetType: asset.asset_type,
            _assetRaw: asset,
          } as any));

          setCalendarAssets(mappedAssets);

          // Fetch asset availabilities and merge into the same map
          const assetAvailabilities = await getBusinessAssetsAvailability(selectedBusiness.id);
          assetAvailabilities.forEach(avail => {
            availabilityMap.set(avail.asset_id, {
              resource_working_availability_id: avail.id,
              resource_id: avail.asset_id,
              business_id: avail.business_id,
              monday: avail.monday,
              tuesday: avail.tuesday,
              wednesday: avail.wednesday,
              thursday: avail.thursday,
              friday: avail.friday,
              saturday: avail.saturday,
              sunday: avail.sunday,
              created_at: avail.created_at,
              updated_at: avail.updated_at,
            } as ResourceWorkingAvailability);
          });
        } catch (err) {
          console.error('Error fetching assets for calendar:', err);
          setCalendarAssets([]);
        }
      } else {
        setCalendarAssets([]);
      }

      setResourceAvailabilities(availabilityMap);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setResources([]);
      setCalendarAssets([]);
    } finally {
      setIsLoadingResources(false);
    }
  }, [selectedBusiness?.id, canAccess.viewAllBookings, currentResource?.id, assetsEnabled]);

  // Initialize viewStartDate and viewEndDate for week/day views
  useEffect(() => {
    if (viewType === 'week') {
      console.log('📅 Initializing dates for week view');
      setViewStartDate(weekDays[0] || weekStartDate);
      setViewEndDate(weekDays[6] || new Date(weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000));
    } else if (viewType === 'day') {
      console.log('📅 Initializing dates for day view');
      setViewStartDate(currentDate);
      setViewEndDate(currentDate);
    }
  }, [viewType, weekDays, weekStartDate, currentDate]);

  // Load resources when business changes or view changes to week/day
  useEffect(() => {
    if (viewType === 'week' || viewType === 'day' || showAdvancedMap) {
      console.log('🔄 Fetching resources for', viewType, 'view', showAdvancedMap ? '(advanced map)' : '');
      fetchResources().then(() => {
        // Set calendar as loaded for week/day views after resources are fetched
        console.log('✅ Resources loaded, setting calendarLoaded = true');
        setCalendarLoaded(true);
        setIsCalendarReady(true);
      });
    }
  }, [selectedBusiness?.id, viewType, fetchResources]);

  // Memoize the currentMonthStart and currentMonthEnd
  const dateRange = useMemo(() => {
    const currentMonthStart = new Date(viewStartDate);
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);
    
    const currentMonthEnd = new Date(currentMonthStart);
    currentMonthEnd.setMonth(currentMonthEnd.getMonth() + 1);
    currentMonthEnd.setDate(0);
    currentMonthEnd.setHours(23, 59, 59, 999);
    
    return { currentMonthStart, currentMonthEnd };
  }, [viewStartDate]);
  
  // Function to fetch approved time off requests
  const fetchTimeOffRequests = useCallback(async (startDate: Date, endDate: Date) => {
    if (!selectedBusiness?.id || !supabase) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('time_off_requests')
        .select(`
          id,
          start_date,
          end_date,
          request_type,
          is_partial_day,
          start_time,
          end_time,
          resource_id,
          resources:resource_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'approved')
        .lte('start_date', format(endDate, 'yyyy-MM-dd'))
        .gte('end_date', format(startDate, 'yyyy-MM-dd'));

      if (error) {
        console.error('Error fetching time off requests:', error);
        return [];
      }

      // Transform time off requests into CalendarBooking format
      const timeOffEvents: CalendarBooking[] = (data || []).map((request) => {
        const resource = Array.isArray(request.resources) ? request.resources[0] : request.resources;
        const resourceName = resource 
          ? `${resource.first_name} ${resource.last_name}`
          : 'Unknown Resource';

        const typeLabels = {
          vacation: 'Vacation',
          sick: 'Sick Leave',
          personal: 'Personal Day',
          family: 'Family Emergency',
          bereavement: 'Bereavement',
          other: 'Other'
        };

        const typeLabel = typeLabels[request.request_type as keyof typeof typeLabels] || request.request_type;

        const eventTitle = `${typeLabel} - ${resourceName}`;
        
        // For all-day events, FullCalendar expects the end date to be exclusive (day after the last day)
        let endDate = request.end_date;
        if (!request.is_partial_day) {
          // Parse as local date to avoid timezone issues
          const [year, month, day] = request.end_date.split('-').map(Number);
          const localEndDate = new Date(year, month - 1, day);
          const nextDay = addDays(localEndDate, 1);
          endDate = format(nextDay, 'yyyy-MM-dd');
        }

        return {
          id: `timeoff_${request.id}`,
          title: eventTitle,
          booking_number: 0, // Not applicable for time off
          start: request.is_partial_day && request.start_time 
            ? `${request.start_date}T${request.start_time}:00`
            : request.start_date,
          end: request.is_partial_day && request.end_time
            ? `${request.end_date}T${request.end_time}:00`
            : endDate,
          backgroundColor: '#000000', // Black color for time off
          borderColor: '#000000',
          textColor: '#ffffff', // White text on black background
          allDay: !request.is_partial_day,
          display: 'block',
          classNames: ['time-off-event'],
          extendedProps: {
            customer: resourceName,
            status: 'Time Off',
            booking_number: 0,
            subtotal: 0,
            total_amount: 0,
            is_time_off: true,
            time_off_type: typeLabel,
            resource_name: resourceName
          }
        };
      });

      return timeOffEvents;
    } catch (error) {
      console.error('Error fetching time off requests:', error);
      return [];
    }
  }, [selectedBusiness?.id]);

  // Function to fetch field quotes as calendar events
  const fetchFieldQuotes = useCallback(
    async (startDate: Date, endDate: Date, filterResourceId?: string) => {
      if (!selectedBusiness?.id) return [];
      return getCalendarFieldQuotes(
        selectedBusiness.id,
        startDate,
        endDate,
        filterResourceId
      );
    },
    [selectedBusiness?.id]
  );

  // Optimized fetch with immediate rendering - no more two-phase loading
  const fetchBookings = useCallback(async (forceFetch = false, overrideDate?: Date) => {
    if (!selectedBusiness?.id) return;
    if (isFetching.current) return;
    
    // Default to month view if viewType not set yet
    const currentViewType = viewType || 'month';
    // Use overrideDate if provided, otherwise use currentDate from state
    // For day view, ensure we always have a valid date
    let dateToUse = overrideDate || currentDate;
    if (currentViewType === 'day') {
      if (!dateToUse) {
        // No date provided, use today
        dateToUse = new Date();
        dateToUse.setHours(0, 0, 0, 0);
        console.warn('⚠️ Day view: No date provided, using today:', format(dateToUse, 'yyyy-MM-dd'));
      } else {
        // Normalize date to start of day for day view
        const normalized = new Date(dateToUse);
        normalized.setHours(0, 0, 0, 0);
        dateToUse = normalized;
      }
    }
    console.log('📊 fetchBookings called for view:', currentViewType, {
      overrideDate: overrideDate ? format(overrideDate, 'yyyy-MM-dd HH:mm') : 'none',
      currentDate: currentDate ? format(currentDate, 'yyyy-MM-dd HH:mm') : 'none',
      dateToUse: dateToUse ? format(dateToUse, 'yyyy-MM-dd HH:mm') : 'none',
      dateToUseISO: dateToUse ? dateToUse.toISOString() : 'none'
    });
    
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    try {
      setIsLoading(true);
      setError(null);
      isFetching.current = true;
      
      // Calculate date range based on view type
      let startDate: Date;
      let endDate: Date;
      
      if (currentViewType === 'week') {
        // Week view: use weekDays range
        if (weekDays.length < 7) {
          console.warn('⚠️ weekDays not ready yet, using weekStartDate directly');
          startDate = new Date(weekStartDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(weekStartDate);
          endDate.setDate(endDate.getDate() + 6);
          endDate.setHours(23, 59, 59, 999);
        } else {
          startDate = new Date(weekDays[0]);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(weekDays[6]);
          endDate.setHours(23, 59, 59, 999);
        }
      } else if (currentViewType === 'day') {
        // Day view: use current day only (use overrideDate if provided)
        // Similar to week view - ensure we have a valid date even if currentDate isn't ready
        if (!dateToUse || !(dateToUse instanceof Date) || isNaN(dateToUse.getTime())) {
          console.warn('⚠️ Day view: dateToUse is invalid, using today');
          dateToUse = new Date();
          dateToUse.setHours(0, 0, 0, 0);
        }
        
        const dayDate = new Date(dateToUse);
        dayDate.setHours(0, 0, 0, 0);
        startDate = new Date(dayDate);
        endDate = new Date(dayDate);
        endDate.setHours(23, 59, 59, 999);
        
        console.log('📅 Day view date range:', {
          startDate: format(startDate, 'yyyy-MM-dd HH:mm:ss'),
          endDate: format(endDate, 'yyyy-MM-dd HH:mm:ss'),
          dateToUse: format(dayDate, 'yyyy-MM-dd HH:mm:ss'),
          overrideDate: overrideDate ? format(overrideDate, 'yyyy-MM-dd HH:mm:ss') : 'none',
          currentDate: currentDate ? format(currentDate, 'yyyy-MM-dd HH:mm:ss') : 'none',
          dateToUseValid: dateToUse instanceof Date && !isNaN(dateToUse.getTime())
        });
      } else {
        // Month view: calculate from the date being used (overrideDate or currentDate)
        // This ensures we fetch for the correct month when navigating
        const monthStart = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), 1);
        const monthEnd = new Date(dateToUse.getFullYear(), dateToUse.getMonth() + 1, 0);
        monthStart.setHours(0, 0, 0, 0);
        monthEnd.setHours(23, 59, 59, 999);
        startDate = monthStart;
        endDate = monthEnd;
      }
      
      console.log('📅 fetchBookings for', currentViewType, 'view:', {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        weekDaysLength: weekDays.length,
        overrideDate: overrideDate ? format(overrideDate, 'yyyy-MM-dd') : 'none',
        currentDate: format(currentDate, 'yyyy-MM-dd'),
        dateToUse: format(dateToUse, 'yyyy-MM-dd'),
        forceFetch
      });
      
      // Determine if we should filter by resource based on the viewAllBookings permission
      const shouldFilterByResource = !canAccess.viewAllBookings && currentResource?.id;
      const filterResourceId = shouldFilterByResource ? currentResource.id : undefined;
      
      // Include resource filtering in cache key if filtering is active
      const cacheKey = getCacheKey(
        selectedBusiness.id, 
        startDate, 
        endDate
      ) + (filterResourceId ? `_resource_${filterResourceId}` : '');
      
      // Helper function to fetch buffer blocks
      const fetchBufferBlocks = async (_mobileBookings: CalendarBooking[]): Promise<CalendarBooking[]> => {
        const bufferBlocks: CalendarBooking[] = [];
        if (supabase) {
          try {
            // Format dates for query
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');
            
            // Fetch buffer blocks that overlap with the date range
            // A buffer block overlaps if: start_date <= endDate AND end_date >= startDate
            // We fetch by date range directly, not just by booking IDs, because buffer blocks
            // might be visible in the current week even if their parent booking is in a different week
            let bufferQuery = supabase
              .from('mobile_bookings')
              .select(`
                id,
                business_id,
                resource_id,
                asset_id,
                booking_id,
                start_date,
                start_time,
                end_date,
                end_time,
                buffer_time,
                is_buffer_block
              `)
              .eq('business_id', selectedBusiness.id)
              .eq('is_buffer_block', true)
              .lte('start_date', formattedEndDate)
              .gte('end_date', formattedStartDate);
            
            // Filter by resource if user doesn't have viewAllBookings permission
            if (filterResourceId) {
              bufferQuery = bufferQuery.or(`resource_id.eq.${filterResourceId},asset_id.eq.${filterResourceId}`);
            }
            
            const { data: bufferBlocksData, error: bufferError } = await bufferQuery;
            
            console.log('Buffer blocks query result:', {
              dateRange: `${formattedStartDate} to ${formattedEndDate}`,
              filterResourceId,
              bufferBlocksCount: bufferBlocksData?.length || 0,
              error: bufferError
            });
            
            if (bufferError) {
              console.error('Error fetching buffer blocks:', bufferError);
            } else if (bufferBlocksData) {
              // Convert buffer blocks to CalendarBooking format
              bufferBlocksData.forEach(block => {
                // Parse dates and times - use local time to avoid timezone issues
                const [startYear, startMonth, startDay] = block.start_date.split('-').map(Number);
                const [startHour, startMinute] = block.start_time.split(':').map(Number);
                // Create date in local timezone (not UTC)
                const startDateObj = new Date(startYear, startMonth - 1, startDay, startHour, startMinute, 0, 0);
                
                const [endYear, endMonth, endDay] = block.end_date.split('-').map(Number);
                const [endHour, endMinute] = block.end_time.split(':').map(Number);
                // Create date in local timezone (not UTC)
                const endDateObj = new Date(endYear, endMonth - 1, endDay, endHour, endMinute, 0, 0);
                
                // Convert to ISO strings (this will convert to UTC, but parseISO will handle it correctly)
                const start = startDateObj.toISOString();
                const end = endDateObj.toISOString();
                
                console.log('🔍 Buffer block date conversion:', {
                  blockId: block.id,
                  start_date: block.start_date,
                  start_time: block.start_time,
                  end_date: block.end_date,
                  end_time: block.end_time,
                  startISO: start,
                  endISO: end,
                  startLocal: startDateObj.toString(),
                  endLocal: endDateObj.toString()
                });
                
                // Use resource_id if present, otherwise fall back to asset_id
                // (asset IDs work because CalendarV2 maps assets to Resource-compatible objects)
                const blockOwnerId = block.resource_id || block.asset_id;
                
                bufferBlocks.push({
                  id: `buffer-${block.id}`,
                  title: 'Buffer',
                  booking_number: 0,
                  start,
                  end,
                  backgroundColor: '#000000',
                  borderColor: '#000000',
                  secondaryColor: null,
                  allDay: false,
                  display: 'block',
                  resourceId: blockOwnerId,
                  extendedProps: {
                    customer: '',
                    booking_number: 0,
                    subtotal: 0,
                    total_amount: 0,
                    status: 'buffer',
                    is_buffer_block: true,
                    buffer_time: block.buffer_time,
                    booking_id: block.booking_id,
                    resource_id: blockOwnerId,
                    resources: block.resource_id ? [{
                      id: block.resource_id,
                      first_name: '',
                      last_name: ''
                    }] : [],
                    booking_assets: block.asset_id ? [{
                      id: block.asset_id,
                      asset_id: block.asset_id
                    }] : []
                  }
                });
              });
              
              console.log(`Fetched ${bufferBlocks.length} buffer blocks for CalendarV2`, {
                bufferBlocks: bufferBlocks.map(b => ({
                  id: b.id,
                  resourceId: b.resourceId,
                  extendedPropsResourceId: b.extendedProps.resource_id,
                  start: b.start,
                  end: b.end
                }))
              });
            }
          } catch (error) {
            console.error('Error processing buffer blocks:', error);
          }
        }
        return bufferBlocks;
      };
      
      // Check cache first for both bookings and time off requests
      const cachedBookings = basicBlocksCache.get(cacheKey);
      const cachedTimeOff = timeOffCache.get(cacheKey);
      const now = Date.now();
      
      let bookings: CalendarBooking[] = [];
      let timeOffEvents: CalendarBooking[] = [];
      let fieldQuotes: CalendarBooking[] = [];
      
      if (!forceFetch && cachedBookings && cachedTimeOff && 
          (now - cachedBookings.timestamp) < CACHE_DURATION &&
          (now - cachedTimeOff.timestamp) < CACHE_DURATION) {
        console.log('📦 Using cached data for date range:', format(startDate, 'yyyy-MM-dd'), 'to', format(endDate, 'yyyy-MM-dd'));
        // Use cached data for bookings and time off, but ALWAYS fetch field quotes and buffer blocks fresh in parallel
        bookings = cachedBookings.data;
        timeOffEvents = cachedTimeOff.data;
        
        console.log('📦 Cached bookings count:', bookings.length);
        
        // Remove any existing buffer blocks and tasks from cached data (they might be stale)
        bookings = bookings.filter(b => b.extendedProps?.is_buffer_block !== true && b.extendedProps?.is_task !== true);
        
        // Fetch field quotes, buffer blocks, and tasks in parallel
        const [fieldQuotesResult, bufferBlocks, cachedTasks] = await Promise.all([
          fetchFieldQuotes(startDate, endDate, filterResourceId),
          fetchBufferBlocks([]),
          tasksEnabled
            ? getCalendarTasks(selectedBusiness.id, startDate, endDate, filterResourceId)
            : Promise.resolve([])
        ]);
        fieldQuotes = fieldQuotesResult;
        
        console.log('Fetched buffer blocks count (cached path):', bufferBlocks.length);
        // Ensure buffer blocks and tasks are added
        bookings = [...bookings, ...bufferBlocks, ...cachedTasks];
        console.log('Final bookings count after adding buffer blocks (cached path):', bookings.length, 'buffer blocks:', bufferBlocks.length);
      } else {
        // Fetch bookings (both location and mobile), time off requests, field quotes, buffer blocks, and tasks simultaneously
        const fetchPromises: Promise<any>[] = [
          getCalendarBookings(
            selectedBusiness.id,
            startDate,
            endDate,
            true, // isLocationBooking = true (location bookings)
            filterResourceId
          ),
          getCalendarBookings(
            selectedBusiness.id,
            startDate,
            endDate,
            false, // isLocationBooking = false (mobile bookings)
            filterResourceId
          ),
          fetchTimeOffRequests(startDate, endDate),
          fetchFieldQuotes(startDate, endDate, filterResourceId),
          fetchBufferBlocks([]),
          tasksEnabled
            ? getCalendarTasks(selectedBusiness.id, startDate, endDate, filterResourceId)
            : Promise.resolve([])
        ];

        const [fetchedLocationBookings, fetchedMobileBookings, fetchedTimeOff, fetchedFieldQuotes, bufferBlocks, fetchedTasks] = await Promise.all(fetchPromises);
        
        console.log('Fetched buffer blocks count (parallel fetch):', bufferBlocks.length);
        if (fetchedTasks.length > 0) {
          console.log('📋 Fetched tasks count:', fetchedTasks.length);
        }
        
        // Combine location and mobile bookings with buffer blocks and tasks
        bookings = [...fetchedLocationBookings, ...fetchedMobileBookings, ...bufferBlocks, ...fetchedTasks];
        timeOffEvents = fetchedTimeOff;
        fieldQuotes = fetchedFieldQuotes;
        
        // Update both caches (cache bookings without buffer blocks, we'll fetch them fresh)
        // Store bookings without buffer blocks in cache
        const bookingsWithoutBuffers = bookings.filter(b => b.extendedProps?.is_buffer_block !== true);
        basicBlocksCache.set(cacheKey, { data: bookingsWithoutBuffers, timestamp: now });
        timeOffCache.set(cacheKey, { data: timeOffEvents, timestamp: now });
      }

      // Check for active timers for this resource
      if (currentResource?.id) {
        try {
          const { data: activeTimerLogs } = await supabase
            .from('booking_timer_logs')
            .select('booking_id')
            .eq('resource_id', currentResource.id)
            .eq('is_running', true);
          
          if (activeTimerLogs && activeTimerLogs.length > 0) {
            // The timer context will handle displaying the timer on the correct booking
          }
        } catch (error) {
          console.error('Error checking for active timers:', error);
        }
      }

      // Debug: Log final bookings count including buffer blocks
      const bufferBlocksCount = bookings.filter(b => b.extendedProps?.is_buffer_block === true).length;
      console.log('📊 Final bookings array before state update:', {
        totalBookings: bookings.length,
        bufferBlocksCount,
        mobileBookingsCount: bookings.filter(b => b.extendedProps?.mobile_booking === true).length,
        dateRange: `${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`,
        bufferBlockIds: bookings.filter(b => b.extendedProps?.is_buffer_block === true).map(b => b.id),
        sampleBookingDates: bookings.slice(0, 3).map(b => ({
          id: b.id,
          title: b.title,
          start: b.start,
          startDate: format(parseISO(b.start), 'yyyy-MM-dd')
        }))
      });

      // Update the calendar immediately with complete visual data
      if (isMounted.current) {
        // Ensure buffer blocks are included in the state update
        const finalBookings = [...bookings];
        setBookings(finalBookings);
        setTimeOffRequests(timeOffEvents);
        setFieldQuoteEvents(fieldQuotes || []);
        setLoadedBookingIds(new Set(finalBookings.map(b => b.id)));

        // Fetch Google Calendar events for the same date range
        if (supabase && selectedBusiness?.id) {
          supabase
            .from('google_calendar_events')
            .select('id, title, start_time, end_time, all_day, location, description, resource_id, google_event_id, status, meet_link, conference_data, attendees, organizer_email, organizer_name')
            .eq('business_id', selectedBusiness.id)
            .eq('is_deleted', false)
            .gte('start_time', startDate.toISOString())
            .lte('start_time', endDate.toISOString())
            .then(({ data: gcalEvents }) => {
              if (isMounted.current) {
                setGoogleCalendarEvents(gcalEvents || []);
              }
            })
            .catch((err: any) => console.warn('Failed to fetch Google Calendar events:', err));
        }
        
        // Verify buffer blocks are in state after update
        setTimeout(() => {
          console.log('State after update - buffer blocks count:', finalBookings.filter(b => b.extendedProps?.is_buffer_block === true).length);
        }, 100);
        
        // Load feedback for completed bookings
        loadFeedbackForBookings(finalBookings);
        
        setTimeout(() => {
          if (isMounted.current) {
            setIsLoading(false);
            setIsCalendarReady(true); // Mark calendar as ready after normal loading
            // Ensure calendarLoaded is true for week/day views after bookings load
            if (currentViewType === 'week' || currentViewType === 'day') {
              setCalendarLoaded(true);
              console.log('✅ Bookings loaded for', currentViewType, 'view, setting calendarLoaded = true');
            }
          }
        }, 200); // Small delay to ensure everything is fully rendered
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (isMounted.current) {
        setError('Failed to load bookings. Please try refreshing the page.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
      isFetching.current = false;
    }
  }, [selectedBusiness?.id, dateRange, getCacheKey, canAccess.viewAllBookings, currentResource?.id, fetchTimeOffRequests, fetchFieldQuotes, viewType, weekDays, currentDate, tasksEnabled]);

  
  // Calculate total sales - with fallback to database query if optimized version fails
  const calculateTotalSales = useCallback(async () => {
    if (!selectedBusiness?.id) {
      console.log('⚠️ Cannot calculate sales: no business selected');
      return;
    }
    
    // Early return if bookings array is not ready (but allow calculation even if empty to show $0.00)
    // We'll calculate even with empty array to show $0.00 for months with no bookings
    
      // Get the exact month and year being displayed in the calendar view
      const calendarYear = currentDate.getFullYear();
      const calendarMonth = currentDate.getMonth(); // 0-based month (0 = January)
      
      // Calculate first and last day of the displayed month
      const monthStart = new Date(calendarYear, calendarMonth, 1);
      const monthEnd = new Date(calendarYear, calendarMonth + 1, 0);
      
    // Format to YYYY-MM-DD for comparison
      const formattedMonthStart = monthStart.toISOString().split('T')[0];
      const formattedMonthEnd = monthEnd.toISOString().split('T')[0];
      
    // Check cache first
    const cacheKey = `${selectedBusiness.id}_${formattedMonthStart}_${formattedMonthEnd}`;
    const cachedSales = salesCache.get(cacheKey);
    const now = Date.now();
    
    if (cachedSales && (now - cachedSales.timestamp) < CACHE_DURATION) {
      // Use cached sales
      setTotalSales(cachedSales.totalSales);
      setSalesInitiallyLoaded(true);
      setIsCalculatingSales(false);
      return;
    }
    
    setIsCalculatingSales(true);
    
    // Always query database directly for sales calculation
    // This ensures hidden bookings are included in the sales total
    // (hidden bookings are filtered from calendar display but should count toward sales)
    try {
      if (!supabase) {
        throw new Error('Supabase client not available');
      }
      
      const { data: salesData, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, discount_amount, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false) // Exclude deleted bookings
        .neq('status', 'cancelled') // Exclude cancelled bookings
        .or('in_que.is.null,in_que.eq.false')
        .gte('end_date', formattedMonthStart)
        .lte('end_date', formattedMonthEnd);
      
      if (salesError) {
        console.error('Error fetching sales data:', salesError);
        if (isMounted.current) {
          setTotalSales(0);
          setSalesInitiallyLoaded(true);
          setIsCalculatingSales(false);
        }
        return;
      }
      
      // Standalone (booking-less) invoices issued this month count as sales too.
      const standaloneRows = await fetchStandaloneInvoiceSales(selectedBusiness.id, {
        start: new Date(calendarYear, calendarMonth, 1),
        end: new Date(calendarYear, calendarMonth + 1, 0, 23, 59, 59, 999),
      });
      const standaloneSum = sumStandaloneInvoiceSales(standaloneRows);

      const bookingSum = (salesData || []).reduce((acc, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return acc + (subtotal - discountAmount);
      }, 0);
      const sum = bookingSum + standaloneSum;

      // Cache the result
      salesCache.set(cacheKey, { totalSales: sum, timestamp: now });

      console.log('💰 Sales total:', sum, `(${salesData?.length || 0} bookings + ${standaloneRows.length} standalone invoices)`);

      if (isMounted.current) {
        setTotalSales(sum);
        setSalesInitiallyLoaded(true);
        setIsCalculatingSales(false);
      }
    } catch (err) {
      console.error('Error calculating sales (fallback):', err);
      if (isMounted.current) {
        setTotalSales(0);
        setSalesInitiallyLoaded(true);
        setIsCalculatingSales(false);
      }
    }
  }, [selectedBusiness?.id, currentDate, bookings, supabase]);
  
  // Fetch sales for all months of a given year
  const fetchMonthlySales = useCallback(async (year: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    setIsLoadingMonthlySales(true);
    
    try {
      const salesData: { [key: number]: number } = {};
      
      // Fetch all bookings for the entire year
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);
      
      const formattedYearStart = yearStart.toISOString().split('T')[0];
      const formattedYearEnd = yearEnd.toISOString().split('T')[0];
      
      const { data: salesDataAll, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, discount_amount, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false)
        .or('in_que.is.null,in_que.eq.false')
        .gte('end_date', formattedYearStart)
        .lte('end_date', formattedYearEnd);
      
      if (salesError) {
        console.error('Error fetching monthly sales data:', salesError);
        return;
      }
      
      // Initialize all months to 0
      for (let i = 0; i < 12; i++) {
        salesData[i] = 0;
      }
      
      // Group sales by month
      if (salesDataAll && salesDataAll.length > 0) {
        salesDataAll.forEach((booking) => {
          const endDate = new Date(booking.end_date);
          const month = endDate.getMonth();
          
          const subtotal = booking.subtotal || 0;
          const discountAmount = booking.discount_amount || 0;
          const discountedSalesAmount = subtotal - discountAmount;
          
          salesData[month] = (salesData[month] || 0) + discountedSalesAmount;
        });
      }

      // Standalone invoices issued during the year, bucketed by issue month.
      const standaloneRows = await fetchStandaloneInvoiceSales(selectedBusiness.id, {
        start: yearStart,
        end: new Date(year, 11, 31, 23, 59, 59, 999),
      });
      const standaloneByMonth = standaloneSalesByMonth(standaloneRows);
      for (const [monthIdx, amount] of Object.entries(standaloneByMonth)) {
        salesData[Number(monthIdx)] = (salesData[Number(monthIdx)] || 0) + amount;
      }

      if (isMounted.current) {
        setMonthlySales(salesData);
      }
    } catch (err) {
      console.error('Error calculating monthly sales:', err);
    } finally {
      if (isMounted.current) {
        setIsLoadingMonthlySales(false);
      }
    }
  }, [selectedBusiness?.id]);
  
  // Update timer display
  useEffect(() => {
    if (!activeTimer) {
      setTimerElapsedTime(0);
      return;
    }

    const updateTime = () => {
      setTimerElapsedTime(getElapsedSeconds());
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [activeTimer, getElapsedSeconds]);

  // Calculate sales when bookings are loaded or when month changes
  useEffect(() => {
    // Wait for business to be loaded and bookings to be ready
    if (selectedBusiness?.id && !businessLoading && !isLoading) {
      // Clear cache for current month to force fresh calculation
      // This ensures sales are recalculated when bookings load
      const calendarYear = currentDate.getFullYear();
      const calendarMonth = currentDate.getMonth();
      const monthStart = new Date(calendarYear, calendarMonth, 1);
      const monthEnd = new Date(calendarYear, calendarMonth + 1, 0);
      const formattedMonthStart = monthStart.toISOString().split('T')[0];
      const formattedMonthEnd = monthEnd.toISOString().split('T')[0];
      const cacheKey = `${selectedBusiness.id}_${formattedMonthStart}_${formattedMonthEnd}`;
      salesCache.delete(cacheKey); // Clear cache to force recalculation
      
      // Small delay to ensure bookings state is fully updated
      // This is especially important on initial load
      const timeoutId = setTimeout(() => {
        calculateTotalSales();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [calculateTotalSales, selectedBusiness?.id, currentDate, bookings.length, businessLoading, isLoading]);

  // Check for preloaded calendar data and use it if available
  const usePreloadedCalendarData = useCallback(() => {
    try {
      const preloadedDataStr = localStorage.getItem('preloadedCalendarData');
      if (!preloadedDataStr) {
        return false;
      }

      const preloadedData = JSON.parse(preloadedDataStr);
      
      // Check if data is still fresh (within 15 minutes)
      const dataAge = Date.now() - preloadedData.timestamp;
      if (dataAge > CACHE_DURATION) {
        localStorage.removeItem('preloadedCalendarData');
        return false;
      }
      
      // Set all the calendar state immediately with preloaded data
      setBookings(preloadedData.bookings || []);
      setLoadedBookingIds(new Set((preloadedData.bookings || []).map((b: any) => b.id)));
      setTotalSales(preloadedData.totalSales || 0);
      setSalesInitiallyLoaded(true);
      setIsCalculatingSales(false);
      
      // Restore booking feedback
      if (preloadedData.bookingFeedback && Array.isArray(preloadedData.bookingFeedback)) {
        const feedbackMap = new Map<string, { rating: number; hasText: boolean }>(
          preloadedData.bookingFeedback as [string, { rating: number; hasText: boolean }][]
        );
        setBookingFeedback(feedbackMap);
      }
      
      // Set initial date for scroll positioning
      if (preloadedData.initialDate) {
        const initialDate = new Date(preloadedData.initialDate);
        setCurrentDate(initialDate);
      }
      
      // Clear loading states immediately
      setIsLoading(false);
      
      // Mark calendar as ready since we have instant data
      setIsCalendarReady(true);
      
      // Clean up the preloaded data since we've used it
      localStorage.removeItem('preloadedCalendarData');
      
      return true;
    } catch (error) {
      console.error('📅 Error using preloaded calendar data:', error);
      localStorage.removeItem('preloadedCalendarData');
      return false;
    }
  }, []);

  // Initialize on component mount
  useEffect(() => {
    isMounted.current = true;
    
    console.log('🚀 CalendarV2 mount useEffect triggered:', {
      businessLoading,
      selectedBusinessId: selectedBusiness?.id,
      viewType,
      defaultViewLoaded
    });
    
    // Wait for business context to be loaded
    if (businessLoading) {
      console.log('⏸️ Business still loading, skipping fetch');
      return;
    }
    
    // Wait for default view to be loaded
    if (!defaultViewLoaded) {
      console.log('⏸️ Default view not loaded yet, skipping fetch');
      return;
    }
    
    if (selectedBusiness?.id && viewType) {
      console.log('✅ Conditions met, attempting to load data');
      // First try to use preloaded calendar data for instant loading
      const usedPreloadedData = usePreloadedCalendarData();
      
      console.log('📦 Used preloaded data?', usedPreloadedData);
      
      if (!usedPreloadedData) {
        // Only fetch bookings if we didn't use preloaded data
        console.log('📊 Calling fetchBookings()');
        
        // For day view, ensure we pass the currentDate explicitly
        // Similar to week view - wait a bit for state to be ready
        if (viewType === 'day') {
          // Use currentDate from state, ensuring it's set to today if not already set
          const dateToUse = currentDate || new Date();
          const normalizedDate = new Date(dateToUse);
          normalizedDate.setHours(0, 0, 0, 0);
          console.log('📅 Day view initial load: Using date', format(normalizedDate, 'yyyy-MM-dd'));
          // Small delay to ensure currentDate state is ready (similar to week view)
          setTimeout(() => {
            fetchBookings(false, normalizedDate);
          }, 50);
        } else {
        fetchBookings();
        }
      }
    }
    
    return () => {
      isMounted.current = false;
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [fetchBookings, selectedBusiness?.id, businessLoading, usePreloadedCalendarData, defaultViewLoaded, viewType, currentDate]);
  
  // Update the calendar title whenever the current date changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      setCalendarTitle(calendarApi.view.title);
    }
  }, [currentDate]);

  // Handle navigation state to load booking data for the already-opened modal
  useEffect(() => {
    const state = navigationState as { selectedBookingId?: string; skipModalOpen?: boolean } | null;
    
    // If skipModalOpen is true, don't open the modal (it's already open via portal)
    if (state?.skipModalOpen) {
      window.history.replaceState({}, document.title);
      return;
    }
    
    if (state?.selectedBookingId && isLoadingBookingFromNav) {
      const loadBookingData = async () => {
        try {
          // Fetch the specific booking details
          const booking = await getBooking(state.selectedBookingId!);
          setSelectedBooking(booking);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error loading booking from navigation state:', err);
          setError('Failed to load booking details. Please try again.');
          // Close the modal if we can't load the booking
          setIsBookingInfoModalOpen(false);
        } finally {
          setIsLoadingBookingFromNav(false);
        }
      };
      
      // Execute immediately
      loadBookingData();
    }
  }, []); // Run only once on mount
  
  // Set up Supabase real-time subscription for bookings
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;
    
    // Subscribe to changes in the bookings table
    const subscription = supabase
      .channel('bookings-changes')
      .on('postgres_changes', 
        { 
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: 'bookings',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        async (payload: any) => {
          // Handle different types of changes
          switch (payload.eventType) {
            case 'INSERT':
              // Fetch the new booking details
              await fetchBookings(true);
              break;
              
            case 'UPDATE':
              // Refresh the bookings to get updated data
              await fetchBookings(true);
              break;
              
            case 'DELETE':
              // Remove the deleted booking from the state
              if (payload.old?.id) {
                setBookings(prevBookings => 
                  prevBookings.filter(booking => booking.id !== payload.old.id)
                );
              }
              break;
          }
        }
      )
      .subscribe();
    
    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Set up Supabase real-time subscription for Google Calendar events
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;

    const gcalSubscription = supabase
      .channel('gcal-events-changes')
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'google_calendar_events',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        async (payload: any) => {
          if (payload.eventType === 'DELETE' && payload.old?.id) {
            setGoogleCalendarEvents(prev => prev.filter(e => e.id !== payload.old.id));
          } else {
            await fetchBookings(true);
          }
        }
      )
      .subscribe();

    return () => {
      gcalSubscription.unsubscribe();
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Listen for immediate booking creation events from CreateBookingModal
  useEffect(() => {
    const handleBookingCreated = (event: any) => {
      const { bookingId, businessId } = event.detail;
      
      // Only refresh if the booking is for the current business
      if (businessId === selectedBusiness?.id) {
        fetchBookings(true); // Force fetch to get the new booking
        loadUnscheduled(); // Keep the unscheduled tab in sync
      }
    };

    // Add event listener for immediate updates
    window.addEventListener('bookingCreated', handleBookingCreated);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('bookingCreated', handleBookingCreated);
    };
  }, [selectedBusiness?.id, fetchBookings, loadUnscheduled]);

  // Listen for task creation events
  useEffect(() => {
    const handleTaskCreated = (event: any) => {
      const { businessId } = event.detail;
      if (businessId === selectedBusiness?.id) {
        fetchBookings(true);
      }
    };

    window.addEventListener('taskCreated', handleTaskCreated);
    return () => {
      window.removeEventListener('taskCreated', handleTaskCreated);
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Listen for FAB booking creation events (both mobile and location bookings)
  useEffect(() => {
    const handleFABBookingCreated = () => {
      // FAB is global and doesn't pass business ID, so refresh unconditionally
      // This handles bookings created via the floating action button
      fetchBookings(true);
      loadUnscheduled(); // Keep the unscheduled tab in sync
    };

    window.addEventListener('mobileBookingCreated', handleFABBookingCreated);

    return () => {
      window.removeEventListener('mobileBookingCreated', handleFABBookingCreated);
    };
  }, [fetchBookings, loadUnscheduled]);

  // Listen for Nexus booking modification events
  useEffect(() => {
    const handleNexusBookingUpdate = (event: any) => {
      const { businessId, modifications } = event.detail;
      
      // Only refresh if the booking modification is for the current business
      if (businessId === selectedBusiness?.id) {
        fetchBookings(true); // Force fetch to get the updated bookings
        loadUnscheduled(); // Keep the unscheduled tab in sync
      }
    };

    // Add event listener for Nexus booking updates
    window.addEventListener('nexusBookingUpdate', handleNexusBookingUpdate);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('nexusBookingUpdate', handleNexusBookingUpdate);
    };
  }, [selectedBusiness?.id, fetchBookings, loadUnscheduled]);
  
  // Handle date changes (when calendar navigates)
  const handleDatesSet = useCallback((arg: any) => {
    console.log('📅 MONTH VIEW - handleDatesSet called:', {
      currentStart: arg.view.currentStart,
      title: arg.view.title,
      start: arg.start,
      end: arg.end
    });
    setCurrentDate(arg.view.currentStart);
    setCalendarTitle(arg.view.title);
    setViewStartDate(arg.start);
    setViewEndDate(arg.end);
    console.log('✅ MONTH VIEW - state updated');
  }, []);
  
  // Optimized navigation handlers with faster transitions
  const handlePrev = useCallback(() => {
    if (viewType === 'week') {
      // Week view: go back 7 days
      const newDate = new Date(weekStartDate);
      newDate.setDate(newDate.getDate() - 7);
      setBookings([]); // Clear bookings first
      setWeekStartDate(newDate);
      // Use setTimeout to ensure state updates before fetching
      setTimeout(() => {
        fetchBookings(true);
      }, 0);
    } else if (viewType === 'day') {
      // Day view: go back 1 day
      // Match week view pattern - update state, then fetch without passing date
      const currentDateForNav = currentDate || new Date();
      const newDate = new Date(currentDateForNav);
      newDate.setDate(newDate.getDate() - 1);
      newDate.setHours(0, 0, 0, 0);
      console.log('📅 Day view handlePrev: Navigating from', format(currentDateForNav, 'yyyy-MM-dd'), 'to', format(newDate, 'yyyy-MM-dd'));
      setBookings([]); // Clear bookings first
      setCurrentDate(newDate);
      // Ensure calendar stays visible during navigation (same fix as week view)
      setCalendarLoaded(true);
      // Pass the date explicitly to avoid stale closure issues (like initial load)
      setTimeout(() => {
        console.log('📅 Day view handlePrev: Calling fetchBookings with date', format(newDate, 'yyyy-MM-dd'));
        fetchBookings(true, newDate);
      }, 0);
    } else if (calendarRef.current) {
      // Month view: use calendar API
      setIsCalendarReady(false);
      setBookings([]); // Clear bookings first
      
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
          const newDate = calendarApi.getDate();
          setCurrentDate(newDate);
          
          // Fetch with the new date from calendar API
          fetchBookings(true, newDate);
        }
      }, 150);
    }
  }, [fetchBookings, viewType, weekStartDate, currentDate]);

  const handleNext = useCallback(() => {
    if (viewType === 'week') {
      // Week view: go forward 7 days
      const newDate = new Date(weekStartDate);
      newDate.setDate(newDate.getDate() + 7);
      setBookings([]); // Clear bookings first
      setWeekStartDate(newDate);
      // Use setTimeout to ensure state updates before fetching
      setTimeout(() => {
        fetchBookings(true);
      }, 0);
    } else if (viewType === 'day') {
      // Day view: go forward 1 day
      // Match week view pattern - update state, then fetch without passing date
      const currentDateForNav = currentDate || new Date();
      const newDate = new Date(currentDateForNav);
      newDate.setDate(newDate.getDate() + 1);
      newDate.setHours(0, 0, 0, 0);
      console.log('📅 Day view handleNext: Navigating from', format(currentDateForNav, 'yyyy-MM-dd'), 'to', format(newDate, 'yyyy-MM-dd'));
      setBookings([]); // Clear bookings first
      setCurrentDate(newDate);
      // Ensure calendar stays visible during navigation
      setCalendarLoaded(true);
      // Pass the date explicitly to avoid stale closure issues (like initial load)
      setTimeout(() => {
        console.log('📅 Day view handleNext: Calling fetchBookings with date', format(newDate, 'yyyy-MM-dd'));
        fetchBookings(true, newDate);
      }, 0);
    } else if (calendarRef.current) {
      // Month view: use calendar API
      setIsCalendarReady(false);
      setBookings([]); // Clear bookings first
      
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
          const newDate = calendarApi.getDate();
          setCurrentDate(newDate);
          
          // Fetch with the new date from calendar API
          fetchBookings(true, newDate);
        }
      }, 150);
    }
  }, [fetchBookings, viewType, weekStartDate, currentDate]);

  // Handle month/year picker navigation
  const handleMonthYearSelect = useCallback(() => {
    if (calendarRef.current) {
      setIsCalendarReady(false);
      setIsMonthPickerOpen(false);
      
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          const newDate = new Date(selectedYear, selectedMonth, 1);
          calendarApi.gotoDate(newDate);
          setCurrentDate(newDate);
          
          // Update selected month/year to match current view
          setSelectedMonth(newDate.getMonth());
          setSelectedYear(newDate.getFullYear());
          
          fetchBookings(true);
        }
      }, 150);
    }
  }, [selectedMonth, selectedYear, fetchBookings]);

  // Initialize selected month/year when picker opens
  const handleOpenMonthPicker = useCallback(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const currentDate = calendarApi.getDate();
      setSelectedMonth(currentDate.getMonth());
      setSelectedYear(currentDate.getFullYear());
      setIsMonthPickerOpen(true);
    }
  }, []);
  
  const handleToday = useCallback(() => {
    if (viewType === 'week') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Find the start of the week (Sunday)
      const dayOfWeek = today.getDay();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - dayOfWeek);
      setBookings([]); // Clear bookings first
      setWeekStartDate(weekStart);
      setTimeout(() => {
        fetchBookings(true);
      }, 0);
    } else if (viewType === 'day') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setBookings([]); // Clear bookings first
      setCurrentDate(today);
      fetchBookings(true, today);
    } else if (calendarRef.current) {
      setIsCalendarReady(false);
      setBookings([]); // Clear bookings first
      
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.today();
          const todayDate = calendarApi.getDate();
          setCurrentDate(todayDate);
          
          // Pass today's date directly to fetchBookings to avoid stale closure
          fetchBookings(true, todayDate);
        }
      }, 150);
    }
  }, [fetchBookings, viewType]);
  
  // Handle event click
  const handleEventClick = useCallback(async (info: any) => {
    // Google Calendar events - open details modal
    if (info.event?.id?.startsWith('gcal-') || info.event?.extendedProps?.is_google_calendar) {
      const gcalId = (info.event.id || '').replace('gcal-', '');
      const match = googleCalendarEvents.find(e => e.id === gcalId);
      if (match) {
        setSelectedGcalEvent(match);
        setIsGcalEventModalOpen(true);
      }
      return;
    }

    // Check if Shift key is pressed for move mode (desktop/keyboard users)
    const isShiftClick = info.jsEvent?.shiftKey || info.event?.jsEvent?.shiftKey;
    
    // If Shift+Click, trigger move booking flow
    if (isShiftClick && !info.event.id.startsWith('fieldquote_') && info.event.extendedProps?.is_buffer_block !== true) {
      try {
        const booking = await getBooking(info.event.id);
        setBookingToMove(booking);
        setIsMoveBookingConfirmModalOpen(true);
      } catch (error) {
        console.error('Error loading booking for move:', error);
        setError('Failed to load booking details');
      }
      return;
    }
    
    // Field quote / measurement blocks — dedicated detail UI (not project modal)
    if (info.event.id.startsWith('fieldquote_')) {
      const fqId =
        (info.event.extendedProps?.field_quote_id as string | undefined) ||
        info.event.id.replace(/^fieldquote_/, '');
      if (!fqId) return;
      setSelectedFieldQuoteId(fqId);
      setIsFieldQuoteDetailModalOpen(true);
      return;
    }

    // Handle buffer blocks - open buffer block modal
    if (info.event.extendedProps?.is_buffer_block === true) {
      console.log('Buffer block clicked:', info.event);
      
      // Set the selected buffer block and open the modal
      setSelectedBufferBlock({
        id: info.event.id,
        buffer_time: info.event.extendedProps.buffer_time,
        booking_id: info.event.extendedProps.booking_id,
        start: new Date(info.event.start),
        end: new Date(info.event.end),
        resource_id: info.event.extendedProps.resource_id
      });
      
      // Fetch booking details to get booking number
      const bookingId = info.event.extendedProps.booking_id as string | undefined;
      if (bookingId && supabase) {
        try {
          const { data: bookingData, error: bookingError } = await supabase
            .from('bookings')
            .select('id, booking_number, title')
            .eq('id', bookingId)
            .single();
          
          if (!bookingError && bookingData) {
            setBufferBlockBooking(bookingData);
          } else {
            console.error('Error fetching booking for buffer block:', bookingError);
            setBufferBlockBooking(null);
          }
        } catch (error) {
          console.error('Error fetching booking details:', error);
          setBufferBlockBooking(null);
        }
      } else {
        setBufferBlockBooking(null);
      }
      
      // Fetch resource details to get resource name
      const resourceId = info.event.extendedProps.resource_id as string | undefined;
      if (resourceId && supabase) {
        try {
          const { data: resourceData, error: resourceError } = await supabase
            .from('resources')
            .select('id, first_name, last_name')
            .eq('id', resourceId)
            .single();
          
          if (!resourceError && resourceData) {
            setBufferBlockResource(resourceData);
          } else {
            console.error('Error fetching resource for buffer block:', resourceError);
            setBufferBlockResource(null);
          }
        } catch (error) {
          console.error('Error fetching resource details:', error);
          setBufferBlockResource(null);
        }
      } else {
        setBufferBlockResource(null);
      }
      
      setIsBufferModalOpen(true);
      return;
    }

    // Handle task events
    if (info.event.extendedProps?.is_task === true) {
      setSelectedTaskId(info.event.id);
      setIsTaskDetailsModalOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      setSelectedBooking(null); // Clear any previous booking
      // Fetch full booking details first
      const booking = await getBooking(info.event.id);
      setSelectedBooking(booking);
      setIsBookingInfoModalOpen(true); // Open modal only after booking is loaded
    } catch (err) {
      setError('Failed to load booking details. Please try again.');
      setIsBookingInfoModalOpen(false);
    } finally {
      setIsLoading(false);
    }
    }, [selectedBusiness?.id, googleCalendarEvents]);

  // Handle long press on event
  const handleLongPress = useCallback(async (bookingId: string) => {
    try {
      // Find the booking in the calendar events (which has start/end as ISO strings)
      const calendarBooking = [...bookings, ...timeOffRequests, ...fieldQuoteEvents].find(b => b.id === bookingId);
      
      if (!calendarBooking) {
        console.error('❌ Booking not found in calendar events');
        return;
      }
      
      // Don't allow moving buffer blocks (but allow field quotes and measurements)
      if (calendarBooking.extendedProps?.is_buffer_block) {
        console.log('⚠️ Cannot move buffer blocks');
        return;
      }
      
      // Construct start and end ISO strings if they don't exist
      let startISO = calendarBooking.start;
      let endISO = calendarBooking.end;
      
      if (!startISO && calendarBooking.extendedProps?.start_date && calendarBooking.extendedProps?.start_time) {
        startISO = `${calendarBooking.extendedProps.start_date}T${calendarBooking.extendedProps.start_time}`;
      }
      
      if (!endISO && calendarBooking.extendedProps?.end_date && calendarBooking.extendedProps?.end_time) {
        endISO = `${calendarBooking.extendedProps.end_date}T${calendarBooking.extendedProps.end_time}`;
      }
      
      console.log('📦 Found booking in calendar:', {
        id: calendarBooking.id,
        title: calendarBooking.title,
        start: startISO,
        end: endISO,
        isFieldQuote: calendarBooking.extendedProps?.is_field_quote,
        isMeasurement: calendarBooking.extendedProps?.is_measurement,
        hasStartField: !!calendarBooking.start,
        hasEndField: !!calendarBooking.end
      });
      
      // Create a booking object with proper start/end fields
      const bookingWithDates = {
        ...calendarBooking,
        start: startISO,
        end: endISO
      };
      
      // Directly enter drag mode (skip the modal)
      setDraggedBooking(bookingWithDates);
      setIsDraggingMode(true);
    } catch (error) {
      console.error('Error loading booking:', error);
      setError('Failed to load booking details');
    }
  }, [bookings, timeOffRequests, fieldQuoteEvents]);

  // Handle confirming the move booking modal (enters drag mode)
  const handleConfirmMoveBooking = useCallback(async (updatedData: {
    dropOffDate?: string;
    dropOffTime?: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    resources: any[];
  }) => {
    if (!bookingToMove) return;

    try {
      // Determine if this is a field quote or regular booking
      const isFieldQuote = bookingToMove.extendedProps?.is_field_quote || bookingToMove.extendedProps?.is_measurement;
      const tableName = isFieldQuote ? 'project_field_quotes' : 'bookings';
      
      // Strip the "fieldquote_" prefix from the ID if present
      const actualId = bookingToMove.id.replace(/^fieldquote_/, '');
      
      console.log('📦 Updating', tableName, 'before entering drag mode', {
        originalId: bookingToMove.id,
        actualId,
        isFieldQuote
      });
      
      // Update the booking/field quote in the database with the new data
      const updateData: any = {
        start_date: updatedData.startDate,
        start_time: updatedData.startTime,
        end_date: updatedData.endDate,
        end_time: updatedData.endTime,
      };
      
      // Only add drop_off fields for regular bookings
      if (!isFieldQuote) {
        updateData.drop_off_date = updatedData.dropOffDate || null;
        updateData.drop_off_time = updatedData.dropOffTime || null;
      }
      
      const { error: updateError } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', actualId);

      if (updateError) throw updateError;

      // Only update resource assignments for regular bookings (field quotes use resource_id directly)
      if (!isFieldQuote) {
        // First, delete existing assignments
        const { error: deleteError } = await supabase
          .from('booking_resources')
          .delete()
          .eq('booking_id', actualId);

        if (deleteError) throw deleteError;

        // Then, insert new assignments
        if (updatedData.resources.length > 0) {
          const { error: insertError } = await supabase
            .from('booking_resources')
            .insert(
              updatedData.resources.map(resource => ({
                booking_id: actualId,
                resource_id: resource.id
              }))
            );

          if (insertError) throw insertError;
        }

        // Sync mobile_bookings + buffer blocks after resource change
        await syncMobileBookingsForBooking(actualId);
      }

      console.log('📦 Booking/Field Quote updated successfully, entering drag mode');

      // Create updated booking object for drag mode
      const updatedBooking = {
        ...bookingToMove,
        start: `${updatedData.startDate}T${updatedData.startTime}`,
        end: `${updatedData.endDate}T${updatedData.endTime}`,
        extendedProps: {
          ...bookingToMove.extendedProps,
          drop_off_date: updatedData.dropOffDate,
          drop_off_time: updatedData.dropOffTime,
          start_date: updatedData.startDate,
          start_time: updatedData.startTime,
          end_date: updatedData.endDate,
          end_time: updatedData.endTime,
          resources: updatedData.resources
        }
      };
      
      setDraggedBooking(updatedBooking);
      setIsDraggingMode(true);
      setIsMoveBookingConfirmModalOpen(false);

      // Reload bookings to reflect the changes
      await fetchBookings(true);
    } catch (error) {
      console.error('Error updating booking:', error);
      setError('Failed to update booking');
    }
  }, [bookingToMove, supabase, fetchBookings]);

  // Handle cancel drag mode
  const handleCancelDragMode = useCallback(async () => {
    console.log('🚫 Cancelling drag mode');
    
    // Clear all drag-related state
    setIsDraggingMode(false);
    setDraggedBooking(null);
    setBookingToMove(null);
    setIsConfirmMoveModalOpen(false);
    setNewDropLocation(null);
    
    // Small delay to ensure state updates, then refresh
    await new Promise(resolve => setTimeout(resolve, 100));
    fetchBookings(true);
  }, [fetchBookings]);

  // Prevent page scrolling when in drag mode on mobile
  useEffect(() => {
    if (isDraggingMode && isMobile) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Also lock the HTML element
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      
      // Prevent touch scrolling but allow taps on interactive elements
      const preventScroll = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        // Allow interactions with buttons, inputs, and modal content
        if (target.closest('button') || target.closest('input') || target.closest('[role="dialog"]')) {
          return; // Allow the event for interactive elements
        }
        // Prevent scrolling on everything else
        if (e.cancelable) {
          e.preventDefault();
        }
      };
      
      // Add to document
      document.addEventListener('touchmove', preventScroll, { passive: false });
      
      return () => {
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore HTML element
        document.documentElement.style.overflow = '';
        document.documentElement.style.height = '';
        
        // Remove event listeners
        document.removeEventListener('touchmove', preventScroll);
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isDraggingMode, isMobile]);

  // Handle drop booking on a new time slot
  const handleDropBooking = useCallback((date: Date, resourceId?: string) => {
    console.log('🎯 handleDropBooking called:', { date, resourceId, draggedBooking });
    
    if (!draggedBooking) {
      console.error('❌ No dragged booking found!');
      return;
    }
    
    // Store the new drop location
    setNewDropLocation({ date, resourceId });
    setIsConfirmMoveModalOpen(true);
    
    console.log('✅ Modal should open now');
  }, [draggedBooking]);

  const handleDateClick = useCallback((info: any) => {
    // Drag mode: drop the booking on the clicked date
    if (isDraggingMode && draggedBooking && isMobile) {
      handleDropBooking(info.date);
      return;
    }

    // Normal mode in month view: open booking type selection (single day)
    if (!isDraggingMode && viewType === 'month') {
      const clickedDate = info.date;
      // Default to 9:00 AM on the clicked date
      clickedDate.setHours(9, 0, 0, 0);
      let anchorRect: AnchorRect | null = null;
      const dayEl = info.dayEl as HTMLElement | undefined;
      if (dayEl?.getBoundingClientRect) {
        const rect = dayEl.getBoundingClientRect();
        anchorRect = {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        };
      }
      setSelectedTimeSlotData({
        date: clickedDate,
        resourceId: '',
        isAsset: false,
        endDate: null,
        anchorRect,
        anchorSlotKey: monthCreateSlotKey(clickedDate),
        anchorEndSlotKey: null,
      });
      void hapticLight();
      setIsBookingTypeSelectionModalOpen(true);
    }
  }, [isDraggingMode, draggedBooking, isMobile, handleDropBooking, viewType]);

  // Month view: press-and-drag across days to set start + end date
  const handleDateSelect = useCallback((info: any) => {
    if (isDraggingMode || viewType !== 'month') {
      info.view?.calendar?.unselect?.();
      return;
    }

    const start = new Date(info.start);
    start.setHours(9, 0, 0, 0);

    // FullCalendar end is exclusive
    const lastDay = subDays(new Date(info.end), 1);
    lastDay.setHours(17, 0, 0, 0);

    const isRange = !isSameDay(start, lastDay);

    // Single-day "select" (tiny drag) — same as a click; dateClick may also fire
    if (!isRange) {
      info.view?.calendar?.unselect?.();
      return;
    }

    let anchorRect: AnchorRect | null = null;
    const startStr = format(start, 'yyyy-MM-dd');
    const startEl = document.querySelector(
      `.fc-daygrid-day[data-date="${startStr}"]`
    ) as HTMLElement | null;

    // Anchor popover to the start day only — do not union first/last day rects
    // (that creates a huge bounding box across week rows).
    if (startEl) {
      const rect = startEl.getBoundingClientRect();
      anchorRect = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };
    }

    setSelectedTimeSlotData({
      date: start,
      resourceId: '',
      isAsset: false,
      endDate: lastDay,
      anchorRect,
      anchorSlotKey: monthCreateSlotKey(start),
      anchorEndSlotKey: monthCreateSlotKey(lastDay),
    });
    void hapticMedium();
    setIsBookingTypeSelectionModalOpen(true);
    // Keep FullCalendar's per-cell range highlight visible while the create popover is open
  }, [isDraggingMode, viewType]);

  // Calculate new dates based on drop location
  const calculateNewDates = useCallback((
    oldStart: Date, 
    oldEnd: Date, 
    newStart: Date
  ): { newStartDate: Date; newEndDate: Date } => {
    // Calculate the duration of the booking
    const durationMs = differenceInMilliseconds(oldEnd, oldStart);
    
    // Calculate new end date by adding duration to new start
    const newEndDate = new Date(newStart.getTime() + durationMs);
    
    return {
      newStartDate: newStart,
      newEndDate: newEndDate
    };
  }, []);

  // Handle confirming the booking move (save to database)
  const handleConfirmBookingMove = useCallback(async (updatedDates: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => {
    if (!draggedBooking || !newDropLocation || !supabase) {
      console.error('❌ Missing required data:', { draggedBooking, newDropLocation, supabase });
      return;
    }
    
    setIsSavingMove(true);
    
    try {
      console.log('📅 Moving booking with updated dates:', {
        bookingId: draggedBooking.id,
        updatedDates,
        isFieldQuote: draggedBooking.extendedProps?.is_field_quote,
        isMeasurement: draggedBooking.extendedProps?.is_measurement
      });
      
      // Determine which table to update
      const isFieldQuote = draggedBooking.extendedProps?.is_field_quote || draggedBooking.extendedProps?.is_measurement;
      const tableName = isFieldQuote ? 'project_field_quotes' : 'bookings';
      
      // Strip the "fieldquote_" prefix from the ID if present
      const actualId = draggedBooking.id.replace(/^fieldquote_/, '');
      
      console.log('💾 Updating', tableName, 'with ID:', actualId);
      
      // Update the booking/field quote in the database with the manually edited dates
      const updateData = {
        start_date: updatedDates.startDate,
        start_time: updatedDates.startTime,
        end_date: updatedDates.endDate,
        end_time: updatedDates.endTime,
        ...(tableName === 'bookings' ? {
          drop_off_date: updatedDates.startDate,
          drop_off_time: updatedDates.startTime,
        } : {}),
        updated_at: new Date().toISOString()
      };
      
      console.log('💾 Update data:', updateData);
      
      const { error: updateError } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', actualId);
      
      if (updateError) {
        console.error('❌ Database update error:', updateError);
        throw updateError;
      }
      
      console.log('✅ Booking/Field Quote updated successfully');
      
      if (selectedBusiness?.id) {
        const gcalType = isFieldQuote ? 'field_quote' : 'booking';
        triggerGcalSync(actualId, 'update', selectedBusiness.id, gcalType);
      }
      
      // Close modals and reset state
      setIsConfirmMoveModalOpen(false);
      setIsDraggingMode(false);
      setDraggedBooking(null);
      setBookingToMove(null);
      setNewDropLocation(null);
      
      // Small delay to ensure state updates, then refresh bookings
      await new Promise(resolve => setTimeout(resolve, 100));
      await fetchBookings(true);
    } catch (error) {
      console.error('❌ Error moving booking:', error);
      alert('Failed to move booking. Please try again.');
    } finally {
      setIsSavingMove(false);
    }
  }, [draggedBooking, newDropLocation, supabase, fetchBookings]);



  // Handler for rescheduling mobile bookings
  const handleRescheduleMobileBooking = useCallback(async () => {
    if (selectedBooking && selectedBooking.extendedProps?.mobile_booking) {
      try {
        // Fetch the full booking data before opening reschedule modal
        const fullBooking = await getBooking(selectedBooking.id);
        if (fullBooking) {
          setBookingToReschedule(fullBooking);
          setIsRescheduleMobileBookingModalOpen(true);
          setIsBookingInfoModalOpen(false);
        }
      } catch (error) {
        console.error('Error fetching booking for reschedule:', error);
      }
    }
  }, [selectedBooking]);

  // Open Google Maps
  const openGoogleMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
    
    if (isMobile) {
      const googleMapsApp = `comgooglemaps://?q=${encodedAddress}`;
      const link = document.createElement('a');
      link.href = googleMapsApp;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        window.open(mapsUrl, '_blank');
        document.body.removeChild(link);
      }, 500);
    } else {
      window.open(mapsUrl, '_blank');
    }
  };

  // Handler for map pin clicks
  const handleMapPinClick = useCallback((event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const address = event.extendedProps?.booking_address;
    if (!address) {
      alert('No address available for this booking');
      return;
    }
    
    setSelectedBookingAddress(address);
    setSelectedBookingForSMS(event);
    setIsGoogleMapsModalOpen(true);
  }, []);
  
  // Handler for deleting buffer blocks
  const handleDeleteBufferBlock = useCallback(async () => {
    if (!supabase || !selectedBufferBlock || !selectedBufferBlock.id) {
      console.error('Cannot delete buffer block: Missing data or database connection');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Extract the actual ID from the buffer-{id} format
      const bufferId = selectedBufferBlock.id.replace('buffer-', '');
      
      // Delete the buffer block from the mobile_bookings table
      const { error } = await supabase
        .from('mobile_bookings')
        .delete()
        .eq('id', bufferId);
      
      if (error) {
        console.error('Error deleting buffer block:', error);
        throw error;
      }
      
      // Close the modal and refresh bookings
      setIsBufferModalOpen(false);
      setBufferBlockBooking(null);
      setBufferBlockResource(null);
      setSelectedBufferBlock(null);
      
      // Refresh the bookings to show changes
      await fetchBookings(true);
      
      // Show success message
      alert('Buffer block successfully deleted');
    } catch (error) {
      console.error('Failed to delete buffer block:', error);
      alert('Failed to delete buffer block. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBufferBlock, supabase, fetchBookings]);
  
  // Handler for map and notify
  const handleMapAndNotify = async () => {
    if (!selectedBookingForSMS || !selectedBusiness?.id) {
      alert('Missing booking or business information');
      return;
    }
    
    setIsSendingSMS(true);
    
    try {
      const fullCustomerName = selectedBookingForSMS.extendedProps?.customer || 'there';
      const customerFirstName = fullCustomerName.split(' ')[0];
      const customerPhone = selectedBookingForSMS.extendedProps?.customer_phone;
      
      if (!customerPhone) {
        alert('No phone number available for this customer');
        setIsSendingSMS(false);
        return;
      }
      
      const message = `Hey ${customerFirstName}, I am on my way`;
      
      const result = await sendSMS({
        businessId: selectedBusiness.id,
        to: customerPhone,
        message: message,
        aiGenerated: false
      });
      
      if (result.success) {
        openGoogleMaps(selectedBookingAddress);
        setIsGoogleMapsModalOpen(false);
        alert(`SMS sent to ${customerFirstName}!`);
      } else {
        alert(`Failed to send SMS: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS. Please try again.');
    } finally {
      setIsSendingSMS(false);
    }
  };

  // Handler for time slot clicks / drag-range selects — opens booking type selection
  const handleTimeSlotClick = useCallback((
    date: Date,
    resourceId: string,
    anchorRect?: { top: number; left: number; width: number; height: number },
    endDate?: Date
  ) => {
    const resource = resources.find(r => r.id === resourceId);
    const asset = !resource ? calendarAssets.find(a => a.id === resourceId) : null;
    if (!resource && !asset) return;

    setSelectedTimeSlotData({
      date,
      resourceId,
      isAsset: !!asset,
      endDate: endDate ?? null,
      anchorRect: anchorRect ?? null,
      anchorSlotKey: weekSlotKey(date, resourceId),
      anchorEndSlotKey:
        endDate && endDate.getTime() > date.getTime()
          ? weekSlotKey(new Date(endDate.getTime() - 30 * 60 * 1000), resourceId)
          : null,
    });
    if (endDate && endDate.getTime() > date.getTime()) {
      void hapticMedium();
    } else {
      void hapticLight();
    }
    setIsBookingTypeSelectionModalOpen(true);
  }, [resources, calendarAssets]);

  // Handle mobile booking selection
  const handleSelectMobileBooking = useCallback(() => {
    console.log('🚀 handleSelectMobileBooking called');
    console.log('selectedTimeSlotData:', selectedTimeSlotData);
    
    if (!selectedTimeSlotData) {
      console.error('❌ No selectedTimeSlotData');
      return;
    }
    
    const { date, resourceId, isAsset, endDate: rangeEnd } = selectedTimeSlotData;
    const resource = !isAsset && resourceId ? resources.find(r => r.id === resourceId) : null;
    const asset = isAsset && resourceId ? calendarAssets.find(a => a.id === resourceId) : null;
    const noResourceSelected = !resource && !asset;

    const startDate = format(date, 'yyyy-MM-dd');
    const startTime = format(date, 'HH:mm');

    let endDateStr: string;
    let endTime: string;
    let availableHours = 1;

    if (rangeEnd && rangeEnd.getTime() > date.getTime()) {
      endDateStr = format(rangeEnd, 'yyyy-MM-dd');
      endTime = format(rangeEnd, 'HH:mm');
      availableHours = Math.max(
        0.5,
        (rangeEnd.getTime() - date.getTime()) / 3600000
      );
    } else if (noResourceSelected) {
      // Month view click — no resource pre-selected, open regular mobile booking with just date
      setQuickBookingData({
        scheduleData: {
          startDate,
          startTime,
          endDate: format(new Date(date.getTime() + 3600000), 'yyyy-MM-dd'),
          endTime: format(new Date(date.getTime() + 3600000), 'HH:mm'),
          dropOffDate: startDate,
          dropOffTime: startTime,
          customer_notes: '',
          availableHours: 1,
        },
      });
      setIsCreateMobileBookingModalOpen(true);
      return;
    } else {
      const selectedDateStr = format(date, 'yyyy-MM-dd');
      const dayOfWeek = format(date, 'EEEE').toLowerCase() as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
      const resourceAvailability = resourceAvailabilities.get(resourceId);
      const daySchedule = resourceAvailability?.[dayOfWeek];

      let dayEndTime = new Date(date);
      dayEndTime.setHours(20, 0, 0, 0);

      if (daySchedule?.shifts && daySchedule.shifts.length > 0) {
        const latestShift = daySchedule.shifts.reduce((latest, shift) => {
          const [endHour, endMin] = shift.end.split(':').map(Number);
          const [latestHour, latestMin] = latest.end.split(':').map(Number);
          return (endHour * 60 + endMin) > (latestHour * 60 + latestMin) ? shift : latest;
        });
        const [endHour, endMin] = latestShift.end.split(':').map(Number);
        dayEndTime.setHours(endHour, endMin, 0, 0);
      }

      const columnBookings = bookings.filter(booking => {
        const bookingStart = parseISO(booking.start);
        const bookingDate = format(bookingStart, 'yyyy-MM-dd');
        if (bookingDate !== selectedDateStr) return false;
        if (isAsset) {
          return booking.extendedProps?.booking_assets?.some((a: any) => (a.id || a.asset_id) === resourceId);
        } else {
          return booking.resourceId === resourceId || booking.extendedProps?.resources?.some((r: any) => r.id === resourceId);
        }
      });

      const [selectedHour, selectedMinute] = format(date, 'HH:mm').split(':').map(Number);
      const selectedMinutes = selectedHour * 60 + selectedMinute;

      const nextBookings = columnBookings
        .filter(booking => {
          const bookingStart = parseISO(booking.start);
          return bookingStart.getHours() * 60 + bookingStart.getMinutes() > selectedMinutes;
        })
        .sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

      const nextBooking = nextBookings[0];
      let availableEndMinutes: number;
      if (nextBooking) {
        const nextBookingStart = parseISO(nextBooking.start);
        availableEndMinutes = nextBookingStart.getHours() * 60 + nextBookingStart.getMinutes();
      } else {
        availableEndMinutes = dayEndTime.getHours() * 60 + dayEndTime.getMinutes();
      }

      const availableMinutesTotal = Math.max(0, availableEndMinutes - selectedMinutes);
      availableHours = availableMinutesTotal / 60;

      const availableEndTime = new Date(date);
      availableEndTime.setHours(Math.floor(availableEndMinutes / 60), availableEndMinutes % 60, 0, 0);
      endDateStr = format(availableEndTime, 'yyyy-MM-dd');
      endTime = format(availableEndTime, 'HH:mm');
    }

    const quickData: any = {
      scheduleData: {
        startDate,
        startTime,
        endDate: endDateStr,
        endTime,
        dropOffDate: startDate,
        dropOffTime: startTime,
        customer_notes: '',
        availableHours
      },
      isQuickBooking: true
    };

    if (isAsset && asset) {
      const assetRaw = (asset as any)._assetRaw;
      quickData.asset = {
        id: assetRaw?.id || asset.id,
        name: `${asset.first_name} ${asset.last_name}`.trim(),
        asset_id: assetRaw?.id || asset.id
      };
    } else if (resource) {
      quickData.resource = {
        id: resource.id,
        name: `${resource.first_name} ${resource.last_name}`,
        resource_id: resource.id
      };
    }

    console.log('Setting quick booking data:', quickData);
    setQuickBookingData(quickData);
    setIsCreateMobileBookingModalOpen(true);
  }, [selectedTimeSlotData, resources, calendarAssets, bookings, resourceAvailabilities]);

  // Handle location booking selection
  const handleSelectLocationBooking = useCallback(() => {
    if (!selectedTimeSlotData) return;
    
    const { date, resourceId, isAsset, endDate: rangeEnd } = selectedTimeSlotData;
    const resource = !isAsset && resourceId ? resources.find(r => r.id === resourceId) : null;
    const asset = isAsset && resourceId ? calendarAssets.find(a => a.id === resourceId) : null;

    const startDate = format(date, 'yyyy-MM-dd');
    const startTime = format(date, 'HH:mm');
    
    const endDate =
      rangeEnd && rangeEnd.getTime() > date.getTime()
        ? rangeEnd
        : (() => {
            const d = new Date(date);
            d.setHours(d.getHours() + 1);
            return d;
          })();
    const endDateStr = format(endDate, 'yyyy-MM-dd');
    const endTime = format(endDate, 'HH:mm');

    const quickData: any = {
      scheduleData: {
        startDate,
        startTime,
        endDate: endDateStr,
        endTime,
        dropOffDate: startDate,
        dropOffTime: startTime,
        customer_notes: ''
      },
      isQuickBooking: true
    };

    const source = resource || asset;
    if (source) {
      quickData.selectedResource = {
        id: source.id,
        name: `${source.first_name} ${source.last_name}`,
        first_name: source.first_name,
        last_name: source.last_name,
        phone: source.phone || '',
        email: source.email || '',
        avatar_url: source.avatar_url || null
      };
    }

    setQuickBookingData(quickData);
    setIsCreateBookingModalOpen(true);
  }, [selectedTimeSlotData, resources, calendarAssets]);

  // Handle convert project to booking selection
  const handleSelectConvertProject = useCallback(() => {
    if (!selectedTimeSlotData) return;
    
    const { date, resourceId, isAsset, endDate: rangeEnd } = selectedTimeSlotData;
    const resource = !isAsset && resourceId ? resources.find(r => r.id === resourceId) : null;
    const asset = isAsset && resourceId ? calendarAssets.find(a => a.id === resourceId) : null;

    const startDate = format(date, 'yyyy-MM-dd');
    const startTime = format(date, 'HH:mm');
    
    const endDate =
      rangeEnd && rangeEnd.getTime() > date.getTime()
        ? rangeEnd
        : (() => {
            const d = new Date(date);
            d.setHours(d.getHours() + 1);
            return d;
          })();
    const endDateStr = format(endDate, 'yyyy-MM-dd');
    const endTime = format(endDate, 'HH:mm');

    const quickData: any = {
      scheduleData: {
        startDate,
        startTime,
        endDate: endDateStr,
        endTime,
        dropOffDate: startDate,
        dropOffTime: startTime,
        customer_notes: ''
      },
      isQuickBooking: true,
      showProjectSelection: true
    };

    const source = resource || asset;
    if (source) {
      quickData.selectedResource = {
        id: source.id,
        name: `${source.first_name} ${source.last_name}`,
        resource_id: source.id
      };
    }

    setQuickBookingData(quickData);
    setIsCreateBookingModalOpen(true);
  }, [selectedTimeSlotData, resources, calendarAssets]);

  // Memoize the event content renderer to show loading state
  const renderEventContent = useCallback((eventInfo: any) => {
    // Google Calendar events
    if (eventInfo.event.extendedProps?.is_google_calendar) {
      const gcalStart = eventInfo.event.start;
      const gcalEnd = eventInfo.event.end;
      const startStr = gcalStart ? format(gcalStart, 'h:mm a') : '';
      const endStr = gcalEnd ? format(gcalEnd, 'h:mm a') : '';
      const timeStr = eventInfo.event.allDay ? 'All day' : startStr && endStr ? `${startStr} - ${endStr}` : startStr;
      const gcalAttendees: any[] = eventInfo.event.extendedProps?.attendees || [];
      const firstGuest = gcalAttendees[0];
      const extraCount = gcalAttendees.length - 1;
      return (
        <div
          className="relative h-full w-full p-0.5 sm:p-1 overflow-hidden border-l-2 border-blue-400 cursor-pointer"
          style={{ backgroundColor: 'rgba(66, 133, 244, 0.12)' }}
          onClick={(e) => {
            e.stopPropagation();
            const gcalId = (eventInfo.event.id || '').replace('gcal-', '');
            const match = googleCalendarEvents.find((ge: any) => ge.id === gcalId);
            if (match) {
              setSelectedGcalEvent(match);
              setIsGcalEventModalOpen(true);
            }
          }}
        >
          <div className="flex items-center gap-0.5">
            <svg className="h-2.5 w-2.5 flex-shrink-0 text-blue-500 hidden sm:block" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
            </svg>
            <span className="text-[9px] sm:text-xs font-medium text-blue-700 truncate">{eventInfo.event.title}</span>
          </div>
          {timeStr && (
            <div className="text-[8px] sm:text-[10px] text-blue-500 truncate mt-0.5 pl-0.5">{timeStr}</div>
          )}
          {firstGuest && (
            <div className="flex items-center gap-0.5 mt-0.5 pl-0.5">
              <svg className="h-2 w-2 sm:h-2.5 sm:w-2.5 flex-shrink-0 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <span className="text-[7px] sm:text-[9px] text-blue-500 truncate">
                {firstGuest.displayName || firstGuest.email?.split('@')[0]}
                {extraCount > 0 && <span className="text-blue-400"> +{extraCount}</span>}
              </span>
            </div>
          )}
          {eventInfo.event.extendedProps?.is_gcal_digital && (
            <div className="flex items-center gap-0.5 mt-0.5 pl-0.5">
              <Video className="h-2 w-2 sm:h-2.5 sm:w-2.5 flex-shrink-0 text-blue-500" />
              <span className="text-[7px] sm:text-[9px] text-blue-600 font-medium truncate">Digital</span>
            </div>
          )}
          {eventInfo.event.extendedProps?.is_gcal_in_person && (
            <div className="flex items-center gap-0.5 mt-0.5 pl-0.5">
              <MapPin className="h-2 w-2 sm:h-2.5 sm:w-2.5 flex-shrink-0 text-blue-500" />
              <span className="text-[7px] sm:text-[9px] text-blue-600 truncate">
                {eventInfo.event.extendedProps.gcal_location
                  ? truncateGcalLocation(eventInfo.event.extendedProps.gcal_location, 24)
                  : 'In person'}
              </span>
            </div>
          )}
        </div>
      );
    }

    // Time off events are always "loaded" since they don't need additional data fetching
    const isTimeOffEvent = eventInfo.event.id.startsWith('timeoff_');
    const isFieldQuoteEvent =
      eventInfo.event.id.startsWith('fieldquote_') ||
      eventInfo.event.extendedProps.is_field_quote;
    const isLoaded = isTimeOffEvent || isFieldQuoteEvent || loadedBookingIds.has(eventInfo.event.id);
    
    // Handle time off events with simpler rendering
    if (isTimeOffEvent) {
      return (
        <div className="relative h-full w-full p-1 overflow-hidden">
          <div className="text-[9px] sm:text-xs font-medium text-white relative z-10">
            {eventInfo.event.title}
          </div>
          <div className="text-[8px] sm:text-xs opacity-75 text-white relative z-10 mt-0.5">
            {eventInfo.event.extendedProps.time_off_type}
          </div>
        </div>
      );
    }

    // Prefer drop_off_time, fallback to start_time
    const dropOffTime = eventInfo.event.extendedProps.drop_off_time;
    const startTime = eventInfo.event.extendedProps.start_time;
    const endTimeForFieldQuote = eventInfo.event.extendedProps.end_time;
    const timeToShow = dropOffTime || startTime;
    const bookingNumber = eventInfo.event.extendedProps.booking_number;
    // Get secondary color from both possible locations
    const secondaryColor = eventInfo.event.extendedProps.secondary_color || 
                          eventInfo.event.extendedProps.secondaryColor || 
                          eventInfo.event.secondaryColor;
    
    // Get booking status to determine if we should show secondary color
    const bookingStatus = eventInfo.event.extendedProps.status;
    const shouldShowSecondaryColor = secondaryColor && 
                                   bookingStatus !== 'in_progress' && 
                                   bookingStatus !== 'completed' && 
                                   bookingStatus !== 'cancelled';
    
    // Get feedback data for this booking
    const feedbackData = bookingFeedback.get(eventInfo.event.id);
    const showFeedbackRating = bookingStatus === 'completed' && feedbackData;
    
    // Calculate workflow progress for in-progress bookings
    let totalSteps = 0;
    let completedSteps = 0;
    let showWorkflowProgress = false;
    
    if (bookingStatus === 'in_progress' && eventInfo.event.extendedProps.services) {
      // Check if any service has workflow steps
      const services = eventInfo.event.extendedProps.services;
      for (const service of services) {
        if (service.workflow_steps && Array.isArray(service.workflow_steps)) {
          service.workflow_steps.forEach((step: any) => {
            totalSteps++;
            if (step.status === 'completed') {
              completedSteps++;
            }
          });
        }
      }
      showWorkflowProgress = totalSteps > 0;
    }

    // Check if this booking has an active timer
    const hasActiveTimer = activeTimer?.bookingId === eventInfo.event.id;
    
    // Format timer display
    const formatTimerDisplay = (seconds: number) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      }
    };
    
    // Resource avatars (assigned technicians)
    const resources = eventInfo.event.extendedProps.resources || [];
    const visibleResources = Array.isArray(resources) ? resources.slice(0, 3) : [];
    const extraResourceCount = Math.max(
      0,
      Array.isArray(resources) ? resources.length - visibleResources.length : 0
    );
    
    let formattedTime = '';
    if (timeToShow) {
      // Format as HH:MM AM/PM for generic single-time display (used by field quotes)
      const [h, m] = timeToShow.split(':');
      const date = new Date();
      date.setHours(Number(h), Number(m), 0, 0);
      formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // For regular bookings, prefer displaying date + "Start - End" time range based on event start/end
    let bookingTimeRange: any = '';
    const eventStart: Date | null = eventInfo.event.start || null;
    const eventEnd: Date | null = eventInfo.event.end || null;

    if (eventStart) {
      const getOrdinalDay = (d: Date) => {
        const day = d.getDate();
        const suffix =
          day % 10 === 1 && day !== 11
            ? 'st'
            : day % 10 === 2 && day !== 12
            ? 'nd'
            : day % 10 === 3 && day !== 13
            ? 'rd'
            : 'th';
        return (
          <span>
            {day}
            <span
              style={{
                fontSize: '0.6em',
                verticalAlign: 'super',
                marginLeft: '1px'
              }}
            >
              {suffix}
            </span>
          </span>
        );
      };

      const formatTime = (d: Date) => {
        const hours = d.getHours();
        const minutes = d.getMinutes();
        // Use full am/pm suffix
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHour = hours % 12 === 0 ? 12 : hours % 12;
        const minutePart = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
        return `${displayHour}${minutePart}${ampm}`;
      };

      const sameDay = eventEnd
        ? eventStart.toDateString() === eventEnd.toDateString()
        : true;

      const startTime = formatTime(eventStart);

      if (eventEnd) {
        const endDayNode = getOrdinalDay(eventEnd);
        const endTime = formatTime(eventEnd);

        // Same-day: "9a - 5p"
        // Multi-day: "9a - 20th 5p" (only end gets date, with small suffix)
        bookingTimeRange = sameDay ? (
          `${startTime} - ${endTime}`
        ) : (
          <>
            {startTime} - {endDayNode} {endTime}
          </>
        );
      } else {
        bookingTimeRange = startTime;
      }
    }
    
    // Use full booking title instead of trying to extract vehicle model
    const displayTitle = eventInfo.event.title || 'Untitled';
    
    // Create a wrapper component for long press and shift+click
    const EventWrapper = ({ children }: { children: React.ReactNode }) => {
      // Track if shift was pressed during the interaction
      const shiftPressedRef = useRef(false);
      
      // Check if this is not the dragged booking but we're in drag mode
      const isOtherBookingInDragMode = isDraggingMode && draggedBooking && draggedBooking.id !== eventInfo.event.id;
      // Check if this IS the dragged booking (already in move mode)
      const isThisBookingBeingDragged = isDraggingMode && draggedBooking && draggedBooking.id === eventInfo.event.id;
      
      const longPressHandlers = useLongPress({
        onLongPress: () => {
          // Don't allow long press on other bookings when in drag mode
          if (isOtherBookingInDragMode) return;
          // Don't allow long press on the booking that's already being dragged
          if (isThisBookingBeingDragged) return;
          // Trigger move booking flow on long press (mobile)
          handleLongPress(eventInfo.event.id);
        },
        onClick: () => {
          // Don't allow clicks on other bookings when in drag mode
          if (isOtherBookingInDragMode) return;
          // Don't allow clicks on the booking that's already being dragged
          if (isThisBookingBeingDragged) return;
          // This fires on quick taps/clicks (under 300ms, not long press)
          // Regular click - open booking info
          handleEventClick({ event: eventInfo.event });
        },
        threshold: 500, // Reduced from 600 to 500ms for better PWA responsiveness
        preventDefault: false // Allow natural scrolling
      });

      // Override onMouseDown to detect Shift key
      const originalOnMouseDown = longPressHandlers.onMouseDown;
      const handleMouseDown = (e: React.MouseEvent) => {
        // Prevent all interactions with other bookings when in drag mode
        if (isOtherBookingInDragMode) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        shiftPressedRef.current = e.shiftKey;
        if (e.shiftKey) {
          // Shift+Click detected - trigger move flow immediately, skip long press
          e.preventDefault();
          e.stopPropagation();
          handleEventClick({ event: eventInfo.event, jsEvent: e });
          return;
        }
        
        // Don't interfere with dragging in drag mode
        if (isDraggingMode && draggedBooking?.id === eventInfo.event.id) {
          console.log('🎯 In drag mode, allowing native drag');
          return; // Let FullCalendar handle the drag
        }
        
        if (originalOnMouseDown) {
          originalOnMouseDown(e);
        }
      };

      // When this is the dragged booking, pass through pointer/touch so FullCalendar can handle drag (iOS)
      const passThroughForDrag = isThisBookingBeingDragged;

      return (
        <div 
          {...(passThroughForDrag || isOtherBookingInDragMode ? {} : longPressHandlers)}
          onMouseDown={passThroughForDrag ? undefined : handleMouseDown}
          onTouchStart={passThroughForDrag ? undefined : (e) => {
            if (isOtherBookingInDragMode) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (longPressHandlers.onTouchStart) {
              longPressHandlers.onTouchStart(e);
            }
          }}
          className="h-full w-full cursor-pointer"
          style={{
            pointerEvents: passThroughForDrag || isOtherBookingInDragMode ? 'none' : 'auto',
            touchAction: isOtherBookingInDragMode ? 'auto' : 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {children}
        </div>
      );
    };
    
    // Special layout for field quote events: label + centered resource icon + time
    if (isFieldQuoteEvent) {
      const isDraggedBooking = draggedBooking?.id === eventInfo.event.id;
      const isInDragMode = isDraggingMode && draggedBooking;
      const isOtherBookingInDragMode = isInDragMode && !isDraggedBooking;
      
      let endFormatted = '';
      if (endTimeForFieldQuote) {
        const [eh, em] = String(endTimeForFieldQuote).split(':');
        const endDate = new Date();
        endDate.setHours(Number(eh), Number(em), 0, 0);
        endFormatted = endDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      const timeRange =
        formattedTime && endFormatted && endFormatted !== formattedTime
          ? `${formattedTime} - ${endFormatted}`
          : formattedTime;

      const isMeasurement = eventInfo.event.extendedProps.is_measurement;
      const fqResources = Array.isArray(eventInfo.event.extendedProps.resources)
        ? eventInfo.event.extendedProps.resources
        : [];
      const visibleFqResources = fqResources.slice(0, 3);
      const projectTitle = eventInfo.event.extendedProps.project_title;

      return (
        <EventWrapper>
          <div
            className={`p-1 relative overflow-hidden transition-all ${
              isDraggedBooking 
                ? isMobile 
                  ? 'opacity-100 ring-2 ring-blue-500 shadow-2xl scale-105 z-50' 
                  : 'opacity-100 ring-4 ring-blue-500 ring-inset shadow-2xl cursor-grab active:cursor-grabbing' 
                : isOtherBookingInDragMode
                ? 'opacity-30 pointer-events-none'
                : ''
            }`}
            style={{
              // Match approximate vertical size of normal booking blocks
              minHeight: isMobile ? 34 : 40,
              cursor: isDraggedBooking ? (isMobile ? 'pointer' : 'grab') : isOtherBookingInDragMode ? 'default' : 'pointer'
            }}
          >
          {/* Loading Spinner for Dragged Field Quote */}
          {isDraggedBooking && (
            <div className="absolute inset-0 flex items-center justify-center bg-transparent z-[60] pointer-events-none">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-white shadow-2xl bg-white/10 backdrop-blur-sm"></div>
            </div>
          )}
            <div className="flex h-full w-full items-stretch flex-col sm:flex-row">
              {/* Left column: label (top) + project title (middle) + time (bottom) (+ avatar below time on mobile) */}
              <div className="flex-1 flex flex-col justify-between min-w-0">
                <div className="text-[9px] sm:text-[10px] font-semibold text-white truncate">
                  {/* Mobile: shortened labels, Desktop: full labels */}
                  {isMeasurement ? (
                    <>
                      <span className="sm:hidden">Measurement</span>
                      <span className="hidden sm:inline">Field Measurements</span>
                    </>
                  ) : (
                    <>
                      <span className="sm:hidden">Quote</span>
                      <span className="hidden sm:inline">Field Quote</span>
                    </>
                  )}
                </div>

                {projectTitle && (
                  <div className="mt-0.5 text-[8px] sm:text-[9px] text-white/95 truncate">
                    {projectTitle}
                  </div>
                )}

                {timeRange && (
                  <>
                    {/* Mobile: start and end times on separate lines for better fit */}
                    <div className="mt-0.5 text-[8px] text-white/90 sm:hidden">
                      {formattedTime}
                    </div>
                    {endFormatted && endFormatted !== formattedTime && (
                      <div className="text-[8px] text-white/90 sm:hidden">
                        {endFormatted}
                      </div>
                    )}

                    {/* Desktop: single line range */}
                    <div className="mt-0.5 text-[8px] sm:text-[9px] text-white/90 hidden sm:block">
                    {timeRange}
                  </div>
                  </>
                )}

                {/* Mobile: resource avatars below time */}
                {visibleFqResources.length > 0 && (
                  <div className="mt-0.5 flex items-center justify-start sm:hidden">
                    {visibleFqResources.map((res: any, index: number) => {
                      const initials = `${(res.first_name || '').charAt(0)}${(res.last_name || '').charAt(0)}`;
                      return (
                        <div
                          key={res.id || index}
                          className="rounded-full overflow-hidden flex items-center justify-center"
                          style={{
                            width: 14,
                            height: 14,
                            marginLeft: index === 0 ? 0 : -4,
                            zIndex: 10 - index,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            boxShadow: '0 0 0 1px rgba(255,255,255,0.4)',
                          }}
                        >
                          {res.avatar_url ? (
                            <img
                              src={res.avatar_url}
                              alt={`${res.first_name || ''} ${res.last_name || ''}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-[7px] font-semibold text-blue-600">{initials}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Desktop: resource avatars to the right of time */}
              {visibleFqResources.length > 0 && (
                <div className="hidden sm:flex items-center justify-end pl-1">
                  {visibleFqResources.map((res: any, index: number) => {
                    const initials = `${(res.first_name || '').charAt(0)}${(res.last_name || '').charAt(0)}`;
                    return (
                      <div
                        key={res.id || index}
                        className="rounded-full overflow-hidden flex items-center justify-center"
                        style={{
                          width: 18,
                          height: 18,
                          marginLeft: index === 0 ? 0 : -5,
                          zIndex: 10 - index,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          boxShadow: '0 0 0 1px rgba(255,255,255,0.4)',
                        }}
                      >
                        {res.avatar_url ? (
                          <img
                            src={res.avatar_url}
                            alt={`${res.first_name || ''} ${res.last_name || ''}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-[8px] font-semibold text-blue-600">{initials}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </EventWrapper>
      );
    }

    const isDraggedBooking = draggedBooking?.id === eventInfo.event.id;
    const isInDragMode = isDraggingMode && draggedBooking;
    const isOtherBookingInDragMode = isInDragMode && !isDraggedBooking;

    return (
      <EventWrapper>
        <div 
          className={`p-1 relative overflow-hidden transition-all ${
            isDraggedBooking 
              ? isMobile 
                ? 'opacity-100 ring-2 ring-blue-500 shadow-2xl scale-105 z-50' 
                : 'opacity-100 ring-4 ring-blue-500 ring-inset shadow-2xl cursor-grab active:cursor-grabbing' 
              : isOtherBookingInDragMode
              ? 'opacity-30 pointer-events-none' // Fade and disable other bookings
              : ''
          }`}
          style={{
            cursor: isDraggedBooking ? (isMobile ? 'pointer' : 'grab') : isOtherBookingInDragMode ? 'default' : 'pointer'
          }}
        >
        {/* Loading Spinner for Dragged Booking */}
        {isDraggedBooking && (
          <div className="absolute inset-0 flex items-center justify-center bg-transparent z-[60] pointer-events-none">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-white shadow-2xl bg-white/10 backdrop-blur-sm"></div>
          </div>
        )}
        {shouldShowSecondaryColor && (
          <div 
            className="secondary-color-line absolute left-0 right-0" 
            style={{ 
              backgroundColor: secondaryColor,
              height: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              marginLeft: '-5px',
              marginRight: '-5px',
              boxShadow: '0 0 4px rgba(0,0,0,0.2)',
              borderRadius: '2px'
            }}
          />
        )}
        
        {/* Workflow Progress */}
        {showWorkflowProgress && (
          <div 
            className="absolute top-0 right-0 z-30 flex items-center px-1 py-0.5 rounded-bl-md"
            style={{
              backgroundColor: 'rgba(234, 179, 8, 0.95)', // Match in_progress color (#eab308)
              fontSize: isMobile ? '9px' : '11px',
              fontWeight: '600'
            }}
          >
            <span className="text-white">
              {completedSteps}/{totalSteps}
            </span>
          </div>
        )}
        
        {/* Feedback Rating for completed bookings */}
        {showFeedbackRating && (
          <div 
            className="absolute z-30 flex items-center px-1 py-0.5 rounded-bl-md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              fontSize: isMobile ? '8px' : '10px',
              top: showWorkflowProgress ? (isMobile ? '14px' : '16px') : 0,
              right: 0
            }}
          >
            <Star 
              size={isMobile ? 10 : 12} 
              className="text-yellow-400 fill-current mr-0.5" 
            />
            <span className="text-gray-800 font-medium">
              {feedbackData.rating.toFixed(1)}
            </span>
          </div>
        )}
        
        {/* Warranty Indicator */}
        {eventInfo.event.extendedProps.is_warranty && (
          <div 
            className="absolute top-0 left-0 z-20 px-0.5 py-0 text-[5px] sm:text-[8px] font-bold rounded-br-sm text-white"
            style={{ 
              fontSize: isMobile ? '5px' : '8px',
              lineHeight: '1',
              letterSpacing: '0.02em',
              padding: isMobile ? '1px 2px' : '2px 4px',
              backgroundColor: 'rgba(107, 114, 128, 0.9)' // Grey background
            }}
          >
            🛡️
          </div>
        )}

        {/* Active Timer Display */}
        {hasActiveTimer && (
          <div 
            className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-center px-1 py-1"
            style={{
              backgroundColor: 'rgba(220, 38, 38, 0.95)', // Red background
              borderTop: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <div className="text-white font-mono font-bold animate-pulse" style={{ fontSize: isMobile ? '10px' : '12px' }}>
              {formatTimerDisplay(timerElapsedTime)}
            </div>
          </div>
        )}
        
        {/* Display booking number and customer name on first line + resource avatars (desktop/tablet) */}
        <div
          className={`flex items-center relative z-10 space-x-1 ${
            !isMobile ? 'justify-between' : ''
          }`}
        >
          <div className="flex items-center space-x-1 min-w-0">
            {bookingNumber && (
              <div className="text-[9px] sm:text-xs font-medium flex-shrink-0">
                {bookingNumber}
              </div>
            )}
            {isLoaded && eventInfo.event.extendedProps.customer && (
              <div className="text-[9px] sm:text-xs font-medium truncate">
                - {eventInfo.event.extendedProps.customer}
              </div>
            )}
          </div>

          {/* Assigned resources (avatars) - desktop/tablet only */}
          {!isMobile && visibleResources.length > 0 && (
            <div className="flex items-center justify-end flex-shrink-0 ml-1">
              <div className="flex items-center">
                {visibleResources.map((res: any, index: number) => {
                  const initials = `${(res.first_name || '').charAt(0)}${(res.last_name || '').charAt(0)}`;
                  return (
                    <div
                      key={res.id || index}
                      className="rounded-full overflow-hidden flex items-center justify-center"
                      style={{
                        width: isMobile ? 14 : 18,
                        height: isMobile ? 14 : 18,
                        marginLeft: index === 0 ? 0 : -5,
                        zIndex: 10 - index,
                        backgroundColor: 'rgba(255,255,255,0.85)',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.06)'
                      }}
                    >
                      {res.avatar_url ? (
                        <img
                          src={res.avatar_url}
                          alt={`${res.first_name || ''} ${res.last_name || ''}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span
                          className="text-[7px] sm:text-[9px] font-semibold text-gray-800"
                          style={{ lineHeight: 1 }}
                        >
                          {initials}
                        </span>
                      )}
                    </div>
                  );
                })}

                {extraResourceCount > 0 && (
                  <div
                    className="rounded-full text-white flex items-center justify-center text-[7px] sm:text-[9px] font-semibold"
                    style={{
                      width: isMobile ? 14 : 18,
                      height: isMobile ? 14 : 18,
                      marginLeft: -5,
                      backgroundColor: 'rgba(17,24,39,0.85)',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.06)'
                    }}
                  >
                    +{extraResourceCount}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Display booking title on second line */}
        <div className="text-[9px] sm:text-sm truncate relative z-10 mt-0.5">
          {displayTitle}
        </div>
        
        {/* Display time on third line (Start - End for bookings) */}
        <div className="text-[8px] sm:text-xs opacity-75 truncate relative z-10 mt-0.5">
          {bookingTimeRange || formattedTime}
        </div>

        {/* Mobile: resource avatars in a row below time */}
        {isMobile && visibleResources.length > 0 && (
          <div className="flex items-center mt-0.5 relative z-10">
            <div className="flex items-center">
              {visibleResources.map((res: any, index: number) => {
                const initials = `${(res.first_name || '').charAt(0)}${(res.last_name || '').charAt(0)}`;
                return (
                  <div
                    key={res.id || index}
                    className="rounded-full overflow-hidden flex items-center justify-center"
                    style={{
                      width: 16,
                      height: 16,
                      marginLeft: index === 0 ? 0 : -5,
                      zIndex: 10 - index,
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.06)'
                    }}
                  >
                    {res.avatar_url ? (
                      <img
                        src={res.avatar_url}
                        alt={`${res.first_name || ''} ${res.last_name || ''}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span
                        className="text-[7px] font-semibold text-gray-800"
                        style={{ lineHeight: 1 }}
                      >
                        {initials}
                      </span>
                    )}
                  </div>
                );
              })}

              {extraResourceCount > 0 && (
                <div
                  className="rounded-full text-white flex items-center justify-center text-[7px] font-semibold"
                  style={{
                    width: 16,
                    height: 16,
                    marginLeft: -5,
                    backgroundColor: 'rgba(17,24,39,0.85)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.06)'
                  }}
                >
                  +{extraResourceCount}
                </div>
              )}
            </div>
          </div>
        )}
        
        {!isLoaded && (
          <div className="text-[8px] sm:text-xs opacity-50 relative z-10">Loading...</div>
        )}
      </div>
      </EventWrapper>
    );
  }, [loadedBookingIds, isMobile, bookingFeedback, handleLongPress, handleEventClick, activeTimer, timerElapsedTime, isDraggingMode, draggedBooking, googleCalendarEvents]);
  
  // Force a calendar redraw/rerender when bookings change
  useEffect(() => {
    if (calendarRef.current && (bookings.length > 0 || timeOffRequests.length > 0 || fieldQuoteEvents.length > 0)) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  }, [bookings, timeOffRequests, fieldQuoteEvents, bookingFeedback]);
  
  // Force a redraw when the loading state changes to ensure colors are applied
  useEffect(() => {
    if (!isLoading && calendarRef.current && (bookings.length > 0 || timeOffRequests.length > 0 || fieldQuoteEvents.length > 0)) {
      // Defer updateSize to avoid flushSync warning
      queueMicrotask(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.updateSize();
        }
      });
    }
  }, [isLoading, bookings, timeOffRequests, fieldQuoteEvents]);
  
  // Memoize calendar events to prevent unnecessary re-renders - combine bookings, time off, and field quotes
  const calendarEvents = useMemo(() => {
    console.log('📊 MONTH VIEW - calendarEvents memoization:', {
      viewType,
      bookingsLength: bookings.length,
      timeOffLength: timeOffRequests.length,
      fieldQuoteLength: fieldQuoteEvents.length,
      isDraggingMode,
      draggedBookingId: draggedBooking?.id
    });
    
    // Filter out buffer blocks in month view, keep them in week view
    const filteredBookings = viewType === 'month' 
      ? bookings.filter(b => b.extendedProps?.is_buffer_block !== true)
      : bookings;
    
    // Map events and set editable flag for the dragged booking
    const mappedEvents = [...filteredBookings, ...timeOffRequests, ...fieldQuoteEvents].map(event => {
      // Only make the specific dragged booking editable
      const isEditable = isDraggingMode && draggedBooking && event.id === draggedBooking.id;
      
      return {
        ...event,
        editable: isEditable,
        startEditable: isEditable,
        durationEditable: false // Don't allow resizing, only moving
      };
    });
    
    // Add Google Calendar events as read-only calendar events
    const gcalMapped = googleCalendarEvents
      .filter(ge => ge.start_time && ge.end_time)
      .map(ge => ({
        id: `gcal-${ge.id}`,
        title: ge.title || '(No title)',
        booking_number: 0,
        start: ge.all_day ? gcalAllDayDate(ge.start_time!) : ge.start_time!,
        end: ge.all_day ? gcalAllDayDate(ge.end_time!) : ge.end_time!,
        backgroundColor: 'rgba(66, 133, 244, 0.2)',
        borderColor: '#4285F4',
        allDay: ge.all_day,
        extendedProps: {
          customer: '',
          status: 'google_calendar',
          booking_number: 0,
          subtotal: 0,
          total_amount: 0,
          is_google_calendar: true,
          google_event_title: ge.title || '(No title)',
          location: ge.location,
          meet_link: ge.meet_link,
          is_gcal_digital: isGcalDigitalEvent(ge.meet_link, ge.conference_data),
          is_gcal_in_person: isGcalInPersonEvent(ge.location),
          gcal_location: ge.location?.trim() || null,
          attendees: ge.attendees || [],
        },
        editable: false,
        startEditable: false,
        durationEditable: false,
      }));

    console.log('📊 MONTH VIEW - calendarEvents result:', mappedEvents.length, 'events +', gcalMapped.length, 'gcal events', {
      editableCount: mappedEvents.filter(e => e.editable).length
    });
    
    return [...mappedEvents, ...gcalMapped];
  }, [bookings, timeOffRequests, fieldQuoteEvents, viewType, isDraggingMode, draggedBooking, googleCalendarEvents]);

  
  // Add a dayCellContent renderer for the day number (+ holiday badge, Google Calendar style)
  const renderDayCellContent = (arg: any) => {
    const d: Date = arg.date;
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const holidayName = holidayNameForDate(dateKey, holidayCountry);
    return (
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          position: 'relative',
          width: '100%',
          height: '100%',
          minWidth: 0,
          gap: '3px'
        }}
        className={isDraggingMode ? 'drag-mode-active' : ''}
      >
        {holidayName && (
          <span
            className="min-w-0 truncate rounded-full bg-green-100 text-green-800 font-semibold"
            style={{ fontSize: '9px', lineHeight: '14px', padding: '0 5px' }}
            title={holidayName}
          >
            {holidayName}
          </span>
        )}
        <span className={arg.isToday ? 'calendar-today-pill' : ''} style={{ flexShrink: 0 }}>{arg.dayNumberText}</span>
        {isDraggingMode && (
          <div className="absolute inset-0 border-2 border-dashed border-blue-400 opacity-30 pointer-events-none rounded" />
        )}
      </div>
    );
  };
  
  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simple CSS-based sidebar detection - much more reliable
  useEffect(() => {
    // Set CSS custom properties that the header can use
    const updateHeaderLayout = () => {
      const mainContentParent = document.querySelector('.main-content')?.parentElement as HTMLElement;
      if (mainContentParent) {
        const computedStyle = window.getComputedStyle(mainContentParent);
        const marginLeft = computedStyle.marginLeft;
        const width = computedStyle.width;
        
        // Set CSS variables that the header can use
        document.documentElement.style.setProperty('--calendar-header-left', marginLeft);
        document.documentElement.style.setProperty('--calendar-header-width', width);
      }
    };

    // Update initially
    updateHeaderLayout();

    // Use ResizeObserver for reliable detection
    const resizeObserver = new ResizeObserver(updateHeaderLayout);
    const mainContentParent = document.querySelector('.main-content')?.parentElement;
    
    if (mainContentParent) {
      resizeObserver.observe(mainContentParent);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fix for mobile calendar height issue
  useEffect(() => {
    if (!isMobile || !calendarRef.current) return;
    
    // Function to fix calendar height on mobile
    const fixCalendarHeight = () => {
      try {
        // Target the key elements that might be causing height restrictions
        const viewHarness = document.querySelector('.fc-view-harness');
        const daygridBody = document.querySelector('.fc-daygrid-body');
        const scrollGrid = document.querySelector('.fc-scrollgrid');
        const scrollers = document.querySelectorAll('.fc-scroller');
        const container = document.querySelector('.calendar-v2-container');
        
        // Remove any height restrictions
        if (viewHarness) {
          (viewHarness as HTMLElement).style.height = 'auto';
          (viewHarness as HTMLElement).style.minHeight = '1200px'; // Much taller
        }
        
        if (daygridBody) {
          (daygridBody as HTMLElement).style.height = 'auto';
        }
        
        if (scrollGrid) {
          (scrollGrid as HTMLElement).style.height = 'auto';
        }
        
        // Fix all scrollers
        scrollers.forEach(scroller => {
          (scroller as HTMLElement).style.height = 'auto';
          (scroller as HTMLElement).style.overflow = 'visible';
        });
        
        // Add extra padding to container
        if (container) {
          (container as HTMLElement).style.paddingBottom = '800px';
        }
        
        // Force a redraw of the calendar if calendar reference exists
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.updateSize();
        }
      } catch (error) {
        console.error('Error fixing calendar height:', error);
      }
    };
    
    // Apply fix after calendar is rendered
    fixCalendarHeight();
    
    // Apply fix whenever window is resized
    window.addEventListener('resize', fixCalendarHeight);
    
    // Set interval to keep checking and fixing (helps with iOS Safari)
    const intervalId = setInterval(fixCalendarHeight, 1000);
    
    return () => {
      window.removeEventListener('resize', fixCalendarHeight);
      clearInterval(intervalId);
    };
  }, [isMobile]);
  
  // Add a function to load feedback for completed bookings
  const loadFeedbackForBookings = useCallback(async (bookings: CalendarBooking[]) => {
    if (!selectedBusiness?.id) return;
    
    const completedBookings = bookings.filter(booking => booking.extendedProps.status === 'completed');
    if (completedBookings.length === 0) return;
    
    const feedbackMap = new Map<string, { rating: number; hasText: boolean }>();
    
    // Load feedback for each completed booking
    await Promise.all(
      completedBookings.map(async (booking) => {
        try {
          const feedback = await getBookingFeedback(booking.id);
          if (feedback) {
            // Calculate average rating from form_responses or legacy fields
            let averageRating = 0;
            let ratingCount = 0;
            
            if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
              // Use dynamic form responses
              Object.values(feedback.form_responses).forEach(value => {
                if (typeof value === 'number' && value > 0) {
                  averageRating += value;
                  ratingCount++;
                }
              });
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            } else {
              // Fallback to legacy fields
              if (feedback.overall_quality_rating) {
                averageRating += feedback.overall_quality_rating;
                ratingCount++;
              }
              if (feedback.overall_experience_rating) {
                averageRating += feedback.overall_experience_rating;
                ratingCount++;
              }
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            }
            
            if (averageRating > 0) {
              feedbackMap.set(booking.id, {
                rating: averageRating,
                hasText: !!(feedback.improvement_suggestions || 
                          (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
              });
            }
          }
        } catch (error) {
          console.error('Error loading feedback for booking:', booking.id, error);
        }
      })
    );
    
    setBookingFeedback(feedbackMap);
  }, [selectedBusiness?.id]);
  
  // Calculate if we should start with today's date centered
  const getInitialDate = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Always start with today's date so calendar centers on current week
    return today;
  }, []);

  // Position calendar to today's date immediately after render, before showing
  useEffect(() => {
    if (calendarRef.current && (bookings.length > 0 || timeOffRequests.length > 0 || fieldQuoteEvents.length > 0) && !hasInitialScrolled.current && !calendarLoaded) {
      // Delay briefly to let FullCalendar fully render its DOM
      setTimeout(() => {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const todayCell = document.querySelector(`[data-date="${todayString}"]`) as HTMLElement;
        
        if (todayCell) {
          // Position to today's date instantly
          todayCell.scrollIntoView({
            behavior: 'auto', // No animation
            block: 'center',
            inline: 'center'
          });
          hasInitialScrolled.current = true;
        }
        
        // Always show the calendar after positioning attempt
        setCalendarLoaded(true);
      }, 100);
      
      // Fallback timeout - show calendar even if positioning fails
      setTimeout(() => {
        if (!calendarLoaded) {
          setCalendarLoaded(true);
        }
      }, 500);
    }
  }, [calendarRef.current, bookings.length, timeOffRequests.length, fieldQuoteEvents.length, calendarLoaded]);

  // Show calendar after data is loaded, even if there are no bookings (but field quotes might exist)
  useEffect(() => {
    if (selectedBusiness?.id && !isLoading && calendarRef.current && !calendarLoaded) {
      // If data has been loaded (isLoading is false) and business is selected, show the calendar
      // This handles the case where there are only field quotes and no bookings
      // Use a small delay to ensure field quotes have been set
      setTimeout(() => {
        if (!calendarLoaded) {
          setCalendarLoaded(true);
        }
      }, 200);
    }
  }, [selectedBusiness?.id, isLoading, calendarLoaded]);
  
  // Reset calendar loaded state when month changes (for navigation)
  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const calendarMonth = currentDate.getMonth();
    const calendarYear = currentDate.getFullYear();
    
    // Only apply month view positioning logic for month view
    if (viewType === 'month') {
    // If we're not on today's month, show calendar immediately
    if (currentMonth !== calendarMonth || currentYear !== calendarYear) {
      setCalendarLoaded(true);
    } else if (!hasInitialScrolled.current) {
      // If we're back to today's month and haven't scrolled yet, hide calendar for positioning
      setCalendarLoaded(false);
    }
    }
    // For week/day views, don't reset calendarLoaded - it's managed by resource loading
  }, [currentDate, viewType]);

  return (
    <div className="calendar-v2-container flex flex-col bg-gray-50/30 min-h-screen" 
         style={{ 
           minHeight: isMobile ? 'auto' : '100vh',
           scrollBehavior: 'smooth',
           WebkitOverflowScrolling: 'touch',
           fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
           touchAction: 'manipulation',
           WebkitTouchCallout: 'none',
           WebkitUserSelect: 'none'
         }}
    >
      {/* Show loading state while business context is loading */}
      {businessLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your calendar...</p>
          </div>
        </div>
      )}
      
      {/* Only show calendar content when business context is loaded */}
      {!businessLoading && (
        <>
      <style>{`
        /* Fixed header styles */
        .calendar-v2-container {
          position: relative;
        }
        
        /* Calendar fixed header positioning - uses CSS variables set by JS */
        .calendar-fixed-header {
          left: var(--calendar-header-left, 0) !important;
          width: var(--calendar-header-width, 100%) !important;
        }
        
        /* Ensure fixed header is above all calendar content but below dropdown menu */
        .calendar-v2-container .calendar-fixed-header {
          z-index: 85 !important;
        }
        
        /* Calendar spacing - reduced on mobile / iOS to remove big gap below calendar nav */
        .calendar-v2-container .fc {
          margin-top: 8px;
        }
        @media (max-width: 640px) {
          .calendar-v2-container .fc {
            margin-top: 0;
          }
        }
        
        /* Override any sticky behavior from FullCalendar */
        .fc-header-toolbar,
        .fc .fc-toolbar,
        .fc-header-toolbar.fc-toolbar,
        .fc-sticky,
        .fc-sticky-header {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Ensure the weekday headers aren't sticky */
        .fc-col-header,
        .fc-col-header-cell,
        .fc thead,
        .fc-scroller-harness,
        .fc .fc-scrollgrid-section,
        .fc .fc-scrollgrid-section-header,
        .fc .fc-scrollgrid-section-header > *,
        .fc .fc-scrollgrid-section-sticky {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Also fix any fixed table elements */
        table.fc-scrollgrid.fc-scrollgrid-liquid,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead tr,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead th {
          position: static !important;
          top: auto !important;
        }
        
        /* Mobile optimizations for space */
        @media (max-width: 640px) {
          .fc .fc-toolbar.fc-header-toolbar {
            margin-bottom: 0;
          }
          
          .fc .fc-toolbar-title {
            font-size: 1.2em;
          }
          
          /* Reduce day header height */
          .fc-theme-standard th {
            padding: 4px 0 !important;
          }
          
          /* Smaller day numbers */
          .fc .fc-daygrid-day-top {
            padding: 2px 4px !important;
          }
          
          /* Optimize booking blocks for mobile */
          .fc-daygrid-event {
            padding: 1px 2px !important;
            margin-bottom: 1px !important;
          }
          
          /* Reduce space between booking blocks */
          .fc-daygrid-day-events {
            padding: 0 1px !important;
            margin: 0 !important;
          }
          
          /* Ensure day cells can expand as needed to fit all events */
          .fc .fc-daygrid-day-frame {
            min-height: auto !important;
            height: auto !important;
            padding: 2px 1px !important;
          }
          
          /* Remove any "more" link styling as it should no longer appear */
          .fc-daygrid-more-link {
            display: none !important;
          }
          
          /* Ensure day bottom padding doesn't cause any cut off */
          .fc-daygrid-day-bottom {
            padding: 0 !important;
          }
          
          /* Specific optimizations for iOS Safari */
          @supports (-webkit-touch-callout: none) {
            .fc .fc-daygrid-day-frame {
              padding-top: 2px !important;
            }
          }
          
          /* Make calendar stretch to edges on mobile */
          .fc {
            width: 100% !important; /* Use 100% instead of viewport width */
            max-width: 100% !important; /* Use 100% instead of viewport width */
            margin-left: 0 !important; /* Remove negative margin */
            margin-right: 0 !important;
            padding-left: 0 !important; /* Ensure no extra padding */
            padding-right: 0 !important; /* Remove right padding that was compensating for negative margin */
          }
          
          /* Remove excessive padding in calendar cells */
          .fc td, .fc th {
            padding: 0 !important;
          }
          
          /* Additional right edge fix */
          .fc td:last-child, 
          .fc th:last-child {
            border-right: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Fix table width to prevent overflow */
          .fc table {
            width: 100% !important; /* Use 100% instead of calc */
          }
        }
      `}</style>
      
      {/* Show loading until default view is loaded and initial data is ready */}
      {!defaultViewLoaded || viewType === null || ((viewType === 'week' || viewType === 'day') && isLoadingResources) ? (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-sm text-gray-600 font-medium">
              {!defaultViewLoaded ? 'Loading settings...' : 
               isLoadingResources ? 'Loading resources...' :
               'Loading calendar...'}
            </p>
          </div>
        </div>
      ) : (
        <>
      {/* Fixed Header with navigation controls - iOS Native Style */}
      <div 
        className="bg-white/95 backdrop-blur-xl border-b border-gray-200/60 fixed z-50 calendar-fixed-header"
        style={{ 
          top: 'calc(3rem + env(safe-area-inset-top, 0px))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="flex items-center justify-between px-2 sm:px-6 py-2 sm:py-4 gap-1 sm:gap-4">
          {/* Left: Navigation controls */}
          <div className="flex items-center space-x-1 sm:space-x-3">
            <button 
              onClick={handlePrev}
              className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-2 py-1.5 sm:px-4 sm:py-3 transition-all duration-200 flex items-center justify-center active:scale-95"
              disabled={isLoading}
              style={{ 
                minHeight: '32px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6 text-gray-700" />
            </button>
            
            <button
              onClick={() => {
                if (viewType === 'day') {
                  // If not on today, navigate to today
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const current = new Date(currentDate);
                  current.setHours(0, 0, 0, 0);
                  
                  if (current.getTime() !== today.getTime()) {
                    setCurrentDate(today);
                    // Pass today's date directly to fetchBookings to avoid stale closure
                    fetchBookings(true, today);
                  }
                } else if (viewType === 'week') {
                  // If not on this week, navigate to this week
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const weekStart = new Date(weekStartDate);
                  weekStart.setHours(0, 0, 0, 0);
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekEnd.getDate() + 6);
                  
                  // Check if today is in the current week
                  const isInCurrentWeek = today >= weekStart && today <= weekEnd;
                  
                  if (!isInCurrentWeek) {
                    setWeekStartDate(today);
                    fetchBookings(true);
                  }
                } else if (viewType === 'month') {
                  // If not on this month, navigate to this month
                  const today = new Date();
                  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                                        currentDate.getFullYear() === today.getFullYear();
                  
                  if (!isCurrentMonth && calendarRef.current) {
                    const calendarApi = calendarRef.current.getApi();
                    calendarApi.today();
                    setCurrentDate(today);
                  } else {
                    handleOpenMonthPicker();
                  }
                } else {
                  handleOpenMonthPicker();
                }
              }}
              className={`text-sm sm:text-2xl font-bold px-1.5 sm:px-4 py-0.5 sm:py-1 text-center sm:text-left min-w-0 rounded-lg hover:bg-gray-100/50 transition-colors duration-200 active:bg-gray-200/50 ${
                (() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  if (viewType === 'day') {
                    const current = new Date(currentDate);
                    current.setHours(0, 0, 0, 0);
                    return current.getTime() === today.getTime();
                  } else if (viewType === 'week') {
                    const weekStart = new Date(weekStartDate);
                    weekStart.setHours(0, 0, 0, 0);
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 6);
                    return today >= weekStart && today <= weekEnd;
                  } else if (viewType === 'month') {
                    return currentDate.getMonth() === today.getMonth() && 
                           currentDate.getFullYear() === today.getFullYear();
                  }
                  return false;
                })() ? 'text-blue-600' : 'text-gray-900'
              }`}
              style={{
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}
            >
              <span className="truncate block flex items-center gap-2">
                {viewType === 'week' ? (
                  <>
                    <span className="md:hidden">{weekTitleCompact}</span>
                    <span className="hidden md:inline">{weekTitle}</span>
                    {!isMobile && (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const weekStart = new Date(weekStartDate);
                      weekStart.setHours(0, 0, 0, 0);
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekEnd.getDate() + 6);
                      const isCurrentWeek = today >= weekStart && today <= weekEnd;
                      
                      return isCurrentWeek && (
                        <span className="text-xs sm:text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                          This Week
                        </span>
                      );
                    })()}
                  </>
                ) : viewType === 'day' ? (
                  <>
                    {format(currentDate, isMobile ? 'EEE, MMM' : 'EEEE, MMM')}. {format(currentDate, 'd')}
                    {!isMobile && (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const current = new Date(currentDate);
                      current.setHours(0, 0, 0, 0);
                      return current.getTime() === today.getTime();
                    })() && (
                      <span className="text-xs sm:text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                        Today
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {calendarTitle}
                    {!isMobile && (() => {
                      const today = new Date();
                      const isCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                                            currentDate.getFullYear() === today.getFullYear();
                      
                      return isCurrentMonth && (
                        <span className="text-xs sm:text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                          This Month
                        </span>
                      );
                    })()}
                  </>
                )}
              </span>
            </button>
            
            <button
              onClick={handleNext}
              className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-2 py-1.5 sm:px-4 sm:py-3 transition-all duration-200 flex items-center justify-center active:scale-95"
              disabled={isLoading}
              style={{ 
                minHeight: '32px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6 text-gray-700" />
            </button>
          </div>

          {/* Right: View Toggle + Sales */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Week view resource/asset filter */}
            {assetsEnabled && viewType === 'week' && (
              <div className="relative flex-shrink-0" ref={weekViewFilterRef}>
                <button
                  onClick={() => setWeekViewFilterDropdownOpen(prev => !prev)}
                  className={`rounded-xl p-1.5 sm:p-2 flex items-center justify-center transition-colors duration-200 cursor-pointer ${
                    weekViewFilter !== 'all'
                      ? 'bg-blue-100 hover:bg-blue-200'
                      : 'bg-gray-100/80 hover:bg-gray-200/80'
                  }`}
                  style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
                  title={`Filter: ${weekViewFilter === 'all' ? 'All' : weekViewFilter === 'resources' ? 'Resources' : 'Assets'}`}
                >
                  <svg className={`h-4 w-4 sm:h-5 sm:w-5 ${weekViewFilter !== 'all' ? 'text-blue-600' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>

                {weekViewFilterDropdownOpen && (
                  <div
                    className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-[60] min-w-[160px]"
                    style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
                  >
                    {(['all', 'resources', 'assets'] as const).map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setWeekViewFilter(option);
                          setWeekViewFilterDropdownOpen(false);
                          saveWeekViewFilter(option);
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <span className={`flex items-center justify-center h-4 w-4 rounded border ${
                          weekViewFilter === option 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-gray-300 bg-white'
                        }`}>
                          {weekViewFilter === option && (
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className="font-medium capitalize">{option}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bookings list view toggle (month view) — replaces the retired advanced map pin */}
            {viewType === 'month' && !permissions.calendar?.hideBookingList && (
              <button
                onClick={() => setShowBookingsView(prev => !prev)}
                className={`rounded-xl p-1.5 sm:p-2 flex items-center justify-center transition-colors duration-200 cursor-pointer ${
                  showBookingsView
                    ? 'bg-blue-100 hover:bg-blue-200'
                    : 'bg-gray-100/80 hover:bg-gray-200/80'
                }`}
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}
                title={showBookingsView ? 'Back to calendar' : 'Bookings list'}
              >
                <List className={`h-4 w-4 sm:h-5 sm:w-5 ${showBookingsView ? 'text-blue-600' : 'text-gray-600'}`} />
              </button>
            )}

            {/* View Toggle - Month/Week/Day */}
            <div className="flex items-center gap-1 sm:gap-2 bg-gray-100/80 rounded-xl p-0.5 sm:p-1 flex-shrink-0">
             {canViewMonth && (
              <button
               onClick={() => {
                 const previousViewType = viewType;
                 if (previousViewType !== 'month') {
                   setBookings([]); // Clear bookings first
                 setViewType('month');
                   // Reset to today when switching to month view
                   if (calendarRef.current) {
                     const calendarApi = calendarRef.current.getApi();
                     calendarApi.today();
                     const todayDate = calendarApi.getDate();
                     setCurrentDate(todayDate);
                     // Use setTimeout to ensure state updates complete before fetching
                     setTimeout(() => {
                       fetchBookings(true, todayDate);
                     }, 0);
                   } else {
                     setTimeout(() => {
                       fetchBookings(true);
                     }, 0);
                   }
                 }
               }}
               className={`px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                 viewType === 'month'
                   ? 'bg-white text-gray-900 shadow-sm'
                   : 'text-gray-600 hover:text-gray-900'
               }`}
               style={{ WebkitTapHighlightColor: 'transparent' }}
             >
               <span className="md:hidden">M</span>
               <span className="hidden md:inline">Month</span>
             </button>
             )}
             {canViewWeek && (
              <button
               onClick={() => {
                 const previousViewType = viewType;
                 if (previousViewType !== 'week') {
                   setBookings([]);
                   setShowAdvancedMap(false);
                 setViewType('week');
                   // Reset week start to today when switching to week view
                   const today = new Date();
                   today.setHours(0, 0, 0, 0);
                   // Find the start of the week (Sunday)
                   const dayOfWeek = today.getDay();
                   const weekStart = new Date(today);
                   weekStart.setDate(today.getDate() - dayOfWeek);
                   setWeekStartDate(weekStart);
                   // Use setTimeout to ensure weekDays is recalculated before fetching
                   setTimeout(() => {
                   fetchBookings(true);
                   }, 50);
                 }
               }}
               className={`px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                 viewType === 'week'
                   ? 'bg-white text-gray-900 shadow-sm'
                   : 'text-gray-600 hover:text-gray-900'
               }`}
               style={{ WebkitTapHighlightColor: 'transparent' }}
             >
               <span className="md:hidden">W</span>
               <span className="hidden md:inline">Week</span>
             </button>
             )}
             {canViewDay && (
              <button
               onClick={() => {
                 const previousViewType = viewType;
                 if (previousViewType !== 'day') {
                   setBookings([]);
                   setShowAdvancedMap(false);
                 setViewType('day');
                   // Reset to today when switching to day view
                 const today = new Date();
                 today.setHours(0, 0, 0, 0);
                 setCurrentDate(today);
                   // Use setTimeout to ensure state updates complete before fetching
                   setTimeout(() => {
                     fetchBookings(true, today);
                   }, 0);
                 }
               }}
               className={`px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                 viewType === 'day'
                   ? 'bg-white text-gray-900 shadow-sm'
                   : 'text-gray-600 hover:text-gray-900'
               }`}
               style={{ WebkitTapHighlightColor: 'transparent' }}
             >
               <span className="md:hidden">D</span>
               <span className="hidden md:inline">Day</span>
             </button>
             )}
            </div>

            {/* Sales summary */}
            {canAccess.calendarSales && (
             <button
               onClick={() => {
                 setSalesModalYear(new Date().getFullYear());
                 setIsSalesModalOpen(true);
                 fetchMonthlySales(new Date().getFullYear());
               }}
               className="bg-gray-100/80 hover:bg-gray-200/80 rounded-xl px-2 py-1.5 sm:px-4 sm:py-2.5 flex-shrink-0 transition-colors duration-200 cursor-pointer" 
               style={{
                 boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
               }}
             >
               <span className="text-xs sm:text-lg font-bold text-gray-900 whitespace-nowrap" style={{
                 fontWeight: 700,
                 letterSpacing: '-0.02em'
               }}>{formatCurrency(totalSales)}</span>
             </button>
            )}

          </div>
        </div>

        {/* Month view: search bookings across any timeframe */}
        {viewType === 'month' && !permissions.calendar?.hideSearchBar && (
          <div className="px-2 sm:px-6 pb-2 sm:pb-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                aria-hidden
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search bookings by name, number, customer, vehicle, phone, email…"
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200/80 rounded-xl bg-gray-50/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/40 transition-colors"
                aria-label="Search bookings"
                aria-expanded={searchQuery.trim().length >= 2}
                aria-autocomplete="list"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
              <CalendarBookingSearch
                query={searchQuery}
                results={searchResults}
                loading={searchLoading}
                error={searchError}
                onSelectBooking={(id) => void handleSelectSearchResult(id)}
                panelClassName="left-0 right-0 z-50"
              />
            </div>
          </div>
        )}

        {/* Unscheduled bookings — tab handle + full-width panel; only render when there are any */}
        {unscheduledBookings.length > 0 && !permissions.calendar?.hideUnscheduledTab && (
        <div
          ref={unscheduledRef}
          className="absolute left-0 right-0 top-full z-40 pointer-events-none"
        >
          {/* Wrapper aligns the panel + tab handle: full-width on mobile, ~1/3 on the left on desktop. */}
          <div className="flex flex-col md:mr-auto md:w-1/3 md:min-w-[360px]">
            {/* Drop-down panel — a continuation of the header. The tab handle sits at its bottom-left. */}
            {unscheduledOpen ? (
              <div className="order-1 pointer-events-auto bg-white/95 backdrop-blur-xl border-b border-gray-200/60 shadow-lg animate-in slide-in-from-top-2 duration-150 md:border-r md:rounded-br-2xl">
                <div className="flex items-center justify-between px-3 sm:px-6 pt-3 pb-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Unscheduled bookings
                    {unscheduledBookings.length > 0 ? (
                      <span className="ml-1.5 text-gray-400 font-normal">({unscheduledBookings.length})</span>
                    ) : null}
                  </h3>
                  {unscheduledBookings.length > 0 ? (
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(unscheduledSalesTotal)}
                    </span>
                  ) : null}
                </div>
                <div className="max-h-[min(60vh,460px)] overflow-y-auto ios-momentum-scroll px-3 sm:px-6 pb-4">
                {unscheduledLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    Loading…
                  </div>
                ) : unscheduledBookings.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm font-medium text-gray-800">No unscheduled bookings</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Bookings created without a date will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-2">
                    {unscheduledBookings.map((b) => {
                      // Mirror the month-view block color logic exactly
                      const status = b.status;
                      let blockColor = '#3b82f6';
                      if (status === 'cancelled') blockColor = '#fee2e2';
                      else if (status === 'scheduled' && b.color) blockColor = b.color;
                      else if (status === 'in_progress') blockColor = '#eab308';
                      else if (status === 'pending') blockColor = '#a855f7';
                      else if (status === 'scheduled') blockColor = '#3b82f6';
                      else if (status === 'completed') blockColor = '#22c55e';
                      const darkText = status === 'cancelled';
                      const shouldShowSecondaryColor =
                        !!b.secondary_color &&
                        status !== 'in_progress' &&
                        status !== 'completed' &&
                        status !== 'cancelled';
                      const visibleResources = b.resources.slice(0, 3);
                      const extraResourceCount = Math.max(0, b.resources.length - visibleResources.length);

                      return (
                        <div
                          key={b.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            void handleSelectSearchResult(b.id);
                            setUnscheduledOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              void handleSelectSearchResult(b.id);
                              setUnscheduledOpen(false);
                            }
                          }}
                          className="text-left rounded-md overflow-hidden relative p-1 pr-9 cursor-pointer transition-all hover:brightness-95 active:scale-[0.99]"
                          style={{ backgroundColor: blockColor, color: darkText ? '#1f2937' : '#ffffff' }}
                        >
                          {/* Schedule badge — far right; opens the date/time picker for this booking */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setScheduleTarget({ id: b.id, duration: b.service_duration });
                            }}
                            title="Schedule booking"
                            aria-label="Schedule booking"
                            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/90 text-blue-600 shadow-sm hover:bg-white active:scale-95 transition-all"
                          >
                            <CalendarClock className="h-3.5 w-3.5" />
                          </button>
                          {shouldShowSecondaryColor && (
                            <div
                              className="absolute left-0 right-0"
                              style={{
                                backgroundColor: b.secondary_color as string,
                                height: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                marginLeft: '-5px',
                                marginRight: '-5px',
                                boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                                borderRadius: '2px',
                              }}
                            />
                          )}

                          {/* Line 1: booking number - customer + resource avatars */}
                          <div className="flex items-center justify-between relative z-10 space-x-1">
                            <div className="flex items-center space-x-1 min-w-0">
                              {b.booking_number != null && (
                                <div className="text-xs font-medium flex-shrink-0">{b.booking_number}</div>
                              )}
                              <div className="text-xs font-medium truncate">- {b.customer_name}</div>
                            </div>

                            {visibleResources.length > 0 && (
                              <div className="flex items-center justify-end flex-shrink-0 ml-1">
                                {visibleResources.map((res, index) => {
                                  const initials = `${(res.first_name || '').charAt(0)}${(res.last_name || '').charAt(0)}`;
                                  return (
                                    <div
                                      key={res.id || index}
                                      className="rounded-full overflow-hidden flex items-center justify-center"
                                      style={{
                                        width: 18,
                                        height: 18,
                                        marginLeft: index === 0 ? 0 : -5,
                                        zIndex: 10 - index,
                                        backgroundColor: 'rgba(255,255,255,0.85)',
                                        boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
                                      }}
                                    >
                                      {res.avatar_url ? (
                                        <img
                                          src={res.avatar_url}
                                          alt={`${res.first_name || ''} ${res.last_name || ''}`}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <span className="text-[9px] font-semibold text-gray-800" style={{ lineHeight: 1 }}>
                                          {initials}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                                {extraResourceCount > 0 && (
                                  <div
                                    className="rounded-full text-white flex items-center justify-center text-[9px] font-semibold"
                                    style={{
                                      width: 18,
                                      height: 18,
                                      marginLeft: -5,
                                      backgroundColor: 'rgba(17,24,39,0.85)',
                                      boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
                                    }}
                                  >
                                    +{extraResourceCount}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Line 2: title */}
                          <div className="text-sm truncate relative z-10 mt-0.5">
                            {b.title || 'Untitled'}
                          </div>

                          {/* Line 3: no date (replaces the time range shown on scheduled blocks) */}
                          <div className="text-xs opacity-75 truncate relative z-10 mt-0.5">
                            No date set
                          </div>

                          {/* Linked-records extension — chips for anything tied to the booking */}
                          {(b.links.fieldQuotes > 0 || b.links.proposals > 0 || b.links.hasProject || b.links.hasOpportunity) && (
                            <div className="relative z-10 mt-1 -mx-1 -mb-1 px-1.5 py-1 flex flex-wrap items-center gap-1 bg-black/15 rounded-b-md">
                              {b.links.fieldQuotes > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/85 text-gray-800 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                                  <FileSearch className="h-3 w-3 text-blue-600" />
                                  Field Quote{b.links.fieldQuotes > 1 ? ` ×${b.links.fieldQuotes}` : ''}
                                </span>
                              )}
                              {b.links.proposals > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/85 text-gray-800 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                                  <FileText className="h-3 w-3 text-purple-600" />
                                  Proposal{b.links.proposals > 1 ? ` ×${b.links.proposals}` : ''}
                                </span>
                              )}
                              {b.links.hasProject && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/85 text-gray-800 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                                  <Folder className="h-3 w-3 text-amber-600" />
                                  Project
                                </span>
                              )}
                              {b.links.hasOpportunity && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/85 text-gray-800 text-[10px] font-semibold px-1.5 py-0.5 leading-none">
                                  <TrendingUp className="h-3 w-3 text-green-600" />
                                  Opportunity
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            ) : null}

            {/* Tab handle — hangs off the header when closed, off the panel's bottom-left when open */}
            <div className={`flex justify-start pl-2 sm:pl-6 md:pl-0 ${unscheduledOpen ? 'order-2' : ''}`}>
              <button
                type="button"
                onClick={() => setUnscheduledOpen((o) => !o)}
                className={`pointer-events-auto flex items-center gap-1.5 rounded-b-xl border border-t-0 border-gray-200/60 px-3 py-1 text-xs font-semibold transition-all ${
                  unscheduledOpen
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-white/95 text-gray-700 hover:bg-gray-50 active:scale-95'
                } backdrop-blur-xl`}
                style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)' }}
                aria-expanded={unscheduledOpen}
                aria-label="Unscheduled bookings"
              >
                <CalendarClock className={`h-3.5 w-3.5 ${unscheduledOpen ? 'text-blue-600' : 'text-gray-500'}`} />
                <span>Unscheduled</span>
                {unscheduledBookings.length > 0 ? (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold leading-none">
                    {unscheduledBookings.length}
                  </span>
                ) : null}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${unscheduledOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'}`}
                />
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Schedule picker for an unscheduled booking (opened from the panel badge) */}
      {scheduleTarget && selectedBusiness?.id && (
        <SchedulePickerPanel
          isOpen={!!scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          businessId={selectedBusiness.id}
          showDropOff={false}
          defaultView="month"
          startDate=""
          startTime=""
          endDate=""
          endTime=""
          serviceDuration={scheduleTarget.duration}
          onConfirm={(sDate, sTime, eDate, eTime) => {
            void handleScheduleUnscheduledFromPanel(sDate, sTime, eDate, eTime);
          }}
          disabled={isSchedulingUnscheduled}
        />
      )}

      {error && (
        <div className="bg-red-50/90 border border-red-200/60 text-red-700 p-3 sm:p-4 mx-4 sm:mx-6 rounded-xl shadow-sm text-sm sm:text-base"
             style={{ 
               marginTop: viewType === 'month'
                 ? (isMobile ? 'calc(3rem + 108px)' : 'calc(3rem + 128px)')
                 : (isMobile ? 'calc(3rem + 60px)' : 'calc(3rem + 80px)'),
               boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
             }}>
          {error}
        </div>
      )}

      
              <div className={`flex-grow flex flex-col relative w-full mb-2 sm:mb-6 bg-white shadow-sm overflow-hidden ${isMobile ? '' : 'rounded-2xl border border-gray-200/60'}`} 
            style={{ 
              minHeight: 'auto',
              height: 'auto',
              position: 'static',
              overflowY: 'visible',
              padding: isMobile ? 0 : 0,
              marginTop: viewType === 'month'
                ? (isMobile ? 'calc(3rem + 50px)' : 'calc(3rem + 88px)')
                : (isMobile ? 'calc(3rem + 2px)' : 'calc(3rem + 40px)'),
              marginBottom: '1rem',
              marginLeft: 0,
              marginRight: 0,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
        <style>{`
          /* Additional fix for left edge alignment */
          @media (max-width: 640px) {
            /* Remove all padding and margins from calendar container and components */
            .calendar-v2-container > div:last-child {
              padding: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            
            /* Target the specific FullCalendar view harness that has the 8px padding */
            .fc-view-harness.fc-view-harness-passive {
              padding: 0 !important;
            }
            
            /* Target the calendar container to ensure it extends to the left edge */
            .fc-view-harness, 
            .fc-view-harness-active,
            .fc-daygrid,
            .fc-daygrid-body,
            .fc-scrollgrid,
            .fc-scrollgrid-liquid {
              padding: 0 !important;
              margin: 0 !important;
              left: 0 !important;
          }
            
            /* Target the calendar table to ensure it starts from the left edge */
            .fc table,
            .fc-scrollgrid-sync-table {
              margin-left: 0 !important;
              padding-left: 0 !important;
              left: 0 !important;
              width: 100% !important;
          }
            
            /* Adjust cell borders to ensure proper edge alignment */
            .fc td:first-child, 
            .fc th:first-child {
              border-left: 0 !important;
            }
            
            /* Fix any potential wrapper div issues */
            .fc-view {
              padding-left: 0 !important;
              margin-left: 0 !important;
          }
          }
          
          /* iOS-specific optimizations */
          /* Improve touch responsiveness */
          * {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            touch-action: manipulation;
          }
          
          /* Fix for iOS hover states - prevent stuck hover effects */
          @media (hover: none) {
            .fc-event:hover {
              transform: none !important;
              transition: none !important;
          }
            
            .fc a:hover {
              opacity: 1 !important;
          }
          }
          
          /* Improve scroll performance on iOS */
          .fc {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            -webkit-perspective: 1000;
            backface-visibility: hidden;
          }
          
          /* FIX SCROLLING OVER BOOKING BLOCKS */
          /* Allow scroll when touching booking blocks */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            touch-action: pan-y !important; /* Allow vertical scrolling on touch */
            pointer-events: auto !important; /* Still capture clicks but let scrolls through */
            /* Ensure events are tappable but don't block scrolling */
            position: relative;
            z-index: 5;
          }
          
          /* Fix for iOS scroll handling */
          @media (max-width: 768px) {
            /* Make all event blocks have pan-y behavior */
            .fc-daygrid-event-harness {
              touch-action: pan-y !important;
          }
            
            /* Inner parts of the event blocks should let touches pass through for scrolling */
            .fc-event-main,
            .fc-event-title,
            .fc-event-time {
              touch-action: pan-y !important;
          }
            
            /* Ensure container scroll works */
            .fc-view,
            .fc-scroller,
            .fc-scroller-liquid,
            .fc-scrollgrid {
              touch-action: pan-y !important;
          }
          }
          
          /* Prevent text selection on touch */
          .fc-daygrid-day-number,
          .fc-daygrid-day-top,
          .fc-event-title,
          .fc-event-time {
            -webkit-user-select: none;
            user-select: none;
          }
          
          /* Make tappable areas larger for mobile */
          @media (max-width: 768px) {
            .fc-event {
              min-height: 30px;
              padding: 4px;
          }
            
            .fc-daygrid-day-number {
              padding: 8px !important;
          }
            
            /* Increase tap target size for day cells */
            .fc-daygrid-day-frame {
              min-height: 80px !important;
          }
          }
          
          /* AGGRESSIVELY TARGET AND HIDE BACKGROUND ELEMENT */
          /* First, make all parent containers transparent */
          .fc-view-harness,
          .fc-view-harness-active,
          .fc-theme-standard .fc-view-harness {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          
          /* Remove all background containers that might have styling */
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-liquid,
          .fc .fc-scrollgrid-section,
          .fc-scrollgrid-sync-container,
          .fc-scrollgrid-sync-table,
          .fc .fc-scrollgrid-section-liquid {
            background-color: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            opacity: 1 !important;
          }
          
          /* Main calendar container */
          .fc {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          /* This is the border container for the entire table */
          .fc .fc-scrollgrid {
            border: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }
          
          /* Create cell borders directly */
          .fc-theme-standard td, 
          .fc-theme-standard th {
            border: 1px solid #e5e7eb !important;
            background-color: white !important;
          }
          
          /* Ensure frame expansion for all cells */
          .fc .fc-daygrid-day-frame {
            height: auto !important;
            min-height: 120px !important;
            background-color: white !important;
          }
          
          /* Force table wrapping container to be transparent */
          .fc-view, 
          .fc-dayGridMonth-view,
          .fc-view-harness, 
          .fc-view-harness-active,
          table.fc-scrollgrid {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Reset scrolling container styles */
          .fc, 
          .fc .fc-view,
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-section,
          .fc .fc-scrollgrid-section-body,
          .fc .fc-scrollgrid-section-header,
          .fc .fc-scroller,
          .fc .fc-scroller-harness,
          .fc .fc-scroller-liquid,
          .fc .fc-scroller-liquid-absolute,
          .fc .fc-daygrid,
          .fc .fc-daygrid-body,
          .fc .fc-scrollgrid-sync-table {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }
          
          /* DO NOT TOUCH BOOKING BLOCKS */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            /* Preserve these untouched */
          }
          
          /* Preserve cell styling for day frames */
          .fc-daygrid-day-frame {
            background-color: white !important;
          }
          
          /* Other necessary styles */
          .fc-daygrid-day,
          .fc-daygrid-day-top {
            background-color: white !important;
          }
          
          /* Last container attempt - target absolutely everything */
          div[class*="fc-"] {
            border-radius: 0 !important;
          }
          
          /* Calendar today styling */
          .fc .fc-daygrid-day.fc-day-today {
            background: transparent !important;
          }
          
          .calendar-today-pill {
            background: #3b82f6 !important;
            color: #fff !important;
            border-radius: 9999px;
            padding: 2px 8px;
            font-weight: 600;
            display: inline-block;
          }

          /* Holiday badge: keep the day-number anchor inside its cell so the
             badge truncates with an ellipsis instead of overflowing. */
          .fc .fc-daygrid-day-top {
            min-width: 0;
          }
          .fc .fc-daygrid-day-number {
            max-width: 100%;
            min-width: 0;
            overflow: hidden;
          }

          /* Month drag-select range highlight */
          .fc .fc-highlight {
            background: rgba(59, 130, 246, 0.18) !important;
            border: 1.5px dashed rgba(59, 130, 246, 0.65) !important;
            border-radius: 6px;
          }
          .fc-daygrid-day.fc-day-selected,
          .fc-daygrid-day.fc-day-range-middle,
          .fc-daygrid-day.fc-day-range-start,
          .fc-daygrid-day.fc-day-range-end {
            background: transparent !important;
          }
          
          /* Drag mode: day cells clearly tappable for tap-to-drop on iOS */
          .drag-mode-active .fc-daygrid-day,
          .drag-mode-active .fc-daygrid-day-frame {
            cursor: pointer !important;
            -webkit-tap-highlight-color: transparent;
          }
          
          /* Fix the scrolling container styles to allow full display */
          .fc .fc-view-harness,
          .fc-view-harness-active,
          .fc-daygrid,
          .fc-daygrid-body,
          .fc-scrollgrid,
          .fc-scrollgrid-liquid,
          .fc-scroller,
          .fc-scroller-harness {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          
          /* Ensure the entire grid can expand */
          .fc-scrollgrid-sync-table {
            height: auto !important;
          }
          
          /* Make each cell able to grow as needed */
          .fc .fc-daygrid-body-balanced .fc-daygrid-day-events {
            position: relative !important;
          }
          
          /* Ensure all events show and are visible */
          .fc-v-event, .fc-h-event, .fc-daygrid-event {
            visibility: visible !important;
            display: block !important;
          }
        `}</style>
        <div 
          className="w-full overflow-visible relative p-2 sm:p-4" 
          style={{ 
            background: 'transparent', 
            touchAction: 'manipulation',
            WebkitOverflowScrolling: 'touch',
            margin: 0,
            padding: 'clamp(6px, 2vw, 16px)',
            opacity: calendarLoaded ? 1 : 0,
            transition: calendarLoaded ? 'opacity 0.1s ease-in' : 'none'
          }}
        >
          {(() => {
            console.log('🎨 RENDERING VIEW:', viewType, {
              resourcesLength: resources.length,
              bookingsLength: bookings.length,
              weekDaysLength: weekDays.length,
              isLoadingResources,
              isLoading
            });
            return null;
          })()}
          
          {showBookingsView ? (
            <Bookings embeddedMonth={currentDate} />
          ) : viewType === 'month' && showAdvancedMap && mapViewEnabled ? (
            <div className="h-[calc(100vh-140px)]">
              <AdvancedMapView
                businessId={selectedBusiness?.id}
                businessAddress={selectedBusiness?.address}
                bookings={bookings}
                onBookingClick={(booking) => handleEventClick({ event: booking })}
                initialMonth={currentDate}
                resources={resources}
                salesPinsEnabled={salesPinsEnabled}
              />
            </div>
          ) : viewType === 'month' ? (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={getInitialDate()}
              headerToolbar={false}
              height="auto"
              contentHeight="auto"
              aspectRatio={isMobile ? 0.8 : 1.7}
              dayMaxEvents={false}
              firstDay={1}
              weekNumbers={false}
              navLinks={false}
              editable={isDraggingMode} // Enable drag in drag mode (desktop and mobile so iOS can drag after long press)
              selectable={!isDraggingMode} // Drag across days to set booking start/end
              selectMirror={true}
              selectMinDistance={5}
              select={handleDateSelect}
              dateClick={handleDateClick}
              longPressDelay={100}
              selectLongPressDelay={350}
              unselectAuto={!isBookingTypeSelectionModalOpen}
              dayHeaderFormat={{ weekday: 'short' }}
              stickyHeaderDates={false}
              expandRows={!isMobile}
              eventTimeFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short'
              }}
              themeSystem="standard"
              slotEventOverlap={false}
              fixedWeekCount={false}
              businessHours={false}
              nowIndicator={true}
              datesSet={handleDatesSet}
              events={calendarEvents}
              eventContent={renderEventContent}
              dayCellContent={renderDayCellContent}
              lazyFetching={false}
              forceEventDuration={true}
              handleWindowResize={true}
              rerenderDelay={0}
              eventDisplay="block"
              eventMinHeight={isMobile ? 18 : 25}
              eventMinWidth={10}
              eventShortHeight={isMobile ? 18 : 25}
              eventLongPressDelay={400} // Increase to help distinguish between tap and scroll
              eventOverlap={isDraggingMode ? true : false} // Allow overlap when dragging
              eventOrder="extendedProps.booking_number"
              eventConstraint={isDraggingMode ? undefined : {
                startTime: '00:00',
                endTime: '24:00',
                dows: [0, 1, 2, 3, 4, 5, 6]
              }}
              eventDrop={(info) => {
                console.log('🚀 eventDrop FIRED!', {
                  isDraggingMode,
                  draggedBooking: draggedBooking,
                  draggedBookingId: draggedBooking?.id,
                  draggedBookingStart: draggedBooking?.start,
                  droppedEventId: info.event.id,
                  oldStart: info.oldEvent.start,
                  newStart: info.event.start
                });
                
                // This fires when an event is dragged and dropped
                if (isDraggingMode && draggedBooking) {
                  console.log('✅ Inside drag mode check');
                  
                  // Prevent FullCalendar from updating the event immediately
                  info.revert();
                  
                  // Get the new date from the drop
                  const newDate = info.event.start;
                  if (newDate) {
                    try {
                      // Validate draggedBooking.start exists
                      if (!draggedBooking.start) {
                        console.error('❌ draggedBooking.start is undefined!', draggedBooking);
                        alert('Error: Booking data is missing. Please try again.');
                        return;
                      }
                      
                      // Preserve the original time from the dragged booking
                      const originalStart = parseISO(draggedBooking.start);
                      const finalDate = new Date(newDate);
                      finalDate.setHours(originalStart.getHours(), originalStart.getMinutes(), originalStart.getSeconds(), 0);
                      
                      console.log('📅 Drop detected:', {
                        droppedOnDate: newDate,
                        originalStartTime: format(originalStart, 'HH:mm'),
                        finalDateTime: format(finalDate, 'yyyy-MM-dd HH:mm')
                      });
                      
                      handleDropBooking(finalDate);
                    } catch (error) {
                      console.error('❌ Error processing drop:', error);
                      alert('Error processing booking move. Please try again.');
                    }
                  } else {
                    console.error('❌ No new date from drop');
                  }
                } else {
                  console.error('❌ Not in drag mode or no dragged booking');
                }
              }}
              eventAllow={(dropInfo, draggedEvent) => {
                // Only allow dragging if we're in drag mode and it's the dragged booking
                const allowed = isDraggingMode && draggedEvent?.id === draggedBooking?.id;
                console.log('🔍 eventAllow check:', {
                  isDraggingMode,
                  draggedEventId: draggedEvent?.id,
                  draggedBookingId: draggedBooking?.id,
                  allowed
                });
                return allowed;
              }}
            />
          ) : viewType === 'week' && showAdvancedMap && mapViewEnabled ? (
            <div className="h-[calc(100vh-140px)]">
              <AdvancedMapView
                businessId={selectedBusiness?.id}
                businessAddress={selectedBusiness?.address}
                bookings={bookings}
                onBookingClick={(booking) => handleEventClick({ event: booking })}
                initialMonth={currentDate}
                resources={resources}
                salesPinsEnabled={salesPinsEnabled}
              />
            </div>
          ) : viewType === 'week' ? (
            <WeekView
              weekDays={weekDays}
              resources={(() => {
                // Apply week view filter
                const filteredResources = weekViewFilter === 'assets' ? [] : resources;
                const filteredAssets = weekViewFilter === 'resources' ? [] : calendarAssets;

                // Check if there are any unassigned bookings in the current visible week
                const weekStart = weekDays[0];
                const weekEnd = weekDays[weekDays.length - 1];
                const weekEndOfDay = new Date(weekEnd);
                weekEndOfDay.setHours(23, 59, 59, 999);
                
                const hasUnassignedBookings = bookings.some(booking => {
                  const isUnassigned = !booking.extendedProps?.resources || booking.extendedProps.resources.length === 0;
                  if (!isUnassigned) return false;
                  
                  const bookingStart = new Date(booking.start);
                  const bookingEnd = new Date(booking.end);
                  const inThisWeek = bookingStart <= weekEndOfDay && bookingEnd >= weekStart;
                  
                  return inThisWeek;
                });
                
                if (hasUnassignedBookings) {
                  return [
                    {
                      id: 'unassigned',
                      first_name: 'Unassigned',
                      last_name: '',
                      email: '',
                      phone: '',
                      avatar_url: null,
                      business_id: selectedBusiness?.id || '',
                      is_mobile_available: false,
                      created_at: '',
                      updated_at: ''
                    } as Resource,
                    ...filteredResources,
                    ...filteredAssets
                  ];
                }
                
                return [...filteredResources, ...filteredAssets];
              })()}
              bookings={bookings}
              timeOffRequests={timeOffRequests}
              fieldQuoteEvents={fieldQuoteEvents}
              onBookingClick={handleEventClick}
              onTimeSlotClick={handleTimeSlotClick}
              isLoading={isLoadingResources}
              businessHours={selectedBusiness?.business_hours}
              resourceAvailabilities={resourceAvailabilities}
              isDraggingMode={isDraggingMode}
              draggedBookingId={draggedBooking?.id}
              onDropBooking={handleDropBooking}
              onResourceClick={(resource) => {
                if ((resource as any)._isAsset) {
                  setSelectedAssetForCalendar((resource as any)._assetRaw || null);
                  setIsAssetModalOpenFromCalendar(true);
                } else {
                  setSelectedResourceForDetails(resource);
                  setIsResourceDetailsModalOpen(true);
                }
              }}
              weekViewFilter={weekViewFilter}
              googleCalendarEvents={googleCalendarEvents}
              onGcalEventClick={(ge) => { setSelectedGcalEvent(ge); setIsGcalEventModalOpen(true); }}
              highlightedSlotKey={
                isBookingTypeSelectionModalOpen &&
                selectedTimeSlotData?.resourceId &&
                selectedTimeSlotData?.date &&
                !selectedTimeSlotData?.endDate
                  ? weekSlotKey(selectedTimeSlotData.date, selectedTimeSlotData.resourceId)
                  : null
              }
              highlightedRange={
                isBookingTypeSelectionModalOpen &&
                selectedTimeSlotData?.resourceId &&
                selectedTimeSlotData?.date &&
                selectedTimeSlotData?.endDate &&
                selectedTimeSlotData.endDate.getTime() > selectedTimeSlotData.date.getTime()
                  ? {
                      resourceId: selectedTimeSlotData.resourceId,
                      startMs: selectedTimeSlotData.date.getTime(),
                      endMs: selectedTimeSlotData.endDate.getTime(),
                    }
                  : null
              }
            />
          ) : (
            <DayView
              selectedDate={currentDate}
              resources={resources}
              bookings={bookings}
              timeOffRequests={timeOffRequests}
              fieldQuoteEvents={fieldQuoteEvents}
              onBookingClick={handleEventClick}
              onTimeSlotClick={handleTimeSlotClick}
              onMapPinClick={handleMapPinClick}
              isLoading={isLoadingResources}
              businessHours={selectedBusiness?.business_hours}
              resourceAvailabilities={resourceAvailabilities}
              bookingFeedback={bookingFeedback}
              selectedBusinessId={selectedBusiness?.id}
              mapViewEnabled={mapViewEnabled}
              salesPinsEnabled={salesPinsEnabled}
              isDraggingMode={isDraggingMode}
              draggedBookingId={draggedBooking?.id}
              onDropBooking={handleDropBooking}
              googleCalendarEvents={googleCalendarEvents}
              onGcalEventClick={(ge) => { setSelectedGcalEvent(ge); setIsGcalEventModalOpen(true); }}
            />
          )}
        </div>
      </div>
      
      <GoogleCalendarEventModal
        isOpen={isGcalEventModalOpen}
        onClose={() => { setIsGcalEventModalOpen(false); setSelectedGcalEvent(null); }}
        event={selectedGcalEvent}
        onUpdate={() => { fetchBookings(true); }}
      />

      {isCreateGcalEventModalOpen && selectedBusiness?.id && (
        <CreateGoogleCalendarEventModal
          isOpen={isCreateGcalEventModalOpen}
          onClose={() => {
            setIsCreateGcalEventModalOpen(false);
            setCreateGcalEventDate(null);
            setCreateGcalEventEndDate(null);
            setSelectedTimeSlotData(null);
          }}
          initialDate={createGcalEventDate}
          initialEndDate={createGcalEventEndDate}
          businessId={selectedBusiness.id}
          syncId={gcalSyncId}
          resourceId={selectedTimeSlotData && !selectedTimeSlotData.isAsset ? selectedTimeSlotData.resourceId || null : null}
          onSuccess={() => { fetchBookings(true); }}
        />
      )}

      {isBookingInfoModalOpen && (
        <BookingInfoModal
          isOpen={isBookingInfoModalOpen}
          onClose={() => {
            // Removed all persistence systems
            setSelectedBooking(null);
            setIsBookingInfoModalOpen(false);
            setIsLoadingBookingFromNav(false);
          }}
          booking={selectedBooking}
          onUpdate={() => { fetchBookings(true); loadUnscheduled(); }}
          onRescheduleMobileBooking={handleRescheduleMobileBooking}
          readOnly={isLoadingBookingFromNav} // Make read-only while loading
        />
      )}

      <TaskDetailsModal
        isOpen={isTaskDetailsModalOpen}
        onClose={() => {
          setIsTaskDetailsModalOpen(false);
          setSelectedTaskId(null);
        }}
        taskId={selectedTaskId}
        onUpdate={() => fetchBookings(true)}
      />
      
      <BookingQuickInfoModal
        isOpen={isBookingQuickInfoModalOpen}
        onClose={() => {
          setIsBookingQuickInfoModalOpen(false);
          setQuickInfoBooking(null);
        }}
        booking={quickInfoBooking}

      />
      
      <DaySummaryModal
        isOpen={isDaySummaryOpen}
        onClose={() => setIsDaySummaryOpen(false)}
        selectedDate={selectedDay}
        position={daySummaryPosition || { x: 0, y: 0, width: 0, height: 0 }}
        isLocationBooking={true}
        onCreateBooking={() => {}}
        onBookingClick={() => {}}
      />
      
      {/* Booking Type Selection Modal */}
      {isBookingTypeSelectionModalOpen && selectedTimeSlotData && (
        <BookingTypeSelectionModal
          isOpen={isBookingTypeSelectionModalOpen}
          onClose={() => {
            setIsBookingTypeSelectionModalOpen(false);
            setSelectedTimeSlotData(null);
            calendarRef.current?.getApi()?.unselect?.();
          }}
          onSelectMobileBooking={handleSelectMobileBooking}
          onSelectLocationBooking={handleSelectLocationBooking}
          onSelectConvertProject={handleSelectConvertProject}
          onSelectTask={() => {
            setTaskInitialData(selectedTimeSlotData ? { ...selectedTimeSlotData } : null);
            setIsCreateTaskModalOpen(true);
            setIsBookingTypeSelectionModalOpen(false);
            calendarRef.current?.getApi()?.unselect?.();
          }}
          onSelectFieldQuote={() => {
            if (!selectedTimeSlotData) return;
            const { date, endDate } = selectedTimeSlotData;
            // Respect a drag-highlighted range; single clicks default to a 1-hour visit.
            const end = endDate && endDate.getTime() > date.getTime()
              ? endDate
              : new Date(date.getTime() + 60 * 60 * 1000);
            setFieldQuoteInitialSchedule({
              startDate: format(date, 'yyyy-MM-dd'),
              startTime: format(date, 'HH:mm'),
              endDate: format(end, 'yyyy-MM-dd'),
              endTime: format(end, 'HH:mm'),
            });
            setIsSlotFieldQuoteModalOpen(true);
            setIsBookingTypeSelectionModalOpen(false);
            calendarRef.current?.getApi()?.unselect?.();
          }}
          showMobileBooking={showMobileBookingInFAB}
          showLocationBooking={showLocationBookingInFAB}
          showConvertProject={false}
          showTask={tasksEnabled}
          showFieldQuote={showFieldQuotesInFAB}
          showGoogleCalendarEvent={gcalConnected}
          onSelectGoogleCalendarEvent={() => {
            if (selectedTimeSlotData) {
              setCreateGcalEventDate(selectedTimeSlotData.date);
              setCreateGcalEventEndDate(selectedTimeSlotData.endDate ?? null);
              setIsCreateGcalEventModalOpen(true);
              setIsBookingTypeSelectionModalOpen(false);
              calendarRef.current?.getApi()?.unselect?.();
            }
          }}
          selectedResourceName={(() => {
            const rid = selectedTimeSlotData?.resourceId;
            if (!rid) return undefined;
            const resource = resources.find(r => r.id === rid);
            if (resource) return `${resource.first_name} ${resource.last_name}`;
            const asset = calendarAssets.find(a => a.id === rid);
            if (asset) return `${asset.first_name} ${asset.last_name}`.trim();
            return undefined;
          })()}
          selectedDate={selectedTimeSlotData?.date ? format(selectedTimeSlotData.date, 'EEEE, MMMM d, yyyy') : undefined}
          selectedTimeSlot={selectedTimeSlotData?.date ? format(selectedTimeSlotData.date, 'h:mm a') : undefined}
          slotDate={selectedTimeSlotData?.date ?? null}
          onSlotDateChange={(date) => {
            setSelectedTimeSlotData((prev) => {
              if (!prev) return prev;
              let endDate = prev.endDate ?? null;
              if (endDate && endDate.getTime() <= date.getTime()) {
                endDate = new Date(date.getTime() + 30 * 60 * 1000);
              }
              return { ...prev, date, endDate };
            });
          }}
          slotEndDate={selectedTimeSlotData?.endDate ?? null}
          onSlotEndDateChange={(endDate) => {
            setSelectedTimeSlotData((prev) => {
              if (!prev) return prev;
              let nextEnd = endDate;
              if (nextEnd.getTime() <= prev.date.getTime()) {
                nextEnd = new Date(prev.date.getTime() + 30 * 60 * 1000);
              }
              return { ...prev, endDate: nextEnd };
            });
          }}
          anchorRect={selectedTimeSlotData?.anchorRect ?? null}
          anchorSlotKey={selectedTimeSlotData?.anchorSlotKey ?? null}
          anchorEndSlotKey={selectedTimeSlotData?.anchorEndSlotKey ?? null}
        />
      )}

      {/* Create mobile booking modal for time slot clicks */}
      {isCreateMobileBookingModalOpen && (
        <CreateMobileBookingModal
          isOpen={isCreateMobileBookingModalOpen}
          onClose={() => {
            setIsCreateMobileBookingModalOpen(false);
            setQuickBookingData(null);
            setSelectedTimeSlotData(null);
          }}
          onSuccess={() => {
            setIsCreateMobileBookingModalOpen(false);
            setQuickBookingData(null);
            setSelectedTimeSlotData(null);
            fetchBookings(true);
            loadUnscheduled();
          }}
          prefilledData={quickBookingData}
        />
      )}

      {/* Schedule field quote modal for time slot clicks */}
      {isSlotFieldQuoteModalOpen && (
        <ScheduleFieldQuoteModal
          isOpen={isSlotFieldQuoteModalOpen}
          onClose={() => {
            setIsSlotFieldQuoteModalOpen(false);
            setFieldQuoteInitialSchedule(null);
            setSelectedTimeSlotData(null);
          }}
          projectId={null}
          projectTitle={null}
          initialSchedule={fieldQuoteInitialSchedule}
          onSuccess={() => {
            setIsSlotFieldQuoteModalOpen(false);
            setFieldQuoteInitialSchedule(null);
            setSelectedTimeSlotData(null);
            fetchBookings(true);
          }}
        />
      )}

      {/* Create task modal for time slot clicks */}
      {isCreateTaskModalOpen && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => {
            setIsCreateTaskModalOpen(false);
            setTaskInitialData(null);
          }}
          onSuccess={() => {
            setIsCreateTaskModalOpen(false);
            setTaskInitialData(null);
            fetchBookings(true);
          }}
          initialDate={taskInitialData?.date || null}
          initialEndDate={taskInitialData?.endDate || null}
          initialResource={(() => {
            const rid = taskInitialData?.resourceId;
            if (!rid || taskInitialData?.isAsset) return null;
            const r = resources.find(res => res.id === rid);
            if (!r) return null;
            return { id: r.id, first_name: r.first_name, last_name: r.last_name, avatar_url: r.avatar_url };
          })()}
          initialAsset={(() => {
            const rid = taskInitialData?.resourceId;
            if (!rid || !taskInitialData?.isAsset) return null;
            const a = calendarAssets.find(asset => asset.id === rid);
            if (!a) return null;
            return { id: a.id, name: a.first_name, image_url: a.avatar_url };
          })()}
        />
      )}

      {/* Create location booking modal */}
      {isCreateBookingModalOpen && (
        <CreateBookingModal
          isOpen={isCreateBookingModalOpen}
          onClose={() => {
            setIsCreateBookingModalOpen(false);
            setQuickBookingData(null);
            setSelectedTimeSlotData(null);
          }}
          onSuccess={() => {
            setIsCreateBookingModalOpen(false);
            setQuickBookingData(null);
            setSelectedTimeSlotData(null);
            fetchBookings(true);
            loadUnscheduled();
          }}
          initialDate={(() => {
            const sd = quickBookingData?.scheduleData;
            if (sd?.startDate) {
              const [Y, M, D] = sd.startDate.split('-').map((s) => parseInt(s, 10));
              const timePart = sd.startTime || '09:00';
              const [h, mi] = timePart.split(':').map((s) => parseInt(s, 10));
              return new Date(
                Y,
                M - 1,
                D,
                Number.isFinite(h) ? h : 9,
                Number.isFinite(mi) ? mi : 0,
                0,
                0
              );
            }
            return selectedTimeSlotData ? new Date(selectedTimeSlotData.date) : null;
          })()}
          initialCustomer={quickBookingData?.selectedCustomer || null}
          initialResource={quickBookingData?.selectedResource || null}
          initialScheduleData={quickBookingData?.scheduleData || null}
        />
      )}
      
      {isRescheduleMobileBookingModalOpen && bookingToReschedule && (
        <CreateMobileBookingModal
          isOpen={isRescheduleMobileBookingModalOpen}
          onClose={() => {
            setIsRescheduleMobileBookingModalOpen(false);
            setBookingToReschedule(null);
          }}
          onSuccess={() => {
            setIsRescheduleMobileBookingModalOpen(false);
            setBookingToReschedule(null);
            fetchBookings(true);
            loadUnscheduled();
          }}
          isReschedule={true}
          existingBookingId={bookingToReschedule.id}
          prefilledData={{
            customer: bookingToReschedule.customer,
            services: (bookingToReschedule.services || []).map((service: any) => ({
              vehicle: service.vehicle_id ? {
                id: service.vehicle_id,
                make: service.vehicle_make || '',
                model: service.vehicle_model || '',
                year: service.vehicle_year || '',
                color: service.vehicle_color || '',
                customer_id: bookingToReschedule.customer?.id || '',
                business_id: bookingToReschedule.business_id,
                created_at: '',
                updated_at: ''
              } : null,
              service: {
                id: service.service_id,
                name: service.name,
                description: service.description,
                price: service.price,
                duration_minutes: service.duration_minutes,
                business_id: bookingToReschedule.business_id,
                category: '',
                tax_rate: service.tax_rate,
                tax_exempt: service.tax_exempt,
                active: true,
                price_options: [],
                created_at: '',
                updated_at: ''
              },
              priceOption: {
                id: '',
                service_id: service.service_id,
                name: 'Standard',
                price: service.price,
                duration_minutes: service.duration_minutes
              }
            })),
            scheduleData: {
              dropOffDate: bookingToReschedule.drop_off_date || bookingToReschedule.start_date || '',
              dropOffTime: bookingToReschedule.drop_off_time || bookingToReschedule.start_time || '',
              startDate: bookingToReschedule.start_date || '',
              startTime: bookingToReschedule.start_time || '',
              endDate: bookingToReschedule.end_date || '',
              endTime: bookingToReschedule.end_time || '',
              customer_notes: bookingToReschedule.customer_notes || ''
            },
            bookingAddress: bookingToReschedule.booking_address || '',
            bookingColor: bookingToReschedule.color || '#3B82F6',
            bookingSecondaryColor: bookingToReschedule.secondary_color
          }}
        />
      )}

      {/* Project Info Modal */}
      <ProjectInfoModal
        key={selectedProject?.id || 'no-project'}
        isOpen={isProjectInfoModalOpen}
        onClose={() => {
          setIsProjectInfoModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onUpdate={() => {
          fetchBookings(true);
        }}
        navigate={navigate}
      />

      <FieldQuoteDetailModal
        isOpen={isFieldQuoteDetailModalOpen}
        fieldQuoteId={selectedFieldQuoteId}
        onClose={() => {
          setIsFieldQuoteDetailModalOpen(false);
          setSelectedFieldQuoteId(null);
        }}
        onUpdated={() => fetchBookings(true)}
      />

        {/* Month/Year Picker Modal */}
        {isMonthPickerOpen && (
          <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setIsMonthPickerOpen(false)}
          style={{
            paddingTop: isPWA ? 'env(safe-area-inset-top, 0px)' : '0',
            paddingBottom: isPWA ? 'env(safe-area-inset-bottom, 0px)' : '0',
          }}
        >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900 text-center">Select Month & Year</h3>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Year Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-base font-medium text-gray-900"
                  >
                    {Array.from({ length: 20 }, (_, i) => {
                      const year = new Date().getFullYear() - 10 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Month Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Month</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      'January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedMonth(index)}
                        className={`px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                          selectedMonth === index
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {month.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setIsMonthPickerOpen(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMonthYearSelect}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sales View Modal */}
        {isSalesModalOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => setIsSalesModalOpen(false)}
            style={{
              paddingTop: isPWA ? 'env(safe-area-inset-top, 0px)' : '0',
              paddingBottom: isPWA ? 'env(safe-area-inset-bottom, 0px)' : '0',
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900 text-center">Monthly Sales</h3>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Year Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    value={salesModalYear}
                    onChange={(e) => {
                      const newYear = parseInt(e.target.value);
                      setSalesModalYear(newYear);
                      fetchMonthlySales(newYear);
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-base font-medium text-gray-900"
                  >
                    {Array.from({ length: 20 }, (_, i) => {
                      const year = new Date().getFullYear() - 10 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Month Sales Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Sales by Month</label>
                  {isLoadingMonthlySales ? (
                    <div className="text-center py-8 text-gray-500">Loading...</div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ].map((month, index) => (
                        <div
                          key={index}
                          className="px-4 py-3 rounded-xl font-medium text-sm bg-gray-100 text-gray-700 flex flex-col items-center justify-center"
                        >
                          <div className="text-xs mb-1 opacity-70">{month.substring(0, 3)}</div>
                          <div className="text-xs font-semibold">{formatCurrency(monthlySales[index] || 0)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => setIsSalesModalOpen(false)}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Buffer Block Modal */}
        {isBufferModalOpen && selectedBufferBlock && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-[12px] sm:rounded-[16px] text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 sm:px-6 pt-5 sm:pt-6 pb-4 sm:pb-5">
                  <div className="w-full">
                    <h3 className="text-[17px] sm:text-[19px] font-semibold text-gray-900 mb-3 sm:mb-4" style={{ letterSpacing: '-0.01em' }}>
                      Buffer Block Details
                </h3>
                    
                    <div className="mb-4 space-y-2">
                      {bufferBlockBooking && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Connected Booking:</span> #{bufferBlockBooking.booking_number} {bufferBlockBooking.title ? `- ${bufferBlockBooking.title}` : ''}
                        </p>
                      )}
                      {bufferBlockResource && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Resource:</span> {bufferBlockResource.first_name} {bufferBlockResource.last_name}
                        </p>
                      )}
                      {selectedBufferBlock.start && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Date:</span> {format(selectedBufferBlock.start, 'EEEE, MMMM d, yyyy')}
                        </p>
                      )}
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">Duration:</span> {selectedBufferBlock.buffer_time} minutes
                      </p>
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">Start Time:</span> {selectedBufferBlock.start ? format(selectedBufferBlock.start, 'h:mm a') : 'N/A'}
                      </p>
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">End Time:</span> {selectedBufferBlock.end ? format(selectedBufferBlock.end, 'h:mm a') : 'N/A'}
                      </p>
              </div>

                    <div className="bg-gray-50 rounded-[8px] sm:rounded-[10px] px-3 sm:px-4 py-2.5 sm:py-3">
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        Do you want to delete this buffer block?
                  </p>
                    </div>
                </div>
              </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200/60 flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:justify-end">
                <button
                  type="button"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2.5 text-[13px] sm:text-[14px] font-semibold text-gray-700 bg-white border border-gray-200/60 rounded-[8px] sm:rounded-[10px] hover:bg-gray-50 active:bg-gray-100 focus:outline-none transition-all duration-150 active:scale-[0.97] touch-manipulation"
                  onClick={() => {
                      setIsBufferModalOpen(false);
                      setBufferBlockBooking(null);
                      setBufferBlockResource(null);
                      setSelectedBufferBlock(null);
                  }}
                  style={{ 
                      minHeight: '40px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                    Cancel
                </button>
                <button
                  type="button"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2.5 text-[13px] sm:text-[14px] font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-[8px] sm:rounded-[10px] focus:outline-none transition-all duration-150 active:scale-[0.97] touch-manipulation"
                    onClick={handleDeleteBufferBlock}
                  style={{ 
                      minHeight: '40px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                    Delete Buffer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Buffer Block Modal */}
        {isBufferModalOpen && selectedBufferBlock && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-[12px] sm:rounded-[16px] text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 sm:px-6 pt-5 sm:pt-6 pb-4 sm:pb-5">
                  <div className="w-full">
                    <h3 className="text-[17px] sm:text-[19px] font-semibold text-gray-900 mb-3 sm:mb-4" style={{ letterSpacing: '-0.01em' }}>
                      Buffer Block Details
                    </h3>
                    
                    <div className="mb-4 space-y-2">
                      {bufferBlockBooking && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Connected Booking:</span> #{bufferBlockBooking.booking_number} {bufferBlockBooking.title ? `- ${bufferBlockBooking.title}` : ''}
                        </p>
                      )}
                      {bufferBlockResource && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Resource:</span> {bufferBlockResource.first_name} {bufferBlockResource.last_name}
                        </p>
                      )}
                      {selectedBufferBlock.start && (
                        <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                          <span className="font-semibold text-gray-900">Date:</span> {format(selectedBufferBlock.start, 'EEEE, MMMM d, yyyy')}
                        </p>
                      )}
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">Duration:</span> {selectedBufferBlock.buffer_time} minutes
                      </p>
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">Start Time:</span> {selectedBufferBlock.start ? format(selectedBufferBlock.start, 'h:mm a') : 'N/A'}
                      </p>
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        <span className="font-semibold text-gray-900">End Time:</span> {selectedBufferBlock.end ? format(selectedBufferBlock.end, 'h:mm a') : 'N/A'}
                      </p>
                </div>

                    <div className="bg-gray-50 rounded-[8px] sm:rounded-[10px] px-3 sm:px-4 py-2.5 sm:py-3">
                      <p className="text-[13px] sm:text-[14px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                        Do you want to delete this buffer block?
                      </p>
                        </div>
                </div>
              </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200/60 flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 sm:justify-end">
                <button
                    type="button"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2.5 text-[13px] sm:text-[14px] font-semibold text-gray-700 bg-white border border-gray-200/60 rounded-[8px] sm:rounded-[10px] hover:bg-gray-50 active:bg-gray-100 focus:outline-none transition-all duration-150 active:scale-[0.97] touch-manipulation"
                    onClick={() => {
                      setIsBufferModalOpen(false);
                      setBufferBlockBooking(null);
                      setBufferBlockResource(null);
                      setSelectedBufferBlock(null);
                    }}
                    style={{ 
                      minHeight: '40px',
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2.5 text-[13px] sm:text-[14px] font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-[8px] sm:rounded-[10px] focus:outline-none transition-all duration-150 active:scale-[0.97] touch-manipulation"
                    onClick={handleDeleteBufferBlock}
                    style={{ 
                      minHeight: '40px',
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Delete Buffer
                </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Google Maps Modal */}
      {isGoogleMapsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div 
              className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}
              onClick={() => setIsGoogleMapsModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-[16px] shadow-xl border border-gray-200/60 overflow-hidden w-full max-w-sm mx-4 sm:max-w-md">
              <div className="px-4 pt-4 pb-3 border-b border-gray-200/60">
                <h3 className="text-[17px] font-semibold text-gray-900 text-center" style={{ letterSpacing: '-0.01em' }}>
                  Open in Google Maps
                </h3>
              </div>

              <div className="px-4 py-4">
                <div className="flex items-start space-x-2.5">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-[14px] text-gray-900 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                    {selectedBookingAddress}
                  </p>
                </div>
              </div>

              <div className="px-4 pb-4 space-y-2">
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-[15px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[12px] focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  onClick={() => {
                    openGoogleMaps(selectedBookingAddress);
                    setIsGoogleMapsModalOpen(false);
                  }}
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  Open Maps
                </button>
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-[15px] font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-[12px] focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleMapAndNotify}
                  disabled={isSendingSMS}
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  {isSendingSMS ? 'Sending...' : 'Map & Notify'}
                </button>
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-[15px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-[12px] focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  onClick={() => setIsGoogleMapsModalOpen(false)}
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

      {/* Move Booking Confirm Modal */}
      {bookingToMove && (() => {
        // Extract dates and times from extendedProps (preferred) or parse from ISO strings (fallback)
        const startDate = bookingToMove.extendedProps?.start_date || (bookingToMove.start ? format(parseISO(bookingToMove.start), 'yyyy-MM-dd') : '');
        const startTime = bookingToMove.extendedProps?.start_time || (bookingToMove.start ? format(parseISO(bookingToMove.start), 'HH:mm') : '');
        const endDate = bookingToMove.extendedProps?.end_date || (bookingToMove.end ? format(parseISO(bookingToMove.end), 'yyyy-MM-dd') : '');
        const endTime = bookingToMove.extendedProps?.end_time || (bookingToMove.end ? format(parseISO(bookingToMove.end), 'HH:mm') : '');
        const dropOffDate = bookingToMove.extendedProps?.drop_off_date || '';
        const dropOffTime = bookingToMove.extendedProps?.drop_off_time || '';

        return (
          <MoveBookingConfirmModal
            isOpen={isMoveBookingConfirmModalOpen}
            onClose={() => {
              setIsMoveBookingConfirmModalOpen(false);
              setBookingToMove(null);
            }}
            onConfirm={handleConfirmMoveBooking}
            bookingTitle={bookingToMove.title || 'Untitled Booking'}
            dropOffDate={dropOffDate}
            dropOffTime={dropOffTime}
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            resources={bookingToMove.extendedProps?.resources || []}
            availableResources={resources}
          />
        );
      })()}

      {/* Confirm Booking Move Modal */}
      {draggedBooking && newDropLocation && (
        <ConfirmBookingMoveModal
          isOpen={isConfirmMoveModalOpen}
          onClose={() => {
            setIsConfirmMoveModalOpen(false);
            setNewDropLocation(null);
          }}
          onConfirm={handleConfirmBookingMove}
          bookingTitle={draggedBooking.title}
          oldStartDate={parseISO(draggedBooking.start)}
          oldEndDate={parseISO(draggedBooking.end)}
          newStartDate={(() => {
            const { newStartDate } = calculateNewDates(
              parseISO(draggedBooking.start),
              parseISO(draggedBooking.end),
              newDropLocation.date
            );
            return newStartDate;
          })()}
          newEndDate={(() => {
            const { newEndDate } = calculateNewDates(
              parseISO(draggedBooking.start),
              parseISO(draggedBooking.end),
              newDropLocation.date
            );
            return newEndDate;
          })()}
          isSaving={isSavingMove}
        />
      )}

      {/* Dragging Mode Banner - Rendered via Portal */}
      {isDraggingMode && draggedBooking && createPortal(
        <div 
          className="fixed left-0 right-0 z-[99999] bg-blue-600 text-white py-3 px-4 shadow-lg"
          style={{
            top: isMobile ? '60px' : '0',
            paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="animate-pulse">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base">Moving: {draggedBooking.title}</p>
                <p className="text-xs sm:text-sm opacity-90">
                  {isMobile ? 'Tap on a date to move this booking' : 'Click on a time slot to drop the booking'}
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelDragMode}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-medium text-sm touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Keyboard Shortcut Hint - Only show when not in dragging mode */}
      {!isDraggingMode && !isMobile && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          <p className="font-medium mb-1">💡 Quick Tip</p>
          <p className="text-xs opacity-90">Hold <kbd className="px-1.5 py-0.5 bg-gray-700 rounded">Shift</kbd> + Click a booking to move it</p>
        </div>
      )}

      {/* Resource Details Modal - from week/day view popover */}
      <ResourceDetailsModal
        isOpen={isResourceDetailsModalOpen}
        onClose={() => {
          setIsResourceDetailsModalOpen(false);
          setSelectedResourceForDetails(null);
        }}
        resource={selectedResourceForDetails}
        onUpdate={() => {
          fetchResources();
        }}
      />

      {/* Asset Modal - from week/day view popover */}
      <AssetModal
        isOpen={isAssetModalOpenFromCalendar}
        onClose={() => {
          setIsAssetModalOpenFromCalendar(false);
          setSelectedAssetForCalendar(null);
        }}
        onSuccess={() => {
          setIsAssetModalOpenFromCalendar(false);
          setSelectedAssetForCalendar(null);
          fetchResources();
        }}
        businessId={selectedBusiness?.id}
        asset={selectedAssetForCalendar}
      />

      </>
      )}
        </>
      )}
    </div>
  );
} 